import {
  DeleteOutlined,
  PlusOutlined,
  FileExcelOutlined,
  ArrowRightOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Modal,
  Segmented,
} from "antd";
import React, { useEffect, useState, useMemo } from "react";
import { useItemLibrary } from "../../hooks/useItemLibrary";
import { formatCurrency } from "../../utils/calculations";
import { useNavigate } from "react-router-dom";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import { useTender } from "../../contexts/TenderContext";
import { useDevMode } from "@/contexts/DevModeContext";
import { useAutoSave } from "../../hooks/useAutoSave";
import { useGlobalSettings } from "../../hooks/useGlobalSettings";
import {
  Manhole,
  StormSewerRun,
  SewerPipeRun,
  LateralConnection,
  GullyConnection,
  FoulBuildUpRowData,
  FoulRoadConstConfig,
} from "../../types/tender.types";
import {
  COLORS,
  formatUnit,
} from "../../utils/constants";
import {
  BACKFILL_TYPE_OPTIONS,
  backfillLabel,
  backfillEvalCode,
  migrateBackfillType,
} from "../../utils/backfillTypes";
import {
  calculateManholeScheduleSummary,
  calculatePipeRunsScheduleSummary,
} from "../../utils/sewersCalculations";
import {
  buildStormSurplusBackfillData,
  type StormSurplusBackfillRow,
  type StormFormulaInfo as StormSurplusBackfillFormulaInfo,
} from "../../utils/stormSummaryUtils";
import {
  calculateGullyConnectionLength,
  calculatePipeDiameterSummaries,
  calculateTotalStormLength,
} from "../../utils/stormCalculations";
import {
  calculateStormSewersSummary,
  calculateManholesSummary,
} from "../../utils/sewerSummaryCalculations";
import {
  calculateGroupedManholeRates,
  getTotalGroupedValue,
  ManholeRateItem,
} from "../../utils/rateCalculations";
import {
  exportManholeRatesToExcel,
  exportManholeRatesSummary,
} from "../../utils/manholeRatesExport";
import {
  exportStormSewersToExcel,
  importStormSewersFromExcel,
  generateStormSewersTemplate,
  StormSewersExcelData,
} from "../../utils/stormSewersExcelUtils";
import {
  pipeworkRatesByDiameter,
  lateralRates,
  gulliesRates,
} from "../../mock";
import { StormBuildUpRowDef } from "../../utils/stormBuildUpTypes";
import { getPondsRows, shiftPondSectionKeys, DEFAULT_POND_SECTIONS, MAX_POND_SECTIONS } from "../../utils/stormBuildUpPonds";
import { getSwalesRows, shiftSwaleSectionKeys, DEFAULT_SWALE_SECTIONS, MAX_SWALE_SECTIONS } from "../../utils/stormBuildUpSwales";
import { HEADWALL_ROWS } from "../../utils/stormBuildUpHeadwalls";
import PondsTable from "./stormBuildUp/PondsTable";
import SwalesTable from "./stormBuildUp/SwalesTable";
import HeadwallsTable from "./stormBuildUp/HeadwallsTable";
import CellularAttenuationTable from "./stormBuildUp/CellularAttenuationTable";
import { getCellularAttenuationRows, shiftCellularAttenuationSectionKeys, CELLULAR_ROW_SUFFIXES, CELLULAR_CRATE_FIELDS, CELLULAR_SUB_CRATE_FIELDS, DEFAULT_CELLULAR_SECTIONS, MAX_CELLULAR_SECTIONS } from "../../utils/stormBuildUpCellularAttenuation";
import { getWeholiteAttenuationRows, shiftWeholiteSectionKeys, WEHOLITE_ROW_SUFFIXES, DEFAULT_WEHOLITE_SECTIONS, MAX_WEHOLITE_SECTIONS } from "../../utils/stormBuildUpWeholiteAttenuation";
import WeholiteAttenuationTable from "./stormBuildUp/WeholiteAttenuationTable";
import { CONC_SUPPLY_OPTIONS } from "../../utils/stormConcAttenuationEvaluateCodesMapping";
import { getConcAttenuationRows, CONC_DEFAULT_DRAWING_REF, shiftConcAttenuationSectionKeys, CONC_ROW_SUFFIXES, DEFAULT_CONC_SECTIONS, MAX_CONC_SECTIONS } from "../../utils/stormBuildUpConcAttenuation";
import ConcAttenuationTable from "./stormBuildUp/ConcAttenuationTable";
import { getBoxCulvertsRows, shiftBoxCulvertSectionKeys, getBoxCulvertRowSuffixes, DEFAULT_BOX_CULVERT_SECTIONS, MAX_BOX_CULVERT_SECTIONS, BOX_CULVERT_BACKFILL_TYPE_OPTIONS } from "../../utils/stormBuildUpBoxCulverts";
import BoxCulvertsTable from "./stormBuildUp/BoxCulvertsTable";
import MainLayout from "../layout/MainLayout";
import { ExcelImportModal } from "../common/ExcelImportExport";
import NumberInput from "../common/NumberInput";
import ExpandCollapseAllButton from "../common/ExpandCollapseAllButton";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import { ColumnConfigModal, ConfigField, ConfigSection } from "../common/ColumnConfigModal";
import { useTableRenderDeferral } from "../../hooks/useTableRenderDeferral";
import TableLoadingPlaceholder from "../common/TableLoadingPlaceholder";


// Component to display manhole, pipework, lateral and gullies rates in the modal
const RatesTable: React.FC<{
  manholes: Manhole[];
  pipeRuns: SewerPipeRun[];
  laterals: LateralConnection[];
  gullies: GullyConnection[];
  projectName?: string;
}> = ({ manholes, pipeRuns, laterals, gullies, projectName }) => {
  const diameterGroups = calculateGroupedManholeRates(manholes);
  const totalManholeValue = getTotalGroupedValue(diameterGroups);

  // Calculate pipework rates - show all rates from mock data
  const calculatePipeworkRates = () => {
    const pipeworkData: any[] = [];

    // Show all rates from pipeworkRatesByDiameter mock data
    Object.entries(pipeworkRatesByDiameter).forEach(([diameter, rates]) => {
      rates.forEach((rate) => {
        // Find matching pipe runs for this rate
        const matchingPipeRuns = pipeRuns.filter((pipeRun) => {
          const runDiameter = pipeRun.diameter;
          const runDepthString = `${pipeRun.runDepthCategory.toFixed(2)}m`;
          return runDiameter === diameter && runDepthString === rate.depth;
        });

        if (matchingPipeRuns.length > 0) {
          // If there are matching pipe runs, show combined quantity
          const totalQty = matchingPipeRuns.reduce(
            (sum, run) => sum + run.length,
            0
          );
          pipeworkData.push({
            item: rate.name,
            description: rate.name,
            qty: totalQty,
            unit: rate.unit,
            rate: rate.rate,
            amount: totalQty * rate.rate,
            diameter: diameter,
            depth: rate.depth,
            exactMatch: true,
          });
        } else {
          // Show the rate with 0 quantity if no matching pipe runs
          pipeworkData.push({
            item: rate.name,
            description: rate.name,
            qty: 0,
            unit: rate.unit,
            rate: rate.rate,
            amount: 0,
            diameter: diameter,
            depth: rate.depth,
            exactMatch: true,
          });
        }
      });
    });

    return pipeworkData;
  };

  const pipeworkRates = calculatePipeworkRates();
  const totalPipeworkValue = pipeworkRates.reduce(
    (sum, item) => sum + item.amount,
    0
  );

  // Calculate lateral rates - show all rates from mock data
  const calculateLateralRates = () => {
    const lateralData: any[] = [];

    // Show all rates from lateralRates mock data
    Object.entries(lateralRates).forEach(([diameter, rates]) => {
      rates.forEach((rate) => {
        // Skip rates with null depth (headers) or null rate (non-applicable)
        if (rate.depth === null || rate.rate === null) {
          return;
        }

        // Find matching lateral connections for this rate
        const matchingLaterals = laterals.filter((lateral) => {
          const lateralDiameter = lateral.pipeDiameter;
          const lateralDepthString = `${(lateral.avgDepthCategory || 0).toFixed(
            2
          )}m`;
          // Map lateral diameter to available rates (100mm -> 150mm for now)
          const mappedDiameter =
            lateralDiameter === "100mm" ? "150mm" : lateralDiameter;
          return (
            mappedDiameter === diameter && lateralDepthString === rate.depth
          );
        });

        if (matchingLaterals.length > 0) {
          // If there are matching laterals, show combined quantity
          const totalQty = matchingLaterals.reduce(
            (sum, lateral) => sum + lateral.length,
            0
          );
          lateralData.push({
            item: rate.name,
            description: rate.name,
            qty: totalQty,
            unit: rate.unit,
            rate: rate.rate,
            amount: totalQty * rate.rate,
            diameter: diameter,
            depth: rate.depth,
            exactMatch: true,
          });
        } else {
          // Show the rate with 0 quantity if no matching laterals
          lateralData.push({
            item: rate.name,
            description: rate.name,
            qty: 0,
            unit: rate.unit,
            rate: rate.rate,
            amount: 0,
            diameter: diameter,
            depth: rate.depth,
            exactMatch: true,
          });
        }
      });
    });

    return lateralData;
  };

  const lateralRatesData = calculateLateralRates();
  const totalLateralValue = lateralRatesData.reduce(
    (sum, item) => sum + item.amount,
    0
  );

  // Calculate gullies rates - show all rates from mock data
  const calculateGulliesRates = () => {
    const gulliesData: any[] = [];

    // Show all rates from gulliesRates mock data
    Object.entries(gulliesRates).forEach(([diameter, rates]) => {
      rates.forEach((rate) => {
        // Skip rates with null depth (headers) or null rate (non-applicable)
        if (rate.depth === null || rate.rate === null) {
          return;
        }

        // Find matching gully connections for this rate
        const matchingGullies = gullies.filter((gully) => {
          const gullyDiameter = gully.pipeDiameter;
          const gullyDepthString = `${(gully.avgDepthCategory || 0).toFixed(
            2
          )}m`;
          // Map gully diameter to available rates
          const mappedDiameter =
            gullyDiameter === "100mm" ? "150mm" : gullyDiameter;
          return mappedDiameter === diameter && gullyDepthString === rate.depth;
        });

        if (matchingGullies.length > 0) {
          // If there are matching gullies, show combined quantity
          const totalQty = matchingGullies.reduce(
            (sum, gully) => sum + gully.length,
            0
          );
          gulliesData.push({
            item: rate.name,
            description: rate.name,
            qty: totalQty,
            unit: rate.unit,
            rate: rate.rate,
            amount: totalQty * rate.rate,
            diameter: diameter,
            depth: rate.depth,
            exactMatch: true,
          });
        } else {
          // Show the rate with 0 quantity if no matching gullies
          gulliesData.push({
            item: rate.name,
            description: rate.name,
            qty: 0,
            unit: rate.unit,
            rate: rate.rate,
            amount: 0,
            diameter: diameter,
            depth: rate.depth,
            exactMatch: true,
          });
        }
      });
    });

    return gulliesData;
  };

  const gulliesRatesData = calculateGulliesRates();
  const totalGulliesValue = gulliesRatesData.reduce(
    (sum, item) => sum + item.amount,
    0
  );
  const grandTotal =
    totalManholeValue +
    totalPipeworkValue +
    totalLateralValue +
    totalGulliesValue;

  const handleExportFull = () => {
    try {
      exportManholeRatesToExcel(
        diameterGroups,
        manholes,
        {
          projectName: projectName || "Untitled Project",
          exportDate: new Date(),
        },
        pipeworkRates,
        pipeRuns
      );
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const handleExportSummary = () => {
    try {
      exportManholeRatesSummary(
        diameterGroups,
        manholes,
        {
          projectName: projectName || "Untitled Project",
          exportDate: new Date(),
        },
        pipeworkRates,
        pipeRuns
      );
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  // Flatten manhole groups into table data with diameter headers
  const manholeTableData: (ManholeRateItem & {
    isDiameterHeader?: boolean;
    subtotal?: number;
  })[] = [];

  diameterGroups.forEach((group) => {
    // Add diameter header row
    manholeTableData.push({
      item: -1,
      description: `${group.diameter} Diameter Manholes`,
      qty: 0,
      unit: "",
      rate: 0,
      amount: 0,
      diameter: group.diameter,
      exactMatch: true,
      isDiameterHeader: true,
      subtotal: group.subtotal,
    });

    // Add items for this diameter
    group.items.forEach((item) => {
      manholeTableData.push(item);
    });
  });

  // Group pipework data by diameter and add proper serialization
  const pipeworkGroups = pipeworkRates.reduce((acc, item) => {
    if (!acc[item.diameter]) {
      acc[item.diameter] = [];
    }
    acc[item.diameter].push(item);
    return acc;
  }, {} as Record<string, any[]>);

  const pipeworkTableData: any[] = [];
  let pipeworkItemCounter = 1;

  Object.entries(pipeworkGroups).forEach(([diameter, items]) => {
    // Add diameter header row
    pipeworkTableData.push({
      item: -1,
      description: `${diameter} Diameter Pipework`,
      qty: 0,
      unit: "",
      rate: 0,
      amount: 0,
      isDiameterHeader: true,
      subtotal: items.reduce((sum, item) => sum + item.amount, 0),
    });

    // Add individual items with proper serialization
    items.forEach((item) => {
      pipeworkTableData.push({
        ...item,
        item: pipeworkItemCounter++,
      });
    });
  });

  const columns = [
    {
      title: "Item",
      dataIndex: "item",
      key: "item",
      width: 60,
      render: (item: number, record: any) => {
        if (record.isDiameterHeader) return "";
        return item;
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 350,
      render: (description: string, record: any) => {
        if (record.isDiameterHeader) {
          return <strong className="text-blue-600">{description}</strong>;
        }
        return (
          <div>
            <div>{description}</div>
            {!record.exactMatch && (
              <Tag color="orange" size="small">
                Closest Match
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: "Qty",
      dataIndex: "qty",
      key: "qty",
      width: 60,
      align: "center" as const,
      render: (qty: number, record: any) => {
        if (record.isDiameterHeader) return "";
        return qty > 0 ? qty : "";
      },
    },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
      width: 60,
      align: "center" as const,
      render: (unit: string, record: any) => {
        if (record.isDiameterHeader) return "";
        return formatUnit(unit);
      },
    },
    {
      title: "Rate (£)",
      dataIndex: "rate",
      key: "rate",
      width: 100,
      align: "right" as const,
      render: (rate: number, record: any) => {
        if (record.isDiameterHeader) return "";
        return `£${rate.toLocaleString("en-GB", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      },
    },
    {
      title: "Amount (£)",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      align: "right" as const,
      render: (amount: number, record: any) => {
        if (record.isDiameterHeader) {
          return record.subtotal > 0 ? (
            <strong className="text-blue-600">
              £
              {record.subtotal.toLocaleString("en-GB", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </strong>
          ) : (
            ""
          );
        }
        return amount > 0 ? (
          <span className="font-semibold">
            £
            {amount.toLocaleString("en-GB", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        ) : (
          ""
        );
      },
    },
  ];

  return (
    <div>
      <div className="mb-4">
        <div className="flex justify-between items-center">
          <Typography.Title level={4} className="m-0">
            Rate Breakdown
          </Typography.Title>
          <div className="text-right">
            <Typography.Text className="block">
              Manhole Total: £{totalManholeValue.toFixed(2)}
            </Typography.Text>
            <Typography.Text className="block">
              Pipework Total: £{totalPipeworkValue.toFixed(2)}
            </Typography.Text>
            <Typography.Text className="block">
              Lateral Total: £{totalLateralValue.toFixed(2)}
            </Typography.Text>
            <Typography.Text className="block">
              Gullies Total: £{totalGulliesValue.toFixed(2)}
            </Typography.Text>
            <Typography.Text strong className="text-lg block">
              Grand Total: £{grandTotal.toFixed(2)}
            </Typography.Text>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <Space>
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={handleExportFull}
          >
            Export Full Details
          </Button>
          <Button
            type="default"
            icon={<FileExcelOutlined />}
            onClick={handleExportSummary}
          >
            Export Summary
          </Button>
        </Space>
      </div>

      {/* Manholes Section */}
      <div className="mb-6">
        <Typography.Title level={5} className="mb-3 text-blue-900">
          Manholes (£{totalManholeValue.toFixed(2)})
        </Typography.Title>
        <Table
          dataSource={manholeTableData}
          columns={columns}
          rowKey={(record, index) =>
            record.isDiameterHeader
              ? `manhole-header-${record.diameter}`
              : `manhole-item-${record.item}-${index}`
          }
          pagination={false}
          size="small"
          scroll={{ x: 800 }}
          rowClassName={(record) =>
            record.isDiameterHeader ? "bg-blue-50 font-semibold" : ""
          }
        />
      </div>
      {/* Pipework Section */}
      <div className="mb-6">
        <Typography.Title level={5} className="mb-3 text-blue-900">
          Pipework (£{totalPipeworkValue.toFixed(2)})
        </Typography.Title>
        <Table
          dataSource={pipeworkTableData}
          columns={columns}
          rowKey={(record, index) =>
            record.isDiameterHeader
              ? `pipework-header-${record.description}`
              : `pipework-item-${record.item}-${index}`
          }
          pagination={false}
          size="small"
          scroll={{ x: 800 }}
          rowClassName={(record) =>
            record.isDiameterHeader ? "bg-blue-50 font-semibold" : ""
          }
        />
      </div>

      {/* Lateral Section */}
      <div className="mb-6">
        <Typography.Title level={5} className="mb-3 text-blue-900">
          Lateral Connections (£{totalLateralValue.toFixed(2)})
        </Typography.Title>
        <Table
          dataSource={lateralRatesData.map((item, index) => ({
            ...item,
            item: index + 1,
          }))}
          columns={columns}
          rowKey={(record, index) => `lateral-item-${record.item}-${index}`}
          pagination={false}
          size="small"
          scroll={{ x: 800 }}
        />
      </div>

      {/* Gullies Section */}
      <div className="mb-6">
        <Typography.Title level={5} className="mb-3 text-blue-900">
          Gullies Connections (£{totalGulliesValue.toFixed(2)})
        </Typography.Title>
        <Table
          dataSource={gulliesRatesData.map((item, index) => ({
            ...item,
            item: index + 1,
          }))}
          columns={columns}
          rowKey={(record, index) => `gullies-item-${record.item}-${index}`}
          pagination={false}
          size="small"
          scroll={{ x: 800 }}
        />
      </div>

      {manholes.length === 0 && (
        <Alert
          message="No manholes available to calculate rates."
          type="info"
          showIcon
          className="mt-4"
        />
      )}
    </div>
  );
};

// ── Storm summary formula helpers ─────────────────────────────────────────────
// SUMIFS('Storm Take off'!O:O, J:J, material, K:K, pipeDiameter)
const calcStormRockerQty = (pipeRuns: SewerPipeRun[], material: string, pipeDiameter: number): number =>
  pipeRuns
    .filter((r) => (r.pipeType ?? '').toLowerCase() === material.toLowerCase() && parseInt(r.diameter ?? '', 10) === pipeDiameter)
    .reduce((sum, r) => sum + (r.rockers ?? 0), 0);

// SUMIF('Storm Take off'!K:K, pipeDiameter, N:N)
const calcStormConcreteProtection = (pipeRuns: SewerPipeRun[], pipeDiameter: number): number =>
  pipeRuns
    .filter((r) => parseInt(r.diameter ?? '', 10) === pipeDiameter)
    .reduce((sum, r) => sum + (r.concreteProtection ?? 0), 0);

// SUM(ladders metres) across all manholes
const calcStormLadderMetres = (manholes: Manhole[]): number =>
  manholes.reduce((sum, mh) => sum + (mh.ladders ?? 0), 0);

// SUM(safetyChains metres) across all manholes
const calcStormChainMetres = (manholes: Manhole[]): number =>
  manholes.reduce((sum, mh) => sum + (mh.safetyChains ?? 0), 0);

// ── Storm static bill template data ──────────────────────────────────────────
const STORM_STUB_PIPE_GROUPS: Array<{
  key: string; label: string; pipeType: string;
  items: Array<{ code: string; desc: string; dia: number }>;
}> = [
  {
    key: 'sstub-supersleeve', label: 'Supersleeve', pipeType: 'clay',
    items: [
      { code: 'APS100R',  desc: 'EO for 100mmØ Stub / rocker pipe', dia: 100 },
      { code: 'APS150R',  desc: 'EO for 150mmØ Stub / rocker pipe', dia: 150 },
      { code: 'APS225R',  desc: 'EO for 225mmØ Stub / rocker pipe', dia: 225 },
      { code: 'APS300R',  desc: 'EO for 300mmØ Stub / rocker pipe', dia: 300 },
    ],
  },
  {
    key: 'sstub-ultrarib', label: 'Ultrarib', pipeType: 'ultra',
    items: [
      { code: 'APU225SR', desc: 'EO for 225mmØ Stub / rocker pipe', dia: 225 },
      { code: 'APU300SR', desc: 'EO for 300mmØ Stub / rocker pipe', dia: 300 },
    ],
  },
  {
    key: 'sstub-ridgisewer', label: 'Ridgisewer', pipeType: 'rigi',
    items: [
      { code: 'APRS150SR', desc: 'EO for 150mmØ Stub / rocker pipe', dia: 150 },
      { code: 'APRS225SR', desc: 'EO for 225mmØ Stub / rocker pipe', dia: 225 },
      { code: 'APRS300SR', desc: 'EO for 300mmØ Stub / rocker pipe', dia: 300 },
      { code: 'APRS400SR', desc: 'EO for 400mmØ Stub / rocker pipe', dia: 400 },
      { code: 'APRS450SR', desc: 'EO for 450mmØ Stub / rocker pipe', dia: 450 },
      { code: 'APRS500SR', desc: 'EO for 500mmØ Stub / rocker pipe', dia: 500 },
      { code: 'APRS600SR', desc: 'EO for 600mmØ Stub / rocker pipe', dia: 600 },
      { code: 'APRS750SR', desc: 'EO for 750mmØ Stub / rocker pipe', dia: 750 },
      { code: 'APRS900SR', desc: 'EO for 900mmØ Stub / rocker pipe', dia: 900 },
    ],
  },
  {
    key: 'sstub-concrete', label: 'Concrete', pipeType: 'concrete',
    items: [
      { code: 'APM300SR',  desc: 'EO for 300mmØ Stub / rocker pipe',  dia: 300  },
      { code: 'APM450SR',  desc: 'EO for 450mmØ Stub / rocker pipe',  dia: 450  },
      { code: 'APM525SR',  desc: 'EO for 525mmØ Stub / rocker pipe',  dia: 525  },
      { code: 'APM600SR',  desc: 'EO for 600mmØ Stub / rocker pipe',  dia: 600  },
      { code: 'APM675SR',  desc: 'EO for 675mmØ Stub / rocker pipe',  dia: 675  },
      { code: 'APM750SR',  desc: 'EO for 750mmØ Stub / rocker pipe',  dia: 750  },
      { code: 'APM825SR',  desc: 'EO for 825mmØ Stub / rocker pipe',  dia: 825  },
      { code: 'APM900SR',  desc: 'EO for 900mmØ Stub / rocker pipe',  dia: 900  },
      { code: 'APM1050SR', desc: 'EO for 1050mmØ Stub / rocker pipe', dia: 1050 },
      { code: 'APM1200SR', desc: 'EO for 1200mmØ Stub / rocker pipe', dia: 1200 },
      { code: 'APM1350SR', desc: 'EO for 1350mmØ Stub / rocker pipe', dia: 1350 },
      { code: 'APM1500SR', desc: 'EO for 1500mmØ Stub / rocker pipe', dia: 1500 },
      { code: 'APM1800SR', desc: 'EO for 1800mmØ Stub / rocker pipe', dia: 1800 },
      { code: 'APM2100SR', desc: 'EO for 2100mmØ Stub / rocker pipe', dia: 2100 },
      { code: 'APM2400SR', desc: 'EO for 2400mmØ Stub / rocker pipe', dia: 2400 },
    ],
  },
];

const STORM_JUNCTION_GROUPS: Array<{
  key: string; label: string; pipeType: string;
  items: Array<{ code: string; desc: string; mainDia: string; branchDia: string }>;
}> = [
  {
    key: 'sjunc-supersleeve', label: 'Supersleeve', pipeType: 'clay',
    items: [
      { code: 'APS100J1', desc: 'EO for 100 x 100mm junction', mainDia: '100mm', branchDia: '100mm' },
      { code: 'APS150J1', desc: 'EO for 150 x 100mm junction', mainDia: '150mm', branchDia: '100mm' },
      { code: 'APS150J2', desc: 'EO for 150 x 150mm junction', mainDia: '150mm', branchDia: '150mm' },
      { code: 'APS225J1', desc: 'EO for 225 x 100mm junction', mainDia: '225mm', branchDia: '100mm' },
      { code: 'APS225J2', desc: 'EO for 225 x 150mm junction', mainDia: '225mm', branchDia: '150mm' },
      { code: 'APS225J3', desc: 'EO for 225 x 225mm junction', mainDia: '225mm', branchDia: '225mm' },
      { code: 'APS300J1', desc: 'EO for 300 x 100mm junction', mainDia: '300mm', branchDia: '100mm' },
      { code: 'APS300J2', desc: 'EO for 300 x 150mm junction', mainDia: '300mm', branchDia: '150mm' },
      { code: 'APS300J3', desc: 'EO for 300 x 225mm junction', mainDia: '300mm', branchDia: '225mm' },
      { code: 'APS300J4', desc: 'EO for 300 x 300mm junction', mainDia: '300mm', branchDia: '300mm' },
    ],
  },
  {
    key: 'sjunc-ultrarib', label: 'Ultrarib', pipeType: 'ultra',
    items: [
      { code: 'APU225J1', desc: 'EO for 225 x 100mm junction', mainDia: '225mm', branchDia: '100mm' },
      { code: 'APU225J2', desc: 'EO for 225 x 150mm junction', mainDia: '225mm', branchDia: '150mm' },
      { code: 'APU225J3', desc: 'EO for 225 x 225mm junction', mainDia: '225mm', branchDia: '225mm' },
      { code: 'APU300J2', desc: 'EO for 300 x 150mm junction', mainDia: '300mm', branchDia: '150mm' },
      { code: 'APU300J3', desc: 'EO for 300 x 225mm junction', mainDia: '300mm', branchDia: '225mm' },
      { code: 'APU300J4', desc: 'EO for 300 x 300mm junction', mainDia: '300mm', branchDia: '300mm' },
    ],
  },
  {
    key: 'sjunc-ridgisewer', label: 'Ridgisewer', pipeType: 'rigi',
    items: [
      { code: 'APRS150J1', desc: 'EO for 150 x 150mm junction', mainDia: '150mm', branchDia: '150mm' },
      { code: 'APRS225J1', desc: 'EO for 225 x 150mm junction', mainDia: '225mm', branchDia: '150mm' },
      { code: 'APRS300J1', desc: 'EO for 300 x 150mm junction', mainDia: '300mm', branchDia: '150mm' },
      { code: 'APRS400J1', desc: 'EO for 400 x 150mm junction', mainDia: '400mm', branchDia: '150mm' },
      { code: 'APRS450J1', desc: 'EO for 450mm Saddle',         mainDia: '450mm', branchDia: '' },
      { code: 'APRS500J1', desc: 'EO for 500mm Saddle',         mainDia: '500mm', branchDia: '' },
      { code: 'APRS600J1', desc: 'EO for 600mm Saddle',         mainDia: '600mm', branchDia: '' },
      { code: 'APRS750J1', desc: 'EO for 750mm Saddle',         mainDia: '750mm', branchDia: '' },
      { code: 'APRS900J1', desc: 'EO for 900mm Saddle',         mainDia: '900mm', branchDia: '' },
    ],
  },
  {
    key: 'sjunc-concrete', label: 'Concrete', pipeType: 'concrete',
    items: [
      { code: 'APM300J1', desc: 'EO for 300 x 150mm junction', mainDia: '300mm', branchDia: '150mm' },
      { code: 'APM375J1', desc: 'EO for 375 x 150mm junction', mainDia: '375mm', branchDia: '150mm' },
      { code: 'APM450J1', desc: 'EO for 450 x 150mm junction', mainDia: '450mm', branchDia: '150mm' },
      { code: 'APMCD001', desc: 'Core Drill 525Ø+',            mainDia: '525mm', branchDia: '' },
    ],
  },
];

const STORM_EO_CONC_BED_ITEMS = [
  { code: 'AMHEO027', desc: 'EO concrete bed and surround 150mmØ pipe'  },
  { code: 'AMHEO028', desc: 'EO concrete bed and surround 225mmØ pipe'  },
  { code: 'AMHEO029', desc: 'EO concrete bed and surround 300mmØ pipe'  },
  { code: 'AMHEO030', desc: 'EO concrete bed and surround 375mmØ pipe'  },
  { code: 'AMHEO031', desc: 'EO concrete bed and surround 450mmØ pipe'  },
  { code: 'AMHEO032', desc: 'EO concrete bed and surround 525mmØ pipe'  },
  { code: 'AMHEO033', desc: 'EO concrete bed and surround 600mmØ pipe'  },
  { code: 'AMHEO034', desc: 'EO concrete bed and surround 675mmØ pipe'  },
  { code: 'AMHEO035', desc: 'EO concrete bed and surround 750mmØ pipe'  },
  { code: 'AMHEO036', desc: 'EO concrete bed and surround 825mmØ pipe'  },
  { code: 'AMHEO037', desc: 'EO concrete bed and surround 900mmØ pipe'  },
  { code: 'AMHEO038', desc: 'EO concrete bed and surround 1050mmØ pipe' },
  { code: 'AMHEO039', desc: 'EO concrete bed and surround 1200mmØ pipe' },
  { code: 'AMHEO040', desc: 'EO concrete bed and surround 1350mmØ pipe' },
  { code: 'AMHEO041', desc: 'EO concrete bed and surround 1500mmØ pipe' },
  { code: 'AMHEO042', desc: 'EO concrete bed and surround 1800mmØ pipe' },
  { code: 'AMHEO043', desc: 'EO concrete bed and surround 2100mmØ pipe' },
  { code: 'AMHEO044', desc: 'EO concrete bed and surround 2400mmØ pipe' },
];

const STORM_CONC_PROTECTION_ITEMS: Array<{ code: string; desc: string; dia: number }> = [
  { code: 'AMHEO008', desc: 'Concrete protection Slab 150mmØ',  dia: 150  },
  { code: 'AMHEO009', desc: 'Concrete protection Slab 225mmØ',  dia: 225  },
  { code: 'AMHEO010', desc: 'Concrete protection Slab 300mmØ',  dia: 300  },
  { code: 'AMHEO012', desc: 'Concrete protection Slab 375mmØ',  dia: 375  },
  { code: 'AMHEO013', desc: 'Concrete protection Slab 450mmØ',  dia: 450  },
  { code: 'AMHEO014', desc: 'Concrete protection Slab 525mmØ',  dia: 525  },
  { code: 'AMHEO015', desc: 'Concrete protection Slab 600mmØ',  dia: 600  },
  { code: 'AMHEO016', desc: 'Concrete protection Slab 675mmØ',  dia: 675  },
  { code: 'AMHEO017', desc: 'Concrete protection Slab 750mmØ',  dia: 750  },
  { code: 'AMHEO018', desc: 'Concrete protection Slab 825mmØ',  dia: 825  },
  { code: 'AMHEO019', desc: 'Concrete protection Slab 900mmØ',  dia: 900  },
  { code: 'AMHEO020', desc: 'Concrete protection Slab 1050mmØ', dia: 1050 },
  { code: 'AMHEO021', desc: 'Concrete protection Slab 1200mmØ', dia: 1200 },
  { code: 'AMHEO022', desc: 'Concrete protection Slab 1350mmØ', dia: 1350 },
  { code: 'AMHEO023', desc: 'Concrete protection Slab 1500mmØ', dia: 1500 },
  { code: 'AMHEO024', desc: 'Concrete protection Slab 1800mmØ', dia: 1800 },
  { code: 'AMHEO025', desc: 'Concrete protection Slab 2100mmØ', dia: 2100 },
  { code: 'AMHEO026', desc: 'Concrete protection Slab 2400mmØ', dia: 2400 },
];

const STORM_EO_ITEMS = [
  { code: 'AMHEO006',  desc: 'EO for working in offsite carriageway',               unit: 'm'  },
  { code: 'AMHEO007',  desc: 'EO trench reinstatement in carriageway',              unit: 'm'  },
  { code: 'AMHEO003B', desc: 'EO construct manhole in existing carriageway',        unit: 'Nr' },
  { code: 'AMHEO003',  desc: 'EO Breaking existing manhole for connection',          unit: 'Nr' },
  { code: 'AMHEO003A', desc: 'EO Construct manhole on existing sewer',              unit: 'Nr' },
];

const STORM_REDUNDANT_SEWER_ITEMS = [
  { desc: 'Grout / grub up redundant sewer - n ex 225mmØ', unit: 'm'  },
  { desc: 'Break out existing manhole, remove from site',   unit: 'Nr' },
];

// Manholes section — replaces the old single generic "EO Hydrobrake" /
// "Penstock" / "Weir walls" placeholder rows with the sheet's actual 4 items.
const STORM_HYDROBRAKE_PENSTOCK_WEIR_ITEMS = [
  { code: 'APMHW006', desc: 'EO for Hydrobrake, penstock & weir wall 1', unit: 'Nr' },
  { code: 'APMHW007', desc: 'EO for Hydrobrake, penstock & weir wall 2', unit: 'Nr' },
  { code: 'APMHW008', desc: 'EO for Hydrobrake, penstock & weir wall 3', unit: 'Nr' },
  { code: 'APMHW009', desc: 'EO for Hydrobrake, penstock & weir wall 4', unit: 'Nr' },
];

// New standalone section, sibling to Manholes — no evaluate codes on the
// source sheet for these two rows.
const STORM_FILTER_DRAIN_ITEMS = [
  { desc: "150mmØ pipe, GBS, b'fill with  gravel- dti ne 1.00m dp", unit: 'm'  },
  { desc: '500mm dia Polyprolplene silt trap dp to sump ne 1.50m',  unit: 'Nr' },
];

// ── Storm Manhole Schedule formula helpers ────────────────────────────────────
function calcStormManholeDerived(
  mh: Manhole,
  ladderCfg: 'N/A' | 'Stainless' | 'Galvanised',
  safetyChainCfg: 'N/A' | 'Stainless' | 'Galvanised',
): Manhole {
  const diamNum = parseInt(mh.diameter, 10) || 0;
  const easiTrad: 'easi' | 'trad' | undefined =
    diamNum === 0 ? undefined : ([1200, 1500, 1800].includes(diamNum) ? 'easi' : 'trad');
  const sump = mh.sump ?? 0;
  const depth = parseFloat((mh.coverLevel - mh.invertLevel + sump).toFixed(3));
  const depthCategory = Math.round(depth / 0.25) * 0.25;
  // Ladders/chains = Invert Level − Sump − 0.15 when depth category > 2.25; else 0
  // (=IF($K$8="n/a","",IF(J>2.25,G-H-0.15)) on the 'Storm Manhole Schedule' sheet)
  const ladderVal = parseFloat((mh.invertLevel - sump - 0.15).toFixed(3));
  const ladders = ladderCfg === 'N/A' ? 0 : depthCategory > 2.25 ? ladderVal : 0;
  const safetyChains = safetyChainCfg === 'N/A' ? 0 : depthCategory > 2.25 ? ladderVal : 0;
  return { ...mh, easiTrad, depth, depthCategory, ladders, safetyChains };
}

// ── Storm Take off formula helpers ────────────────────────────────────────────
function calcStormPipeRunDerived(
  run: StormSewerRun,
  manholes: Manhole[],
  pairedGullyDiameter?: string,
): StormSewerRun {
  // No `?? run.depthFrom` fallback here: depthFrom/depthTo are read-only,
  // fully derived from the selected manhole — if fromMH/toMH doesn't match any
  // manhole (cleared, or renamed out from under it), the depth must reset to 0
  // rather than keep showing the previous manhole's stale calculated value.
  const depthFrom = manholes.find((m) => m.ref === run.fromMH)?.depth ?? 0;
  const depthTo = manholes.find((m) => m.ref === run.toMH)?.depth ?? 0;
  const roadConst = run.roadConstDepth ?? 0;
  const averageDepth = parseFloat(((depthFrom + depthTo) / 2 - roadConst).toFixed(3));
  const runDepthCategory = Math.round(averageDepth / 0.25) * 0.25;
  const diamNum = parseInt(run.diameter, 10) || 0;
  const concreteProtection = runDepthCategory - diamNum / 1000 <= 1.25 ? run.length || 0 : 0;
  const rockers = diamNum > 0 ? 2 : 0;
  const pipeType = (run.pipeType || '').toLowerCase();
  const gullyDiaMm = pairedGullyDiameter ? parseInt(pairedGullyDiameter, 10) || 0 : 0;
  let junctionSize: string;
  if (pipeType === 'rigi' && diamNum >= 450) {
    junctionSize = `${diamNum}mm Saddle`;
  } else if ((pipeType === 'conc' || pipeType === 'concrete') && diamNum >= 525) {
    junctionSize = 'Core Drill';
  } else if (diamNum > 0 && gullyDiaMm > 0) {
    junctionSize = `${diamNum} x ${gullyDiaMm}mm junction`;
  } else {
    junctionSize = '';
  }
  return { ...run, depthFrom, depthTo, averageDepth, runDepthCategory, concreteProtection, rockers, junctionSize };
}

function calcStormLateralDerived(
  lat: LateralConnection,
  manholes: Manhole[],
  pairedRunDepthCategory: number,
): LateralConnection {
  const demarcDepth = parseFloat(((lat.demarcCoverLevel ?? 0) - (lat.demarcInvertLevel ?? 0)).toFixed(3));
  const demarcDepthCategory = Math.round(demarcDepth / 0.25) * 0.25;
  const hasJunction = lat.junctionOffRun && lat.junctionOffRun > 0;
  let aveRunDepth: number;
  if (hasJunction) {
    const mhDepthCat = manholes.find((m) => m.ref === lat.toManhole)?.depthCategory ?? 0;
    aveRunDepth = (pairedRunDepthCategory + mhDepthCat) / 2;
  } else {
    aveRunDepth = (pairedRunDepthCategory + demarcDepthCategory) / 2;
  }
  const averageDepth = parseFloat(aveRunDepth.toFixed(3));
  const avgDepthCategory = Math.round(averageDepth / 0.25) * 0.25;
  return { ...lat, demarcDepth, demarcDepthCategory, averageDepth, avgDepthCategory };
}

const GULLY_DEPTH_M = 0.9;

function calcStormGullyDerived(
  gully: GullyConnection,
  pairedRunDepthCategory: number,
  pairedJunctions: number,
  pairedLateralJunctionOffRun: number,
  pairedFromMH?: string,
  pairedToMH?: string,
): GullyConnection {
  // Gully Depth is a fixed constant, not user-editable — always 0.900m.
  const gullyDepth = GULLY_DEPTH_M;
  const depthOffMainRun = pairedRunDepthCategory;
  const aveRunDepth = parseFloat(((depthOffMainRun + gullyDepth) / 2).toFixed(3));
  const avgRunDepthCategory = Math.round(aveRunDepth / 0.25) * 0.25;
  const len = gully.length ?? 0;
  const concBS = len === 0 ? 0 : avgRunDepthCategory <= 1.25 ? len : 0;
  const gulleys = pairedJunctions + (gully.gulleyIntoManhole ?? 0) - pairedLateralJunctionOffRun;
  // Run Ref is not user-editable — it always mirrors the paired main sewer run's
  // From Manhole – To Manhole (positional pairing), same as the Reference shown
  // elsewhere for a run (e.g. formula-info row labels).
  const runRef = pairedFromMH && pairedToMH ? `${pairedFromMH}-${pairedToMH}` : (pairedFromMH || pairedToMH || '');
  return { ...gully, gullyDepth, depthOffMainRun, averageDepth: aveRunDepth, avgDepthCategory: avgRunDepthCategory, concBS, gulleys, runRef };
}

const calcCellStyle: React.CSSProperties = {
  padding: '4px 8px', textAlign: 'center', background: '#f5f5f5',
  borderRadius: 4, border: '1px solid #e5e7eb', color: '#374151', fontSize: 13,
};

const StormSewersTakeoff: React.FC = () => {
  const showDevMode = useDevMode();
  useScrollToTop({ behavior: 'smooth', delay: 100 });
  const { state, updateField, saveTender } = useTender();
  const { settings } = useGlobalSettings();
  useAutoSave();
  const navigate = useNavigate();

  // Item library for dev-mode rate lookups by evaluate code — only fetched
  // when the dev-only Evaluate Code columns are actually shown.
  const { data: itemLibraryData } = useItemLibrary(state.region || "", undefined, {
    enabled: showDevMode,
  });
  const resourceCodeMap = useMemo(() => {
    const map = new Map<string, number>();
    itemLibraryData?.forEach((item) => {
      const code = item.resourceCode?.trim().toUpperCase();
      if (code) map.set(code, item.nettRate || item.baseRate || 0);
    });
    return map;
  }, [itemLibraryData]);
  const getLibRate = (code?: string): number | undefined => {
    if (!code) return undefined;
    return resourceCodeMap.get(code.trim().toUpperCase());
  };

  const handleSaveAndContinue = () => {
    updateField("status", "In-progress");
    updateField("boqStatus", "In-progress");
    updateField("lastModified", new Date());
    saveTender();
    navigate("/roads-sewers/roads");
  };

  const [editingCell, setEditingCell] = useState<{
    table: string;
    id: string;
    field: string;
  } | null>(null);

  const stormSewersData = state.stormSewers;

  const [activeTab, setActiveTab] = useState("manholes");
  // Defers this step's per-item tables' (Manholes, Storm Take Off) mount by
  // one paint cycle so a tender with a lot of items doesn't make the tab
  // look frozen while the table's render/layout blocks the main thread -
  // see useTableRenderDeferral's own doc comment. Re-keyed on `activeTab`
  // since switching tabs here unmounts/mounts a brand new Table, not just a
  // dataSource update - so the loader should reappear on every tab switch,
  // not just the initial mount. Purely a perceived-lag fix: once it flips
  // false the table(s) render exactly as before.
  const isTableLoading = useTableRenderDeferral([activeTab]);


  // Shared row-delete confirmation — matches the DeleteConfirmationModal pattern
  // used by Groundworks tables, so every delete action in this page asks for
  // confirmation the same way instead of deleting immediately on click.
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<(() => void) | null>(null);
  const [pendingDeleteCount, setPendingDeleteCount] = useState(1);
  const requestDelete = (action: () => void, count: number = 1) => {
    setPendingDelete(() => action);
    setPendingDeleteCount(count);
    setDeleteModalVisible(true);
  };
  const confirmDelete = () => {
    pendingDelete?.();
    setDeleteModalVisible(false);
    setPendingDelete(null);
  };
  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setPendingDelete(null);
  };

  // Box Culverts (id prefix 'bc_'), Cellular Attenuation ('ca_'), Conc
  // Attenuation ('conc_'), Ponds/Swales ('ps_'/'pond_'/'swale_'/'hw_') and
  // Weholite Attenuation ('wh_') rows each live in their own top-level state
  // field's .table — merge for read purposes (ids are disjoint by prefix),
  // route writes back to whichever top-level field the row's id prefix indicates.
  const stormBuildUpData: Record<string, FoulBuildUpRowData> = {
    ...state.boxCulverts.table,
    ...state.cellularAttenuation.table,
    ...state.concAttenuation.table,
    ...state.pondsSwales.table,
    ...state.weholiteAttenuation.table,
  };

  const getStormBuildUpRow = (id: string, defaultQuant: number | null): FoulBuildUpRowData => ({
    quant: stormBuildUpData[id]?.quant !== undefined ? stormBuildUpData[id].quant : defaultQuant,
    evaluateCode: stormBuildUpData[id]?.evaluateCode ?? '',
    quantText: stormBuildUpData[id]?.quantText,
  });

  const updateStormBuildUpRow = (id: string, field: 'quant' | 'evaluateCode' | 'quantText', value: number | null | string) => {
    const current = stormBuildUpData[id] ?? { quant: null, evaluateCode: '' };
    const updatedRow = { ...current, [field]: value };
    if (id.startsWith('bc_')) {
      updateField('boxCulverts', {
        ...state.boxCulverts,
        table: { ...state.boxCulverts.table, [id]: updatedRow },
      });
    } else if (id.startsWith('ca_')) {
      updateField('cellularAttenuation', {
        ...state.cellularAttenuation,
        table: { ...state.cellularAttenuation.table, [id]: updatedRow },
      });
    } else if (id.startsWith('conc_')) {
      updateField('concAttenuation', {
        ...state.concAttenuation,
        table: { ...state.concAttenuation.table, [id]: updatedRow },
      });
    } else if (id.startsWith('ps_') || id.startsWith('pond_') || id.startsWith('swale_') || id.startsWith('hw_')) {
      updateField('pondsSwales', {
        ...state.pondsSwales,
        table: { ...state.pondsSwales.table, [id]: updatedRow },
      });
    } else if (id.startsWith('wh_')) {
      updateField('weholiteAttenuation', {
        ...state.weholiteAttenuation,
        table: { ...state.weholiteAttenuation.table, [id]: updatedRow },
      });
    }
  };

  const buildStormBuildUpColumns = (sectionTitle: string) => [
    {
      title: <span style={{ fontWeight: 700, fontSize: 13 }}>{sectionTitle}</span>,
      dataIndex: 'description',
      key: 'description',
      render: (_: string, record: StormBuildUpRowDef) => {
        if (record.isHeader) {
          return (
            <Typography.Text strong className="text-indigo-600">
              {record.description}
            </Typography.Text>
          );
        }
        return <Typography.Text>{record.description}</Typography.Text>;
      },
    },
    {
      title: 'Qty',
      dataIndex: 'id',
      key: 'quant',
      width: 120,
      align: 'center' as const,
      render: (_: string, record: StormBuildUpRowDef) => {
        if (record.isHeader) return null;
        const { quant } = getStormBuildUpRow(record.id, record.defaultQuant);
        return (
          <NumberInput
            value={quant ?? 0}
            onChange={(val) => updateStormBuildUpRow(record.id, 'quant', val)}
            decimalPlaces={3}
            positiveOnly
            placeholder="0.000"
            style={{ width: '100%', textAlign: 'right' }}
          />
        );
      },
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 70,
      align: 'center' as const,
      render: (_: string, record: StormBuildUpRowDef) => {
        if (record.isHeader) return null;
        const display = formatUnit(record.unit);
        return <span style={{ color: '#6b7280' }}>{display}</span>;
      },
    },
    ...(showDevMode ? [{
      title: 'Evaluate Code',
      dataIndex: 'id',
      key: 'evaluateCode',
      width: 160,
      render: (_: string, record: StormBuildUpRowDef) => {
        if (record.isHeader) return null;
        const { evaluateCode } = getStormBuildUpRow(record.id, record.defaultQuant);
        const libRate = getLibRate(evaluateCode);
        return (
          <span>
            <span style={{ color: '#6b7280', fontSize: 12 }}>{evaluateCode || '—'}</span>
            {libRate !== undefined && (
              <div style={{ color: '#52c41a', fontSize: 12, marginTop: 2 }}>
                {formatCurrency(libRate)}
              </div>
            )}
          </span>
        );
      },
    }] : []),
  ];

  const [stickyOffset, setStickyOffset] = useState(310);
  const [selectedManholeKeys, setSelectedManholeKeys] = useState<React.Key[]>([]);
  const [selectedCombinedStormKeys, setSelectedCombinedStormKeys] = useState<React.Key[]>([]);
  const [ratesModalVisible, setRatesModalVisible] = useState(false);
  const [summaryExpandedKeys, setSummaryExpandedKeys] = useState<React.Key[]>([]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setSummaryExpandedKeys([]);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };
  const [stormFormulaInfoKey, setStormFormulaInfoKey] = useState<string | null>(null);
  const [stormSurplusBackfillFormula, setStormSurplusBackfillFormula] = useState<StormSurplusBackfillFormulaInfo | null>(null);
  const [stormSummaryView, setStormSummaryView] = useState<'summary' | 'surplus' | 'backfill'>('summary');
  const [pondsSwalesView, setPondsSwalesView] = useState<'pond' | 'swale' | 'headwall'>('pond');
  useScrollToTop({ behavior: 'smooth', delay: 100, trigger: pondsSwalesView });
  const [pondsSwalesActiveKeys, setPondsSwalesActiveKeys] = useState<string[]>([]);
  const pondSectionCount = state.pondsSwales.config?.pondSectionCount ?? DEFAULT_POND_SECTIONS;
  // "Ex Topsoil Depth Utilised" (Global Settings) drives the "Excavate to reduce
  // levels to take up Xmm top soil, deposit onsite" row text — same source/
  // fallback convention as FoulSewersTakeoff.tsx's topsoilStripDescription.
  const exTopsoilMm = Math.round((state.roads.cutFillSections.config.stripValueDefault ?? 0.3) * 1000);
  const pondTopsoilThicknessMm = state.pondsSwales.config?.pondTopsoilThicknessMm ?? {};
  const swaleSectionCount = state.pondsSwales.config?.swaleSectionCount ?? DEFAULT_SWALE_SECTIONS;
  const swaleTopsoilThicknessMm = state.pondsSwales.config?.swaleTopsoilThicknessMm ?? {};
  const pondsSwalesSectionTitles = state.pondsSwales.config?.sectionTitles ?? {};
  // Pencil next to a pond/swale title: saves the typed text per section (empty removes it).
  const handleEditPondsSwalesTitle = (headerId: string, text: string) => {
    const next = { ...pondsSwalesSectionTitles };
    if (text) next[headerId] = text;
    else delete next[headerId];
    updateField('pondsSwales', { ...state.pondsSwales, config: { ...state.pondsSwales.config, sectionTitles: next } });
  };
  const pondsSwalesRows = pondsSwalesView === 'pond' ? getPondsRows(pondSectionCount, exTopsoilMm, pondTopsoilThicknessMm, pondsSwalesSectionTitles) : pondsSwalesView === 'swale' ? getSwalesRows(swaleSectionCount, exTopsoilMm, swaleTopsoilThicknessMm, pondsSwalesSectionTitles) : HEADWALL_ROWS;
  const pondsSwalesSectionKeys = pondsSwalesRows.filter((r) => r.isHeader).map((r) => r.id);
  const pondsSwalesAllExpanded = pondsSwalesActiveKeys.length === pondsSwalesSectionKeys.length;

  const handleAddPond = () => {
    updateField('pondsSwales', {
      ...state.pondsSwales,
      config: { ...state.pondsSwales.config, pondSectionCount: pondSectionCount + 1 },
    });
  };
  const handleDeletePondSection = (headerId: string) => {
    const match = headerId.match(/^pond_(\d+)_hdr$/);
    if (!match) return;
    const n = Number(match[1]);
    requestDelete(() => {
      updateField('pondsSwales', {
        ...state.pondsSwales,
        table: shiftPondSectionKeys(state.pondsSwales.table, n),
        config: {
          ...state.pondsSwales.config,
          pondSectionCount: Math.max(0, pondSectionCount - 1),
          pondTopsoilThicknessMm: shiftPondSectionKeys(pondTopsoilThicknessMm, n),
          sectionTitles: shiftPondSectionKeys(pondsSwalesSectionTitles, n),
        },
      });
      setPondsSwalesActiveKeys([]);
    });
  };

  // Configure Pond Section Modal — per-pond "Top soil spread, xxxmm thick"
  // override (site won + imported, separate line items like POS.tsx), same
  // Configure-gear/ColumnConfigModal pattern as POS.tsx's per-section thickness fields.
  const [pondConfigModalVisible, setPondConfigModalVisible] = useState(false);
  const [pondConfigSectionNo, setPondConfigSectionNo] = useState<number | null>(null);
  const [pondConfigDraftSiteWonMm, setPondConfigDraftSiteWonMm] = useState(0);
  const [pondConfigDraftImportedMm, setPondConfigDraftImportedMm] = useState(0);
  const handleConfigurePondSection = (headerId: string) => {
    const match = headerId.match(/^pond_(\d+)_hdr$/);
    if (!match) return;
    const n = Number(match[1]);
    setPondConfigSectionNo(n);
    setPondConfigDraftSiteWonMm(pondTopsoilThicknessMm[`pond_${n}_topsoilSiteWon`] ?? 0);
    setPondConfigDraftImportedMm(pondTopsoilThicknessMm[`pond_${n}_topsoilImported`] ?? 0);
    setPondConfigModalVisible(true);
  };
  const applyPondSectionConfig = () => {
    if (pondConfigSectionNo === null) return;
    const siteWonRowId = `pond_${pondConfigSectionNo}_topsoilSiteWon`;
    const importedRowId = `pond_${pondConfigSectionNo}_topsoilImported`;
    const nextPondTopsoilThicknessMm = { ...pondTopsoilThicknessMm };
    if (pondConfigDraftSiteWonMm > 0) {
      nextPondTopsoilThicknessMm[siteWonRowId] = pondConfigDraftSiteWonMm;
    } else {
      delete nextPondTopsoilThicknessMm[siteWonRowId];
    }
    if (pondConfigDraftImportedMm > 0) {
      nextPondTopsoilThicknessMm[importedRowId] = pondConfigDraftImportedMm;
    } else {
      delete nextPondTopsoilThicknessMm[importedRowId];
    }
    updateField('pondsSwales', {
      ...state.pondsSwales,
      config: { ...state.pondsSwales.config, pondTopsoilThicknessMm: nextPondTopsoilThicknessMm },
    });
    setPondConfigModalVisible(false);
  };

  const handleAddSwale = () => {
    updateField('pondsSwales', {
      ...state.pondsSwales,
      config: { ...state.pondsSwales.config, swaleSectionCount: swaleSectionCount + 1 },
    });
  };
  const handleDeleteSwaleSection = (headerId: string) => {
    const match = headerId.match(/^swale_(\d+)_hdr$/);
    if (!match) return;
    const n = Number(match[1]);
    requestDelete(() => {
      updateField('pondsSwales', {
        ...state.pondsSwales,
        table: shiftSwaleSectionKeys(state.pondsSwales.table, n),
        config: {
          ...state.pondsSwales.config,
          swaleSectionCount: Math.max(0, swaleSectionCount - 1),
          swaleTopsoilThicknessMm: shiftSwaleSectionKeys(swaleTopsoilThicknessMm, n),
          sectionTitles: shiftSwaleSectionKeys(pondsSwalesSectionTitles, n),
        },
      });
      setPondsSwalesActiveKeys([]);
    });
  };

  // Configure Swale Section Modal — per-swale "Top soil spread, xxxmm thick"
  // override (site won + imported, separate line items like POS.tsx), mirrors
  // the Pond Configure modal above.
  const [swaleConfigModalVisible, setSwaleConfigModalVisible] = useState(false);
  const [swaleConfigSectionNo, setSwaleConfigSectionNo] = useState<number | null>(null);
  const [swaleConfigDraftSiteWonMm, setSwaleConfigDraftSiteWonMm] = useState(0);
  const [swaleConfigDraftImportedMm, setSwaleConfigDraftImportedMm] = useState(0);
  const handleConfigureSwaleSection = (headerId: string) => {
    const match = headerId.match(/^swale_(\d+)_hdr$/);
    if (!match) return;
    const n = Number(match[1]);
    setSwaleConfigSectionNo(n);
    setSwaleConfigDraftSiteWonMm(swaleTopsoilThicknessMm[`swale_${n}_topsoilSiteWon`] ?? 0);
    setSwaleConfigDraftImportedMm(swaleTopsoilThicknessMm[`swale_${n}_topsoilImported`] ?? 0);
    setSwaleConfigModalVisible(true);
  };
  const applySwaleSectionConfig = () => {
    if (swaleConfigSectionNo === null) return;
    const siteWonRowId = `swale_${swaleConfigSectionNo}_topsoilSiteWon`;
    const importedRowId = `swale_${swaleConfigSectionNo}_topsoilImported`;
    const nextSwaleTopsoilThicknessMm = { ...swaleTopsoilThicknessMm };
    if (swaleConfigDraftSiteWonMm > 0) {
      nextSwaleTopsoilThicknessMm[siteWonRowId] = swaleConfigDraftSiteWonMm;
    } else {
      delete nextSwaleTopsoilThicknessMm[siteWonRowId];
    }
    if (swaleConfigDraftImportedMm > 0) {
      nextSwaleTopsoilThicknessMm[importedRowId] = swaleConfigDraftImportedMm;
    } else {
      delete nextSwaleTopsoilThicknessMm[importedRowId];
    }
    updateField('pondsSwales', {
      ...state.pondsSwales,
      config: { ...state.pondsSwales.config, swaleTopsoilThicknessMm: nextSwaleTopsoilThicknessMm },
    });
    setSwaleConfigModalVisible(false);
  };

  // Cellular Attenuation — same dynamic Add/Delete section pattern as Pond/Swale,
  // just without a Configure modal (no configurable placeholder field here).
  const [cellularActiveKeys, setCellularActiveKeys] = useState<string[]>([]);
  const cellularSectionCount = state.cellularAttenuation.config?.sectionCount ?? DEFAULT_CELLULAR_SECTIONS;
  const cellularSupplierName = state.cellularAttenuation.config?.supplierName ?? 'Cellular';
  const cellularCrateDims = state.cellularAttenuation.config?.crateDims ?? {};
  const cellularSectionTitles = state.cellularAttenuation.config?.sectionTitles ?? {};
  const cellularRows = getCellularAttenuationRows(cellularSectionCount, cellularSupplierName, cellularCrateDims, cellularSectionTitles);
  // Pencil next to a tank title: saves the typed text per section (empty removes it).
  const handleEditCellularTitle = (headerId: string, text: string) => {
    const next = { ...cellularSectionTitles };
    if (text) next[headerId] = text;
    else delete next[headerId];
    updateField('cellularAttenuation', { ...state.cellularAttenuation, config: { ...state.cellularAttenuation.config, sectionTitles: next } });
  };
  const cellularSectionKeys = cellularRows.filter((r) => r.isHeader).map((r) => r.id);
  const cellularAllExpanded = cellularActiveKeys.length === cellularSectionKeys.length;
  const handleAddCellularTank = () => {
    const newN = cellularSectionCount + 1;
    const newTankRows: Record<string, FoulBuildUpRowData> = {};
    CELLULAR_ROW_SUFFIXES.forEach((suffix) => {
      newTankRows[`ca_${newN}_${suffix}`] = { quant: 0, evaluateCode: '' };
    });
    updateField('cellularAttenuation', {
      ...state.cellularAttenuation,
      table: { ...state.cellularAttenuation.table, ...newTankRows },
      config: { ...state.cellularAttenuation.config, sectionCount: newN },
    });
  };
  const handleDeleteCellularSection = (headerId: string) => {
    const match = headerId.match(/^ca_(\d+)_hdr$/);
    if (!match) return;
    const n = Number(match[1]);
    requestDelete(() => {
      updateField('cellularAttenuation', {
        ...state.cellularAttenuation,
        table: shiftCellularAttenuationSectionKeys(state.cellularAttenuation.table, n),
        config: {
          ...state.cellularAttenuation.config,
          sectionCount: Math.max(0, cellularSectionCount - 1),
          crateDims: shiftCellularAttenuationSectionKeys(cellularCrateDims, n),
          sectionTitles: shiftCellularAttenuationSectionKeys(cellularSectionTitles, n),
        },
      });
      setCellularActiveKeys([]);
    });
  };

  // Configure Tank Section Modal (Cellular Attenuation) — per-tank crate size
  // (L x W x H in metres) used in the "Supply & Fit ... Crates" row wording.
  const [cellularConfigModalVisible, setCellularConfigModalVisible] = useState(false);
  const [cellularConfigSectionNo, setCellularConfigSectionNo] = useState<number | null>(null);
  const [cellularConfigDraft, setCellularConfigDraft] = useState<Record<string, number>>({});
  const handleConfigureCellularSection = (headerId: string) => {
    const match = headerId.match(/^ca_(\d+)_hdr$/);
    if (!match) return;
    const n = Number(match[1]);
    setCellularConfigSectionNo(n);
    setCellularConfigDraft(Object.fromEntries(
      [...CELLULAR_CRATE_FIELDS, ...CELLULAR_SUB_CRATE_FIELDS].map((f) => [f.suffix, cellularCrateDims[`ca_${n}_${f.suffix}`] ?? f.default])
    ));
    setCellularConfigModalVisible(true);
  };
  const applyCellularSectionConfig = () => {
    if (cellularConfigSectionNo === null) return;
    const n = cellularConfigSectionNo;
    const nextCrateDims = { ...cellularCrateDims };
    [...CELLULAR_CRATE_FIELDS, ...CELLULAR_SUB_CRATE_FIELDS].forEach((f) => {
      const value = cellularConfigDraft[f.suffix];
      // Unset (back to the default) rather than storing 0 / the default value.
      if (value > 0 && value !== f.default) nextCrateDims[`ca_${n}_${f.suffix}`] = value;
      else delete nextCrateDims[`ca_${n}_${f.suffix}`];
    });
    updateField('cellularAttenuation', {
      ...state.cellularAttenuation,
      config: { ...state.cellularAttenuation.config, crateDims: nextCrateDims },
    });
    setCellularConfigModalVisible(false);
  };

  // Weholite Attenuation — same dynamic Add/Delete section pattern as Cellular
  // Attenuation, seeding qty 0 on a freshly-added tank.
  const [weholiteActiveKeys, setWeholiteActiveKeys] = useState<string[]>([]);
  const weholiteSectionCount = state.weholiteAttenuation.config?.sectionCount ?? DEFAULT_WEHOLITE_SECTIONS;
  const weholiteSupplierName = state.weholiteAttenuation.config?.supplierName ?? 'Weholite';
  const weholiteConfigPct = state.weholiteAttenuation.config?.weholiteConfigPct ?? {};
  const weholiteSectionTitles = state.weholiteAttenuation.config?.sectionTitles ?? {};
  const weholiteRows = getWeholiteAttenuationRows(weholiteSectionCount, weholiteConfigPct, weholiteSupplierName, weholiteSectionTitles);
  const handleEditWeholiteTitle = (headerId: string, text: string) => {
    const next = { ...weholiteSectionTitles };
    if (text) next[headerId] = text;
    else delete next[headerId];
    updateField('weholiteAttenuation', { ...state.weholiteAttenuation, config: { ...state.weholiteAttenuation.config, sectionTitles: next } });
  };
  const weholiteSectionKeys = weholiteRows.filter((r) => r.isHeader).map((r) => r.id);
  const weholiteAllExpanded = weholiteActiveKeys.length === weholiteSectionKeys.length;
  const handleAddWeholiteTank = () => {
    const newN = weholiteSectionCount + 1;
    const newTankRows: Record<string, FoulBuildUpRowData> = {};
    WEHOLITE_ROW_SUFFIXES.forEach((suffix) => {
      newTankRows[`wh_${newN}_${suffix}`] = { quant: 0, evaluateCode: '' };
    });
    updateField('weholiteAttenuation', {
      ...state.weholiteAttenuation,
      table: { ...state.weholiteAttenuation.table, ...newTankRows },
      config: { ...state.weholiteAttenuation.config, sectionCount: newN },
    });
  };
  const handleDeleteWeholiteSection = (headerId: string) => {
    const match = headerId.match(/^wh_(\d+)_hdr$/);
    if (!match) return;
    const n = Number(match[1]);
    requestDelete(() => {
      updateField('weholiteAttenuation', {
        ...state.weholiteAttenuation,
        table: shiftWeholiteSectionKeys(state.weholiteAttenuation.table, n),
        config: {
          ...state.weholiteAttenuation.config,
          sectionCount: Math.max(0, weholiteSectionCount - 1),
          weholiteConfigPct: shiftWeholiteSectionKeys(weholiteConfigPct, n),
          sectionTitles: shiftWeholiteSectionKeys(weholiteSectionTitles, n),
        },
      });
      setWeholiteActiveKeys([]);
    });
  };

  // Configure Tank Section Modal (Weholite Attenuation) — per-tank FP/OHP %
  // override, same Configure-gear/ColumnConfigModal pattern as Conc
  // Attenuation's own modal.
  const [weholiteConfigModalVisible, setWeholiteConfigModalVisible] = useState(false);
  const [weholiteConfigSectionNo, setWeholiteConfigSectionNo] = useState<number | null>(null);
  const WEHOLITE_CONFIG_DRAFT_SUFFIXES = ['fixedPrice', 'ohp'] as const;
  const [weholiteConfigDraft, setWeholiteConfigDraft] = useState<Record<typeof WEHOLITE_CONFIG_DRAFT_SUFFIXES[number], number>>({
    fixedPrice: 0, ohp: 0,
  });
  const handleConfigureWeholiteSection = (headerId: string) => {
    const match = headerId.match(/^wh_(\d+)_hdr$/);
    if (!match) return;
    const n = Number(match[1]);
    setWeholiteConfigSectionNo(n);
    setWeholiteConfigDraft(Object.fromEntries(
      WEHOLITE_CONFIG_DRAFT_SUFFIXES.map((suffix) => [suffix, weholiteConfigPct[`wh_${n}_${suffix}`] ?? 0])
    ) as Record<typeof WEHOLITE_CONFIG_DRAFT_SUFFIXES[number], number>);
    setWeholiteConfigModalVisible(true);
  };
  const applyWeholiteSectionConfig = () => {
    if (weholiteConfigSectionNo === null) return;
    const n = weholiteConfigSectionNo;
    const nextWeholiteConfigPct = { ...weholiteConfigPct };
    WEHOLITE_CONFIG_DRAFT_SUFFIXES.forEach((suffix) => {
      const rowId = `wh_${n}_${suffix}`;
      const value = weholiteConfigDraft[suffix];
      if (value > 0) nextWeholiteConfigPct[rowId] = value;
      else delete nextWeholiteConfigPct[rowId];
    });
    updateField('weholiteAttenuation', {
      ...state.weholiteAttenuation,
      config: { ...state.weholiteAttenuation.config, weholiteConfigPct: nextWeholiteConfigPct },
    });
    setWeholiteConfigModalVisible(false);
  };

  // Conc Attenuation — same dynamic Add/Delete section pattern as Cellular/
  // Weholite Attenuation, seeding qty 0 on a freshly-added tank.
  const [concActiveKeys, setConcActiveKeys] = useState<string[]>([]);
  const concSectionCount = state.concAttenuation.config?.sectionCount ?? DEFAULT_CONC_SECTIONS;
  const concConfigPct = state.concAttenuation.config?.concConfigPct ?? {};
  const concSupplierName = state.concAttenuation.config?.supplierName ?? 'Carlow';
  const concSectionTitles = state.concAttenuation.config?.sectionTitles ?? {};
  const concDrawingRefs = state.concAttenuation.config?.drawingRefs ?? {};
  const concRows = getConcAttenuationRows(concSectionCount, concConfigPct, concSupplierName, concSectionTitles, concDrawingRefs);
  const handleEditConcTitle = (headerId: string, text: string) => {
    const next = { ...concSectionTitles };
    if (text) next[headerId] = text;
    else delete next[headerId];
    updateField('concAttenuation', { ...state.concAttenuation, config: { ...state.concAttenuation.config, sectionTitles: next } });
  };
  const concSectionKeys = concRows.filter((r) => r.isHeader).map((r) => r.id);
  const concAllExpanded = concActiveKeys.length === concSectionKeys.length;
  const handleAddConcTank = () => {
    const newN = concSectionCount + 1;
    const newTankRows: Record<string, FoulBuildUpRowData> = {};
    CONC_ROW_SUFFIXES.forEach((suffix) => {
      newTankRows[`conc_${newN}_${suffix}`] = { quant: 0, evaluateCode: '' };
    });
    updateField('concAttenuation', {
      ...state.concAttenuation,
      table: { ...state.concAttenuation.table, ...newTankRows },
      config: { ...state.concAttenuation.config, sectionCount: newN },
    });
  };
  const handleDeleteConcSection = (headerId: string) => {
    const match = headerId.match(/^conc_(\d+)_hdr$/);
    if (!match) return;
    const n = Number(match[1]);
    requestDelete(() => {
      updateField('concAttenuation', {
        ...state.concAttenuation,
        table: shiftConcAttenuationSectionKeys(state.concAttenuation.table, n),
        config: {
          ...state.concAttenuation.config,
          sectionCount: Math.max(0, concSectionCount - 1),
          concConfigPct: shiftConcAttenuationSectionKeys(concConfigPct, n),
          sectionTitles: shiftConcAttenuationSectionKeys(concSectionTitles, n),
          drawingRefs: shiftConcAttenuationSectionKeys(concDrawingRefs, n),
        },
      });
      setConcActiveKeys([]);
    });
  };

  // Configure Tank Section Modal — per-tank FP/OHP % override, same
  // Configure-gear/ColumnConfigModal pattern as Box Culverts' own modal.
  const [concConfigModalVisible, setConcConfigModalVisible] = useState(false);
  const [concConfigSectionNo, setConcConfigSectionNo] = useState<number | null>(null);
  const [concDrawingRefDraft, setConcDrawingRefDraft] = useState('');
  const CONC_CONFIG_DRAFT_SUFFIXES = ['tankWidth', 'tankLength', 'tankDepth', 'fp5', 'ohp15', 'mot150', 'haulRoadWidth', 'haulRoadThick', 'craneVisit', 'craneProgramme', 'concPump', 'conc58', 'mortar1', 'steel', 'skip', 'supplyCar'] as const;
  const [concConfigDraft, setConcConfigDraft] = useState<Record<typeof CONC_CONFIG_DRAFT_SUFFIXES[number], number>>({
    tankWidth: 0, tankLength: 0, tankDepth: 0, fp5: 0, ohp15: 0, mot150: 0, haulRoadWidth: 0, haulRoadThick: 0, craneVisit: 0, craneProgramme: 0, concPump: 0, conc58: 0, mortar1: 0, steel: 0, skip: 0, supplyCar: 0,
  });
  const handleConfigureConcSection = (headerId: string) => {
    const match = headerId.match(/^conc_(\d+)_hdr$/);
    if (!match) return;
    const n = Number(match[1]);
    setConcConfigSectionNo(n);
    setConcDrawingRefDraft(concDrawingRefs[`conc_${n}_drawingRef`] ?? CONC_DEFAULT_DRAWING_REF);
    setConcConfigDraft(Object.fromEntries(
      CONC_CONFIG_DRAFT_SUFFIXES.map((suffix) => [suffix, concConfigPct[`conc_${n}_${suffix}`] ?? 0])
    ) as Record<typeof CONC_CONFIG_DRAFT_SUFFIXES[number], number>);
    setConcConfigModalVisible(true);
  };
  const applyConcSectionConfig = () => {
    if (concConfigSectionNo === null) return;
    const n = concConfigSectionNo;
    const nextConcConfigPct = { ...concConfigPct };
    CONC_CONFIG_DRAFT_SUFFIXES.forEach((suffix) => {
      const rowId = `conc_${n}_${suffix}`;
      const value = concConfigDraft[suffix];
      if (value > 0) nextConcConfigPct[rowId] = value;
      else delete nextConcConfigPct[rowId];
    });
    // Unset (back to the default) rather than storing the default text.
    const nextDrawingRefs = { ...concDrawingRefs };
    const drawingRef = concDrawingRefDraft.trim();
    if (drawingRef && drawingRef !== CONC_DEFAULT_DRAWING_REF) nextDrawingRefs[`conc_${n}_drawingRef`] = drawingRef;
    else delete nextDrawingRefs[`conc_${n}_drawingRef`];
    updateField('concAttenuation', {
      ...state.concAttenuation,
      config: { ...state.concAttenuation.config, concConfigPct: nextConcConfigPct, drawingRefs: nextDrawingRefs },
    });
    setConcConfigModalVisible(false);
  };

  // Box Culverts — same dynamic Add/Delete section pattern as Cellular/Conc/
  // Weholite Attenuation, seeding qty 0 on a freshly-added culvert.
  const [boxCulvertActiveKeys, setBoxCulvertActiveKeys] = useState<string[]>([]);
  const boxCulvertSectionCount = state.boxCulverts.config?.sectionCount ?? DEFAULT_BOX_CULVERT_SECTIONS;
  const culvertConfigMm = state.boxCulverts.config?.culvertConfigMm ?? {};
  const culvertBackfillType = state.boxCulverts.config?.culvertBackfillType ?? {};
  const boxCulvertRows = getBoxCulvertsRows(boxCulvertSectionCount, culvertConfigMm, culvertBackfillType);
  const boxCulvertSectionKeys = boxCulvertRows.filter((r) => r.isHeader).map((r) => r.id);
  const boxCulvertAllExpanded = boxCulvertActiveKeys.length === boxCulvertSectionKeys.length;
  const handleAddBoxCulvert = () => {
    const newN = boxCulvertSectionCount + 1;
    const newCulvertRows: Record<string, FoulBuildUpRowData> = {};
    getBoxCulvertRowSuffixes().forEach((suffix) => {
      newCulvertRows[`bc_${newN}_${suffix}`] = { quant: 0, evaluateCode: '' };
    });
    updateField('boxCulverts', {
      ...state.boxCulverts,
      table: { ...state.boxCulverts.table, ...newCulvertRows },
      config: { ...state.boxCulverts.config, sectionCount: newN },
    });
  };
  const handleDeleteBoxCulvertSection = (headerId: string) => {
    const match = headerId.match(/^bc_(\d+)_hdr$/);
    if (!match) return;
    const n = Number(match[1]);
    requestDelete(() => {
      updateField('boxCulverts', {
        ...state.boxCulverts,
        table: shiftBoxCulvertSectionKeys(state.boxCulverts.table, n),
        config: {
          ...state.boxCulverts.config,
          sectionCount: Math.max(0, boxCulvertSectionCount - 1),
          culvertConfigMm: shiftBoxCulvertSectionKeys(culvertConfigMm, n),
          culvertBackfillType: shiftBoxCulvertSectionKeys(culvertBackfillType, n),
        },
      });
      setBoxCulvertActiveKeys([]);
    });
  };

  // Configure Culvert Section Modal — per-culvert Height/Concrete Blinding/Pipe
  // Bedding overrides, same Configure-gear/ColumnConfigModal pattern as Ponds'
  // topsoil thickness field. Height is entered/stored in mm like the other
  // three but displayed in metres (see the modal JSX below); the rest are
  // plain mm thicknesses.
  const [boxCulvertConfigModalVisible, setBoxCulvertConfigModalVisible] = useState(false);
  const [boxCulvertConfigSectionNo, setBoxCulvertConfigSectionNo] = useState<number | null>(null);
  const [boxCulvertConfigDraft, setBoxCulvertConfigDraft] = useState({ height: 0, concBlinding: 0, bedTop: 0, bedSide: 0, purchase: 0, ohp: 0, specSize: 0, specDepth: 0 });
  const [boxCulvertBackfillDraft, setBoxCulvertBackfillDraft] = useState('');
  const handleConfigureBoxCulvertSection = (headerId: string) => {
    const match = headerId.match(/^bc_(\d+)_hdr$/);
    if (!match) return;
    const n = Number(match[1]);
    setBoxCulvertConfigSectionNo(n);
    setBoxCulvertConfigDraft({
      height: culvertConfigMm[`bc_${n}_height`] ?? 0,
      concBlinding: culvertConfigMm[`bc_${n}_concBlinding`] ?? 0,
      bedTop: culvertConfigMm[`bc_${n}_bedTop`] ?? 0,
      bedSide: culvertConfigMm[`bc_${n}_bedSide`] ?? 0,
      purchase: culvertConfigMm[`bc_${n}_purchase`] ?? 0,
      ohp: culvertConfigMm[`bc_${n}_ohp`] ?? 0,
      specSize: culvertConfigMm[`bc_${n}_specSize`] ?? 0,
      specDepth: culvertConfigMm[`bc_${n}_specDepth`] ?? 0,
    });
    setBoxCulvertBackfillDraft(culvertBackfillType[`bc_${n}_backfill`] ?? '');
    setBoxCulvertConfigModalVisible(true);
  };
  const applyBoxCulvertSectionConfig = () => {
    if (boxCulvertConfigSectionNo === null) return;
    const n = boxCulvertConfigSectionNo;
    const nextCulvertConfigMm = { ...culvertConfigMm };
    (['height', 'concBlinding', 'bedTop', 'bedSide', 'purchase', 'ohp', 'specSize', 'specDepth'] as const).forEach((suffix) => {
      const rowId = `bc_${n}_${suffix}`;
      const value = boxCulvertConfigDraft[suffix];
      if (value > 0) nextCulvertConfigMm[rowId] = value;
      else delete nextCulvertConfigMm[rowId];
    });
    const nextCulvertBackfillType = { ...culvertBackfillType };
    const backfillRowId = `bc_${n}_backfill`;
    if (boxCulvertBackfillDraft) nextCulvertBackfillType[backfillRowId] = boxCulvertBackfillDraft;
    else delete nextCulvertBackfillType[backfillRowId];
    updateField('boxCulverts', {
      ...state.boxCulverts,
      config: { ...state.boxCulverts.config, culvertConfigMm: nextCulvertConfigMm, culvertBackfillType: nextCulvertBackfillType },
    });
    setBoxCulvertConfigModalVisible(false);
  };
  const stormEoConcBedQtys = stormSewersData.pipeRuns.config.eoQtys || {};
  const setStormEoConcBedQty = (key: string, val: number) =>
    updateField('stormSewers', {
      ...stormSewersData,
      pipeRuns: { ...stormSewersData.pipeRuns, config: { ...stormSewersData.pipeRuns.config, eoQtys: { ...stormEoConcBedQtys, [key]: val } } },
    });

  interface StormColCalcInfo {
    title: string;
    formula: string;
    rows: { ref: string; value: number | string; unit: string }[];
    unit: string;
  }
  const [colCalcInfo, setColCalcInfo] = useState<StormColCalcInfo | null>(null);

  // Excel import/export states
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importLoading, setImportLoading] = useState(false);

  // Manhole Schedule configure modal
  type StormMhLadderOption = 'N/A' | 'Stainless' | 'Galvanised';
  const [mhConfigModalVisible, setMhConfigModalVisible] = useState(false);
  const [mhConfigDraft, setMhConfigDraft] = useState<{ ladders: StormMhLadderOption; safetyChains: StormMhLadderOption }>({
    ladders: stormSewersData.manholes.config.manholeScheduleConfig?.ladders ?? 'Galvanised',
    safetyChains: stormSewersData.manholes.config.manholeScheduleConfig?.safetyChains ?? 'Galvanised',
  });

  useEffect(() => {
    const cfg = stormSewersData.manholes.config.manholeScheduleConfig;
    if (cfg) {
      setMhConfigDraft({ ladders: cfg.ladders, safetyChains: cfg.safetyChains });
    }
  }, [stormSewersData.manholes.config.manholeScheduleConfig]);

  const openMhConfigModal = () => {
    const cfg = stormSewersData.manholes.config.manholeScheduleConfig;
    setMhConfigDraft({
      ladders: cfg?.ladders ?? 'Galvanised',
      safetyChains: cfg?.safetyChains ?? 'Galvanised',
    });
    setMhConfigModalVisible(true);
  };

  const applyMhConfig = () => {
    const updated = (stormSewersData.manholes.table || []).map((mh) =>
      calcStormManholeDerived(
        { ...mh, laddersType: mhConfigDraft.ladders, safetyChainType: mhConfigDraft.safetyChains },
        mhConfigDraft.ladders,
        mhConfigDraft.safetyChains,
      )
    );
    updateField('stormSewers', {
      ...stormSewersData,
      manholes: {
        ...stormSewersData.manholes,
        table: updated,
        config: { ...stormSewersData.manholes.config, manholeScheduleConfig: { ladders: mhConfigDraft.ladders, safetyChains: mhConfigDraft.safetyChains } },
      },
    });
    setMhConfigModalVisible(false);
  };

  // Road Construction Depth modal — Depth is Manual/Auto, same convention as
  // WallsSectionBuildUp.tsx's "Net" field / FoulSewersTakeoff.tsx's own copy
  // of this modal: no separate flag, "manual" is detected by comparing the
  // stored depth against the roadLevel − formationLevel auto-calculation.
  const [roadConstModalVisible, setRoadConstModalVisible] = useState(false);
  const [roadConstDraft, setRoadConstDraft] = useState<{ roadLevel: number; formationLevel: number; depth: number }>({ roadLevel: 0, formationLevel: 0, depth: 0 });
  const ROAD_CONST_DEPTH_EPSILON = 0.0005;
  const roadConstAutoDepth = (roadLevel: number, formationLevel: number) => parseFloat((roadLevel - formationLevel).toFixed(3));
  const roadConstDepthOverridden = Math.abs(roadConstDraft.depth - roadConstAutoDepth(roadConstDraft.roadLevel, roadConstDraft.formationLevel)) > ROAD_CONST_DEPTH_EPSILON;
  const getEffectiveRoadConstDepth = (cfg: FoulRoadConstConfig | undefined): number =>
    cfg ? (cfg.depth ?? roadConstAutoDepth(cfg.roadLevel, cfg.formationLevel)) : 0;

  const openRoadConstModal = () => {
    const saved = stormSewersData.pipeRuns.config.roadConstConfig;
    const roadLevel = saved?.roadLevel ?? 0;
    const formationLevel = saved?.formationLevel ?? 0;
    setRoadConstDraft({ roadLevel, formationLevel, depth: getEffectiveRoadConstDepth(saved) });
    setRoadConstModalVisible(true);
  };

  const updateRoadConstLevel = (field: 'roadLevel' | 'formationLevel', value: number) => {
    setRoadConstDraft((prev) => {
      const wasOverridden = Math.abs(prev.depth - roadConstAutoDepth(prev.roadLevel, prev.formationLevel)) > ROAD_CONST_DEPTH_EPSILON;
      const next = { ...prev, [field]: value };
      if (!wasOverridden) next.depth = roadConstAutoDepth(next.roadLevel, next.formationLevel);
      return next;
    });
  };

  const clearRoadConstDepthOverride = () =>
    setRoadConstDraft((prev) => ({ ...prev, depth: roadConstAutoDepth(prev.roadLevel, prev.formationLevel) }));

  const applyRoadConstConfig = () => {
    const depth = roadConstDraft.depth;
    const manholes = stormSewersData.manholes.table || [];
    const allGullies = stormSewersData.gullyConnections.table || [];

    const updatedPipeRuns = (stormSewersData.pipeRuns.table || []).map((run, i) =>
      calcStormPipeRunDerived({ ...run, roadConstDepth: depth }, manholes, allGullies[i]?.pipeDiameter)
    );

    const updatedLaterals = (stormSewersData.lateralConnections.table || []).map((lat, i) =>
      calcStormLateralDerived(lat, manholes, updatedPipeRuns[i]?.runDepthCategory ?? 0)
    );

    const updatedGullies = allGullies.map((gully, i) =>
      calcStormGullyDerived(
        gully,
        updatedPipeRuns[i]?.runDepthCategory ?? 0,
        updatedPipeRuns[i]?.junctions ?? 0,
        updatedLaterals[i]?.junctionOffRun ?? 0,
        updatedPipeRuns[i]?.fromMH,
        updatedPipeRuns[i]?.toMH,
      )
    );

    updateField('stormSewers', {
      ...stormSewersData,
      pipeRuns: { ...stormSewersData.pipeRuns, table: updatedPipeRuns, config: { ...stormSewersData.pipeRuns.config, roadConstConfig: { ...roadConstDraft } } },
      lateralConnections: { ...stormSewersData.lateralConnections, table: updatedLaterals },
      gullyConnections: { ...stormSewersData.gullyConnections, table: updatedGullies },
    });
    setRoadConstModalVisible(false);
  };

  // Get all manholes for storm sewers
  const availableManholes = stormSewersData.manholes.table || [];

  // Calculate sticky header offset
  useEffect(() => {
    const calculateOffset = () => {
      const baseOffset = 144;
      const stickyHeader = document.getElementsByClassName('storm-sewers-sticky-header')?.[0];
      const height = stickyHeader?.getBoundingClientRect?.()?.height || 0;
      setStickyOffset(baseOffset + height);
    };
    calculateOffset();
    const timer = setTimeout(calculateOffset, 100);
    return () => clearTimeout(timer);
    // selectedManholeKeys/selectedCombinedStormKeys are included because the
    // "Delete Selected" button appearing/disappearing next to Configure can
    // change whether the tab row wraps onto a second line, changing its height.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedManholeKeys.length, selectedCombinedStormKeys.length]);

  const addManhole = () => {
    const ladderCfg = stormSewersData.manholes.config.manholeScheduleConfig?.ladders ?? 'Galvanised';
    const safetyCfg = stormSewersData.manholes.config.manholeScheduleConfig?.safetyChains ?? 'Galvanised';
    const baseMH: Manhole = {
      id: Date.now().toString(),
      ref: '',
      type: '',
      easiTrad: undefined,
      diameter: '',
      coverLevel: 0,
      invertLevel: 0,
      sump: 0,
      depth: 0,
      depthCategory: 0,
      ladders: 0,
      safetyChains: 0,
      backdrop: 0,
      muckStopper: 0,
      amps: 0,
    };
    const newManhole = calcStormManholeDerived(baseMH, ladderCfg, safetyCfg);

    updateField("stormSewers", {
      ...stormSewersData,
      manholes: { ...stormSewersData.manholes, table: [...(stormSewersData.manholes.table || []), newManhole] },
    });
  };

  // Export manholes to CSV
  const exportManholes = () => {
    const manholes = stormSewersData.manholes.table || [];
    if (manholes.length === 0) {
      alert("No manholes to export");
      return;
    }

    const headers = [
      "MH Ref",
      "Cover Level (m)",
      "Invert Level (m)",
      "Depth (m)",
      "Depth Category",
      "Diameter",
    ];
    const csvData = [
      headers,
      ...manholes.map((manhole) => [
        manhole.ref,
        manhole.coverLevel.toString(),
        manhole.invertLevel.toString(),
        manhole.depth.toString(),
        manhole.depthCategory.toString(),
        manhole.diameter,
      ]),
    ];

    const csvContent = csvData.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `storm_manholes_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Import manholes from CSV
  const importManholes = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csv = e.target?.result as string;
        const lines = csv.split("\n");
        const headers = lines[0].split(",");

        // Validate headers (trim whitespace and case-insensitive)
        const expectedHeaders = [
          "MH Ref",
          "Cover Level (m)",
          "Invert Level (m)",
          "Depth (m)",
          "Depth Category",
          "Diameter",
        ];
        const trimmedHeaders = headers.map((h) => h.trim());
        const isValidFormat = expectedHeaders.every((header) =>
          trimmedHeaders.some((h) => h.toLowerCase() === header.toLowerCase())
        );

        if (!isValidFormat) {
          console.error("Invalid CSV format. Expected headers:", expectedHeaders.join(", "));
          return;
        }

        // Find header positions
        const getHeaderIndex = (headerName: string) => {
          return trimmedHeaders.findIndex(
            (h) => h.toLowerCase() === headerName.toLowerCase()
          );
        };

        const refIndex = getHeaderIndex("MH Ref");
        const coverLevelIndex = getHeaderIndex("Cover Level (m)");
        const invertLevelIndex = getHeaderIndex("Invert Level (m)");
        const diameterIndex = getHeaderIndex("Diameter");

        const importedManholes: Manhole[] = [];

        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;

          const values = line.split(",").map((v) => v.trim());
          if (values.length < expectedHeaders.length) continue;

          const manhole: Manhole = {
            id: Date.now().toString() + "_" + i,
            ref: values[refIndex] || `MH${i}`,
            coverLevel: parseFloat(values[coverLevelIndex]) || 0,
            invertLevel: parseFloat(values[invertLevelIndex]) || 0,
            depth: 0, // Will be calculated below
            depthCategory: 0, // Will be calculated below
            diameter: values[diameterIndex] || "1200mm",
            type: "storm",
          };

          // Recalculate depth and depth category from cover and invert levels
          manhole.depth = manhole.coverLevel - manhole.invertLevel;
          manhole.depthCategory = Math.ceil(manhole.depth / 0.25) * 0.25;

          importedManholes.push(manhole);
        }

        if (importedManholes.length > 0) {
          // Update manholes: if MH Ref exists, update it; otherwise add new
          const existingManholes = stormSewersData.manholes.table || [];
          const updatedManholes = [...existingManholes];
          let updatedCount = 0;
          let addedCount = 0;

          importedManholes.forEach((importedManhole) => {
            const existingIndex = updatedManholes.findIndex(
              (mh) => mh.ref === importedManhole.ref
            );
            if (existingIndex >= 0) {
              // Update existing manhole
              updatedManholes[existingIndex] = {
                ...updatedManholes[existingIndex],
                ...importedManhole,
              };
              updatedCount++;
            } else {
              // Add new manhole
              updatedManholes.push(importedManhole);
              addedCount++;
            }
          });

          updateField("stormSewers", {
            ...stormSewersData,
            manholes: { ...stormSewersData.manholes, table: updatedManholes },
          });
        } else {
          console.warn("No valid manholes found in the CSV file");
        }
      } catch (error) {
        console.error("Error reading CSV file:", error);
      }
    };
    reader.readAsText(file);

    // Reset the input value so the same file can be selected again
    event.target.value = "";
  };

  // Main Sewer Runs, House Laterals, and Gully connections are displayed as
  // paired columns in the same take-off row (see combinedStormTakeOffData
  // below), but they're stored as three independent arrays — imports in
  // particular fill them from separate row sets, so a run with no lateral or
  // gully (or vice versa) is normal data, not an error. These helpers keep
  // the three arrays the same length by padding the shorter ones with
  // blank-but-editable entries, so every row always has a real object on
  // each side instead of rendering a non-editable "—".
  const makeBlankStormPipeRun = (roadConstDepth: number, seed: number): StormSewerRun => ({
    id: `pr-${seed}`,
    ref: '', fromMH: '', toMH: '', length: 0, diameter: '',
    depthFrom: 0, depthTo: 0, averageDepth: 0, depthCategory: 0,
    runDepthCategory: 0, rockers: 0, pipeType: '', backfillType: '',
    roadConstDepth, concreteProtection: 0, junctionSize: '', junctions: 0,
  });

  const makeBlankStormLateral = (seed: number): LateralConnection => ({
    id: `lat-${seed}`,
    ref: '', connectionType: 'direct_to_manhole',
    length: 0, pipeDiameter: '', pipeType: '', manholeDiameter: '',
    backdrop: 0, depth: 0, requiresFirstOff: false, rockers: 0,
    junctionOffRun: 0, demarcCoverLevel: 0, demarcInvertLevel: 0,
    demarcDepth: 0, demarcDepthCategory: 0, nme: 0, count1200: 0,
  });

  const makeBlankGully = (seed: number): GullyConnection => ({
    id: `gu-${seed}`,
    gullyRef: '', location: '', gullyType: 'road',
    connectionType: 'junction_on_run', length: 0, pipeDiameter: '',
    gullyDepth: GULLY_DEPTH_M, connectionDepth: 0, depthCategory: 0, depthOffMainRun: 0,
    averageDepth: 0, avgDepthCategory: 0, gulleyIntoManhole: 0, junction150x150: 0,
    gulleys: 0, runRef: '', pipeType: '', concBS: 0,
  });

  const padStormPipeRuns = (runs: StormSewerRun[], targetLength: number, roadConstDepth: number, seedBase: number): StormSewerRun[] =>
    runs.length >= targetLength
      ? runs
      : [...runs, ...Array.from({ length: targetLength - runs.length }, (_, i) => makeBlankStormPipeRun(roadConstDepth, seedBase + i))];

  const padStormLaterals = (laterals: LateralConnection[], targetLength: number, seedBase: number): LateralConnection[] =>
    laterals.length >= targetLength
      ? laterals
      : [...laterals, ...Array.from({ length: targetLength - laterals.length }, (_, i) => makeBlankStormLateral(seedBase + i))];

  const padGullies = (gullies: GullyConnection[], targetLength: number, seedBase: number): GullyConnection[] =>
    gullies.length >= targetLength
      ? gullies
      : [...gullies, ...Array.from({ length: targetLength - gullies.length }, (_, i) => makeBlankGully(seedBase + i))];

  // Unified Excel Export
  const handleUnifiedExport = async () => {
    try {
      const data: StormSewersExcelData = {
        manholes: availableManholes,
        pipeRuns: stormSewersData.pipeRuns.table,
        laterals: stormSewersData.lateralConnections.table || [],
        gullies: stormSewersData.gullyConnections.table || [],
      };

      await exportStormSewersToExcel(data, state.projectName || "StormSewers");
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  // Generate Template
  const handleDownloadTemplate = async () => {
    try {
      await generateStormSewersTemplate(state.projectName || "StormSewers");
    } catch (error) {
      console.error("Template generation failed:", error);
    }
  };

  // Unified Excel Import
  const handleUnifiedImport = async (file: File) => {
    setImportLoading(true);
    try {
      const result = await importStormSewersFromExcel(file);

      // The sheet's main-run, house-lateral, and gully sections are read
      // independently (not every run has a lateral or gully), so the
      // imported arrays commonly end up different lengths. Pad the shorter
      // ones so every take-off row renders with editable inputs on all sides
      // straight after import.
      const importedRowCount = Math.max(
        result.data.pipeRuns.length,
        result.data.laterals.length,
        result.data.gullies.length
      );
      const importRoadConstDepth = result.data.roadConstConfig
        ? parseFloat((result.data.roadConstConfig.roadLevel - result.data.roadConstConfig.formationLevel).toFixed(3))
        : 0;
      const importSeed = Date.now();

      // Completely replace all imported data — do not merge with existing records.
      // eoQtys is the exception: it holds the Storm Summary tab's manual-input-only
      // quantities (EO Concrete Bed & Surround, EO Items, Removal of Redundant
      // Sewer), matched by description text out of the 'Storm Summary' sheet if
      // present (see parseStormSummarySheet) — merge rather than replace so a
      // workbook without that sheet, or with only some rows filled in, doesn't
      // wipe out previously-entered values.
      // The imported build-up rows are keyed 'bc_*' (Box Culverts) / 'ca_*'
      // (Cellular Attenuation) / 'conc_*' (Conc Attenuation) / 'ps_*'|'pond_*'|
      // 'swale_*'|'hw_*' (Ponds/Swales) / 'wh_*' (Weholite Attenuation) — split
      // them by prefix so each lands in its own top-level state field's .table.
      const importedBuildUp = result.data.buildUp || {};
      const importedBoxCulvertRows: Record<string, FoulBuildUpRowData> = {};
      const importedCellularAttenuationRows: Record<string, FoulBuildUpRowData> = {};
      const importedConcAttenuationRows: Record<string, FoulBuildUpRowData> = {};
      const importedPondsSwalesRows: Record<string, FoulBuildUpRowData> = {};
      const importedWeholiteAttenuationRows: Record<string, FoulBuildUpRowData> = {};
      Object.entries(importedBuildUp).forEach(([id, row]) => {
        if (id.startsWith('bc_')) importedBoxCulvertRows[id] = row;
        else if (id.startsWith('ca_')) importedCellularAttenuationRows[id] = row;
        else if (id.startsWith('conc_')) importedConcAttenuationRows[id] = row;
        else if (id.startsWith('ps_') || id.startsWith('pond_') || id.startsWith('swale_') || id.startsWith('hw_')) importedPondsSwalesRows[id] = row;
        else if (id.startsWith('wh_')) importedWeholiteAttenuationRows[id] = row;
      });

      const updatedStormSewers = {
        ...stormSewersData,
        manholes: {
          ...stormSewersData.manholes,
          table: result.data.manholes,
          config: {
            ...stormSewersData.manholes.config,
            ...(result.data.manholeScheduleConfig ? { manholeScheduleConfig: result.data.manholeScheduleConfig } : {}),
          },
        },
        pipeRuns: {
          ...stormSewersData.pipeRuns,
          table: padStormPipeRuns(result.data.pipeRuns, importedRowCount, importRoadConstDepth, importSeed),
          config: {
            ...stormSewersData.pipeRuns.config,
            ...(result.data.roadConstConfig ? { roadConstConfig: result.data.roadConstConfig } : {}),
            ...(result.data.eoQtys ? { eoQtys: { ...stormSewersData.pipeRuns.config.eoQtys, ...result.data.eoQtys } } : {}),
          },
        },
        lateralConnections: {
          ...stormSewersData.lateralConnections,
          table: padStormLaterals(result.data.laterals, importedRowCount, importSeed + importedRowCount),
        },
        gullyConnections: {
          ...stormSewersData.gullyConnections,
          table: padGullies(result.data.gullies, importedRowCount, importSeed + importedRowCount * 2),
        },
      };

      updateField("stormSewers", updatedStormSewers);
      // Grow Box Culverts' sectionCount to cover the highest culvert n
      // present in the import, same reasoning as Cellular Attenuation above.
      const importedBoxCulvertMaxN = Object.keys(importedBoxCulvertRows).reduce((max, id) => {
        const m = id.match(/^bc_(\d+)_/);
        return m ? Math.max(max, Number(m[1])) : max;
      }, 0);
      const importedBoxCulvertSectionCount = Math.min(Math.max(importedBoxCulvertMaxN, DEFAULT_BOX_CULVERT_SECTIONS), MAX_BOX_CULVERT_SECTIONS);
      updateField("boxCulverts", {
        ...state.boxCulverts,
        table: importedBoxCulvertRows,
        config: {
          ...state.boxCulverts.config,
          sectionCount: importedBoxCulvertSectionCount,
          // Merge rather than replace — a culvert this import doesn't touch
          // (sheet only has fewer culverts, or its depth cell was blank) keeps
          // whatever height was already configured for it.
          culvertConfigMm: {
            ...(state.boxCulverts.config?.culvertConfigMm ?? {}),
            ...(result.data.boxCulvertsConfig?.culvertConfigMm ?? {}),
          },
          culvertBackfillType: {
            ...(state.boxCulverts.config?.culvertBackfillType ?? {}),
            ...(result.data.boxCulvertsConfig?.culvertBackfillType ?? {}),
          },
        },
      });
      // Grow Cellular Attenuation's sectionCount to cover the highest tank n
      // present in the import, same reasoning as pondSectionCount below.
      const importedCellularMaxN = Object.keys(importedCellularAttenuationRows).reduce((max, id) => {
        const m = id.match(/^ca_(\d+)_/);
        return m ? Math.max(max, Number(m[1])) : max;
      }, 0);
      const importedCellularSectionCount = Math.min(Math.max(importedCellularMaxN, DEFAULT_CELLULAR_SECTIONS), MAX_CELLULAR_SECTIONS);
      updateField("cellularAttenuation", {
        ...state.cellularAttenuation,
        table: importedCellularAttenuationRows,
        config: {
          ...state.cellularAttenuation.config,
          sectionCount: importedCellularSectionCount,
          crateDims: { ...(state.cellularAttenuation.config?.crateDims ?? {}), ...(result.data.cellularAttenuationConfig?.crateDims ?? {}) },
        },
      });
      // Grow Conc Attenuation's sectionCount to cover the highest tank n
      // present in the import, same reasoning as Cellular Attenuation above.
      const importedConcMaxN = Object.keys(importedConcAttenuationRows).reduce((max, id) => {
        const m = id.match(/^conc_(\d+)_/);
        return m ? Math.max(max, Number(m[1])) : max;
      }, 0);
      const importedConcSectionCount = Math.min(Math.max(importedConcMaxN, DEFAULT_CONC_SECTIONS), MAX_CONC_SECTIONS);
      updateField("concAttenuation", {
        ...state.concAttenuation,
        table: importedConcAttenuationRows,
        config: {
          ...state.concAttenuation.config,
          sectionCount: importedConcSectionCount,
          // Merge rather than replace — a tank this import doesn't touch
          // keeps whatever FP/OHP % was already configured for it.
          concConfigPct: {
            ...(state.concAttenuation.config?.concConfigPct ?? {}),
            ...(result.data.concAttenuationConfig?.concConfigPct ?? {}),
          },
          drawingRefs: { ...(state.concAttenuation.config?.drawingRefs ?? {}), ...(result.data.concAttenuationConfig?.drawingRefs ?? {}) },
        },
      });
      // Grow pondSectionCount to cover the highest POND n present in the
      // import so any pond beyond the default section renders instead of
      // sitting hidden in .table with no visible panel.
      const importedPondMaxN = Object.keys(importedPondsSwalesRows).reduce((max, id) => {
        const m = id.match(/^pond_(\d+)_/);
        return m ? Math.max(max, Number(m[1])) : max;
      }, 0);
      const importedPondSectionCount = Math.min(Math.max(importedPondMaxN, DEFAULT_POND_SECTIONS), MAX_POND_SECTIONS);
      // Same growth logic for Swale sections.
      const importedSwaleMaxN = Object.keys(importedPondsSwalesRows).reduce((max, id) => {
        const m = id.match(/^swale_(\d+)_/);
        return m ? Math.max(max, Number(m[1])) : max;
      }, 0);
      const importedSwaleSectionCount = Math.min(Math.max(importedSwaleMaxN, DEFAULT_SWALE_SECTIONS), MAX_SWALE_SECTIONS);
      updateField("pondsSwales", {
        ...state.pondsSwales,
        table: importedPondsSwalesRows,
        config: {
          pondSectionCount: importedPondSectionCount,
          pondTopsoilThicknessMm: result.data.pondsSwalesConfig?.pondTopsoilThicknessMm ?? {},
          swaleSectionCount: importedSwaleSectionCount,
          swaleTopsoilThicknessMm: result.data.pondsSwalesConfig?.swaleTopsoilThicknessMm ?? {},
          sectionTitles: result.data.pondsSwalesConfig?.sectionTitles ?? {},
        },
      });
      // Grow Weholite Attenuation's sectionCount to cover the highest tank n
      // present in the import, same reasoning as Cellular Attenuation above.
      const importedWeholiteMaxN = Object.keys(importedWeholiteAttenuationRows).reduce((max, id) => {
        const m = id.match(/^wh_(\d+)_/);
        return m ? Math.max(max, Number(m[1])) : max;
      }, 0);
      const importedWeholiteSectionCount = Math.min(Math.max(importedWeholiteMaxN, DEFAULT_WEHOLITE_SECTIONS), MAX_WEHOLITE_SECTIONS);
      updateField("weholiteAttenuation", {
        ...state.weholiteAttenuation,
        table: importedWeholiteAttenuationRows,
        config: {
          ...state.weholiteAttenuation.config,
          sectionCount: importedWeholiteSectionCount,
          // Merge rather than replace — a tank this import doesn't touch
          // keeps whatever FP/OHP % was already configured for it.
          weholiteConfigPct: {
            ...(state.weholiteAttenuation.config?.weholiteConfigPct ?? {}),
            ...(result.data.weholiteAttenuationConfig?.weholiteConfigPct ?? {}),
          },
        },
      });
      setImportModalVisible(false);
    } catch (error) {
      console.error("Import failed:", error);
    } finally {
      setImportLoading(false);
    }
  };

  const updateManhole = (id: string, field: keyof Manhole, value: unknown) => {
    const ladderCfg = stormSewersData.manholes.config.manholeScheduleConfig?.ladders ?? 'Galvanised';
    const safetyCfg = stormSewersData.manholes.config.manholeScheduleConfig?.safetyChains ?? 'Galvanised';
    const oldRef = (stormSewersData.manholes.table || []).find((mh) => mh.id === id)?.ref;
    const isRefRename = field === 'ref' && oldRef && value !== oldRef;

    const updatedManholes = (stormSewersData.manholes.table || []).map((mh) => {
      if (mh.id !== id) return mh;
      return calcStormManholeDerived({ ...mh, [field]: value }, ladderCfg, safetyCfg);
    });

    const cfg = stormSewersData.pipeRuns.config.roadConstConfig;
    const roadConstDepth = getEffectiveRoadConstDepth(cfg);
    const allGullies = stormSewersData.gullyConnections.table || [];

    // Renaming a manhole's ref invalidates any Manhole From/To or lateral
    // "Manhole Ref" selection pointing at the old ref — clear those instead of
    // leaving them showing a ref that no longer exists on any manhole.
    const updatedPipeRuns = (stormSewersData.pipeRuns.table || []).map((run, i) => {
      const patchedRun = {
        ...run,
        roadConstDepth,
        ...(isRefRename ? {
          fromMH: run.fromMH === oldRef ? '' : run.fromMH,
          toMH: run.toMH === oldRef ? '' : run.toMH,
        } : {}),
      };
      return calcStormPipeRunDerived(patchedRun, updatedManholes, allGullies[i]?.pipeDiameter);
    });

    const updatedLaterals = (stormSewersData.lateralConnections.table || []).map((lat, i) => {
      const patchedLat = isRefRename && lat.toManhole === oldRef ? { ...lat, toManhole: '' } : lat;
      return calcStormLateralDerived(patchedLat, updatedManholes, updatedPipeRuns[i]?.runDepthCategory ?? 0);
    });

    const updatedGullies = allGullies.map((gully, i) =>
      calcStormGullyDerived(
        gully,
        updatedPipeRuns[i]?.runDepthCategory ?? 0,
        updatedPipeRuns[i]?.junctions ?? 0,
        updatedLaterals[i]?.junctionOffRun ?? 0,
        updatedPipeRuns[i]?.fromMH,
        updatedPipeRuns[i]?.toMH,
      )
    );

    updateField('stormSewers', {
      ...stormSewersData,
      manholes: { ...stormSewersData.manholes, table: updatedManholes },
      pipeRuns: { ...stormSewersData.pipeRuns, table: updatedPipeRuns },
      lateralConnections: { ...stormSewersData.lateralConnections, table: updatedLaterals },
      gullyConnections: { ...stormSewersData.gullyConnections, table: updatedGullies },
    });
  };

  const updatePipeRun = (id: string, field: keyof StormSewerRun, value: unknown) => {
    const allPipeRuns = stormSewersData.pipeRuns.table || [];
    const allLaterals = stormSewersData.lateralConnections.table || [];
    const allGullies = stormSewersData.gullyConnections.table || [];
    const manholes = stormSewersData.manholes.table || [];
    const changedIdx = allPipeRuns.findIndex((r) => r.id === id);

    const updatedPipeRuns = allPipeRuns.map((run, i) => {
      if (run.id !== id) return run;
      const patched = { ...run, [field]: value };
      return calcStormPipeRunDerived(patched, manholes, allGullies[i]?.pipeDiameter);
    });

    const updatedLaterals = allLaterals.map((lat, i) => {
      if (i !== changedIdx) return lat;
      return calcStormLateralDerived(lat, manholes, updatedPipeRuns[i]?.runDepthCategory ?? 0);
    });

    const updatedGullies = allGullies.map((gully, i) => {
      if (i !== changedIdx) return gully;
      return calcStormGullyDerived(
        gully,
        updatedPipeRuns[i]?.runDepthCategory ?? 0,
        updatedPipeRuns[i]?.junctions ?? 0,
        updatedLaterals[i]?.junctionOffRun ?? 0,
        updatedPipeRuns[i]?.fromMH,
        updatedPipeRuns[i]?.toMH,
      );
    });

    updateField('stormSewers', {
      ...stormSewersData,
      pipeRuns: { ...stormSewersData.pipeRuns, table: updatedPipeRuns },
      lateralConnections: { ...stormSewersData.lateralConnections, table: updatedLaterals },
      gullyConnections: { ...stormSewersData.gullyConnections, table: updatedGullies },
    });
  };

  const deleteManhole = (id: string) => {
    const allManholes = stormSewersData.manholes.table || [];
    const updatedManholes = allManholes.filter((mh) => mh.id !== id);

    // Remove pipe runs that reference this manhole
    const deletedMH = allManholes.find((mh) => mh.id === id);
    const updatedStormPipeRuns = stormSewersData.pipeRuns.table.filter((run) => {
      return deletedMH
        ? run.fromMH !== deletedMH.ref && run.toMH !== deletedMH.ref
        : true;
    });

    const updatedFoulPipeRuns = (state.foulSewers?.pipeRuns.table || []).filter(
      (run) => {
        return deletedMH
          ? run.fromMH !== deletedMH.ref && run.toMH !== deletedMH.ref
          : true;
      }
    );

    updateField("stormSewers", {
      ...stormSewersData,
      manholes: { ...stormSewersData.manholes, table: updatedManholes },
      pipeRuns: { ...stormSewersData.pipeRuns, table: updatedStormPipeRuns },
    });

    updateField("foulSewers", {
      ...state.foulSewers,
      pipeRuns: { ...state.foulSewers.pipeRuns, table: updatedFoulPipeRuns },
    });

    // Force immediate save to ensure deletion persists
    setTimeout(() => {
      saveTender();
      console.log("Storm manhole deletion saved to storage");
    }, 100);
  };

  const deleteManholes = (ids: React.Key[]) => {
    const idSet = new Set(ids);
    const allManholes = stormSewersData.manholes.table || [];
    const deletedRefs = new Set(allManholes.filter((mh) => idSet.has(mh.id)).map((mh) => mh.ref));
    const updatedManholes = allManholes.filter((mh) => !idSet.has(mh.id));
    const updatedStormPipeRuns = stormSewersData.pipeRuns.table.filter(
      (run) => !deletedRefs.has(run.fromMH) && !deletedRefs.has(run.toMH)
    );
    const updatedFoulPipeRuns = (state.foulSewers?.pipeRuns.table || []).filter(
      (run) => !deletedRefs.has(run.fromMH) && !deletedRefs.has(run.toMH)
    );

    updateField("stormSewers", {
      ...stormSewersData,
      manholes: { ...stormSewersData.manholes, table: updatedManholes },
      pipeRuns: { ...stormSewersData.pipeRuns, table: updatedStormPipeRuns },
    });
    updateField("foulSewers", {
      ...state.foulSewers,
      pipeRuns: { ...state.foulSewers.pipeRuns, table: updatedFoulPipeRuns },
    });

    setTimeout(() => {
      saveTender();
    }, 100);
  };

  const handleCellEdit = (
    table: string,
    id: string,
    field: string,
    value: any
  ) => {
    if (table === "manholes") {
      updateManhole(id, field as keyof Manhole, value);
    } else if (table === "pipeRuns") {
      updatePipeRun(id, field as keyof StormSewerRun, value);
    }
  };

  const handleCellEditComplete = () => {
    setEditingCell(null);
  };

  const updateLateral = (id: string, field: keyof LateralConnection, value: unknown) => {
    const allLaterals = stormSewersData.lateralConnections.table || [];
    const allPipeRuns = stormSewersData.pipeRuns.table || [];
    const allGullies = stormSewersData.gullyConnections.table || [];
    const manholes = stormSewersData.manholes.table || [];
    const changedIdx = allLaterals.findIndex((l) => l.id === id);

    const updatedLaterals = allLaterals.map((lat, i) => {
      if (lat.id !== id) return lat;
      const patched = { ...lat, [field]: value };
      return calcStormLateralDerived(patched, manholes, allPipeRuns[i]?.runDepthCategory ?? 0);
    });

    const updatedGullies = allGullies.map((gully, i) => {
      if (i !== changedIdx) return gully;
      return calcStormGullyDerived(
        gully,
        allPipeRuns[i]?.runDepthCategory ?? 0,
        allPipeRuns[i]?.junctions ?? 0,
        updatedLaterals[i]?.junctionOffRun ?? 0,
        allPipeRuns[i]?.fromMH,
        allPipeRuns[i]?.toMH,
      );
    });

    updateField('stormSewers', {
      ...stormSewersData,
      lateralConnections: { ...stormSewersData.lateralConnections, table: updatedLaterals },
      gullyConnections: { ...stormSewersData.gullyConnections, table: updatedGullies },
    });
  };

  const updateGullyConnection = (id: string, field: keyof GullyConnection, value: unknown) => {
    const allGullies = stormSewersData.gullyConnections.table || [];
    const allPipeRuns = stormSewersData.pipeRuns.table || [];
    const allLaterals = stormSewersData.lateralConnections.table || [];
    const changedIdx = allGullies.findIndex((g) => g.id === id);

    const updatedGullies = allGullies.map((gully, i) => {
      if (gully.id !== id) return gully;
      const patched = { ...gully, [field]: value };
      return calcStormGullyDerived(
        patched,
        allPipeRuns[i]?.runDepthCategory ?? 0,
        allPipeRuns[i]?.junctions ?? 0,
        allLaterals[i]?.junctionOffRun ?? 0,
        allPipeRuns[i]?.fromMH,
        allPipeRuns[i]?.toMH,
      );
    });

    // recalc junctionSize when gully pipe diameter changes
    const updatedPipeRuns = allPipeRuns.map((run, i) => {
      if (i !== changedIdx) return run;
      return calcStormPipeRunDerived(run, stormSewersData.manholes.table || [], updatedGullies[i]?.pipeDiameter);
    });

    updateField('stormSewers', {
      ...stormSewersData,
      pipeRuns: { ...stormSewersData.pipeRuns, table: updatedPipeRuns },
      gullyConnections: { ...stormSewersData.gullyConnections, table: updatedGullies },
    });
  };

  const isEditing = (table: string, id: string, field: string) => {
    return (
      editingCell?.table === table &&
      editingCell?.id === id &&
      editingCell?.field === field
    );
  };

  // Calculate summaries
  const pipeDiameterSummaries = calculatePipeDiameterSummaries(
    stormSewersData.pipeRuns.table
  );
  const totalLength = calculateTotalStormLength(stormSewersData.pipeRuns.table);
  const gullyConnectionLength = calculateGullyConnectionLength(
    stormSewersData.pipeRuns.config.roadGulliesCount,
    stormSewersData.gullyConnections.config.gullyConnectionLength
  );

  // Calculate detailed summaries
  const pipeRunsSummary = calculateStormSewersSummary(
    stormSewersData.pipeRuns.table,
    availableManholes
  );
  const manholesSummary = calculateManholesSummary(availableManholes);
  const manholeScheduleSummary =
    calculateManholeScheduleSummary(availableManholes);
  const pipeRunsScheduleSummary = calculatePipeRunsScheduleSummary(
    stormSewersData.pipeRuns.table
  );

  const colHeader = (title: string, unit?: string): React.ReactNode => (
    <div className="text-center">
      <Tooltip title={title}>
        <div className="gw-header-title">{title}</div>
      </Tooltip>
      {unit && <div className="gw-header-unit text-xs text-gray-500">{unit}</div>}
    </div>
  );

  const colHeaderCalc = (
    title: string,
    unit: string | undefined,
    info: { formula: string; excelRef?: string; rows: { ref: string; value: number | string; unit: string }[] },
  ): React.ReactNode => (
    <div className="text-center">
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        <div className="gw-header-title">{title}</div>
        {showDevMode && (
          <InfoCircleOutlined
            style={{ color: '#1677ff', cursor: 'pointer', flexShrink: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              setColCalcInfo({ title, formula: info.formula, rows: info.rows, unit: unit ?? '' });
            }}
          />
        )}
      </div>
      {unit && <div className="gw-header-unit text-xs text-gray-500">{unit}</div>}
    </div>
  );

  const manholeScheduleContent = (
    <>
      <Table<Manhole>
        className="storm-sewers-table"
        bordered
        size="small"
        scroll={{ x: 1625 }}
        sticky={{ offsetHeader: stickyOffset, offsetScroll: 9999 }}
        pagination={false}
        rowSelection={
          state.isViewMode
            ? undefined
            : {
                selectedRowKeys: selectedManholeKeys,
                onChange: setSelectedManholeKeys,
                fixed: true,
              }
        }
        columns={[
          {
            title: colHeader('Manhole Ref'),
            dataIndex: 'ref',
            key: 'ref',
            width: 100,
            fixed: 'left' as const,
            render: (_, record) => (
              <Input
                size="small"
                value={record.ref}
                onChange={(e) => handleCellEdit('manholes', record.id, 'ref', e.target.value)}
              />
            ),
          },
          {
            title: colHeader('Type'),
            dataIndex: 'type',
            key: 'type',
            width: 90,
            render: (_, record) => (
              <Input
                size="small"
                value={record.type}
                onChange={(e) => handleCellEdit('manholes', record.id, 'type', e.target.value)}
              />
            ),
          },
          {
            title: colHeaderCalc('Easi / Trad', undefined, {
              formula: 'IF(Diam="","", IF(Diam ∈ {1200,1500,1800}, "Easi", "Trad"))',
              excelRef: 'D9',
              rows: availableManholes.map((mh) => ({
                ref: mh.ref || mh.id,
                value: mh.easiTrad === 'easi' ? 'Easi' : mh.easiTrad === 'trad' ? 'Trad' : '',
                unit: '',
              })),
            }),
            dataIndex: 'easiTrad',
            key: 'easiTrad',
            width: 130,
            align: 'center' as const,
            render: (_, record) => (
              <div style={{ padding: '4px 8px', textAlign: 'center', background: '#f5f5f5', borderRadius: 4, border: '1px solid #e5e7eb', color: '#374151', fontSize: 13 }}>
                {record.easiTrad === 'easi' ? 'Easi' : record.easiTrad === 'trad' ? 'Trad' : <span style={{ color: '#d1d5db' }}>—</span>}
              </div>
            ),
          },
          {
            title: colHeader('Diam', 'mm'),
            dataIndex: 'diameter',
            key: 'diameter',
            width: 100,
            render: (_, record) => (
              <NumberInput
                size="small"
                value={parseInt((record.diameter ?? '').replace(/mm$/i, ''), 10) || 0}
                onChange={(v) => updateManhole(record.id, 'diameter', v > 0 ? `${v}mm` : '')}
                isInteger
                decimalPlaces={0}
                positiveOnly
                style={{ width: '100%', textAlign: 'right' }}
              />
            ),
          },
          {
            title: colHeader('Cover Level', 'm'),
            dataIndex: 'coverLevel',
            key: 'coverLevel',
            width: 110,
            render: (_, record) => (
              <NumberInput
                size="small"
                value={record.coverLevel}
                onChange={(value) => updateManhole(record.id, 'coverLevel', value)}
                decimalPlaces={3}
                positiveOnly
                style={{ width: '100%', textAlign: 'right' }}
              />
            ),
          },
          {
            title: colHeader('Invert Level', 'm'),
            dataIndex: 'invertLevel',
            key: 'invertLevel',
            width: 120,
            render: (_, record) => (
              <NumberInput
                size="small"
                value={record.invertLevel}
                onChange={(value) => updateManhole(record.id, 'invertLevel', value)}
                decimalPlaces={3}
                positiveOnly
                style={{ width: '100%', textAlign: 'right' }}
              />
            ),
          },
          {
            title: colHeader('Sump', 'mm'),
            dataIndex: 'sump',
            key: 'sump',
            width: 80,
            render: (_, record) => (
              <NumberInput
                size="small"
                value={record.sump ?? 0}
                onChange={(value) => updateManhole(record.id, 'sump', value)}
                decimalPlaces={3}
                positiveOnly
                style={{ width: '100%', textAlign: 'right' }}
              />
            ),
          },
          {
            title: colHeaderCalc('Depth To Invert', 'm', {
              formula: 'Cover Level − Invert Level + Sump',
              rows: availableManholes.map((mh) => ({ ref: mh.ref || mh.id, value: mh.depth ?? 0, unit: 'm' })),
            }),
            dataIndex: 'depth',
            key: 'depth',
            width: 160,
            align: 'center' as const,
            render: (_, record) => (
              <div style={calcCellStyle}>{(record.depth ?? 0).toFixed(3)}</div>
            ),
          },
          {
            title: colHeaderCalc('Depth Category', 'm', {
              formula: 'ROUND(Depth To Invert / 0.25) × 0.25  →  nearest 0.25m band',
              rows: availableManholes.map((mh) => ({ ref: mh.ref || mh.id, value: mh.depthCategory ?? 0, unit: 'm' })),
            }),
            dataIndex: 'depthCategory',
            key: 'depthCategory',
            width: 150,
            align: 'center' as const,
            render: (_, record) => (
              <div style={calcCellStyle}>{(record.depthCategory ?? 0).toFixed(3)}</div>
            ),
          },
          {
            title: colHeaderCalc('Ladders', 'm', {
              formula: 'N/A in configure → blank  |  Depth Category ≤ 2.25 → blank  |  else: Invert Level − Sump − 0.15',
              rows: availableManholes.map((mh) => ({ ref: mh.ref || mh.id, value: mh.ladders ?? 0, unit: 'm' })),
            }),
            dataIndex: 'ladders',
            key: 'ladders',
            width: 100,
            align: 'center' as const,
            render: (_, record) => {
              const v = record.ladders ?? 0;
              return <div style={calcCellStyle}>{v > 0 ? v.toFixed(3) : '—'}</div>;
            },
          },
          {
            title: colHeaderCalc('Safety Chains', 'Nr', {
              formula: 'N/A in configure → blank  |  Depth Category ≤ 2.25 → blank  |  else: Invert Level − Sump − 0.15',
              rows: availableManholes.map((mh) => ({ ref: mh.ref || mh.id, value: mh.safetyChains ?? 0, unit: 'Nr' })),
            }),
            dataIndex: 'safetyChains',
            key: 'safetyChains',
            width: 145,
            align: 'center' as const,
            render: (_, record) => {
              const v = record.safetyChains ?? 0;
              return <div style={calcCellStyle}>{v > 0 ? v.toFixed(3) : '—'}</div>;
            },
          },
          {
            title: colHeader('Backdrop', 'Nr'),
            dataIndex: 'backdrop',
            key: 'backdrop',
            width: 90,
            render: (_, record) => (
              <NumberInput
                size="small"
                value={record.backdrop ?? 0}
                onChange={(value) => updateManhole(record.id, 'backdrop', value)}
                isInteger
                decimalPlaces={0}
                positiveOnly
                style={{ width: '100%', textAlign: 'right' }}
              />
            ),
          },
          {
            title: colHeader('Muck stopper', 'Nr'),
            dataIndex: 'muckStopper',
            key: 'muckStopper',
            width: 120,
            render: (_, record) => (
              <NumberInput
                size="small"
                value={record.muckStopper ?? 0}
                onChange={(value) => updateManhole(record.id, 'muckStopper', value)}
                isInteger
                decimalPlaces={0}
                positiveOnly
                style={{ width: '100%', textAlign: 'right' }}
              />
            ),
          },
          {
            title: colHeader('AMPS', 'Nr'),
            dataIndex: 'amps',
            key: 'amps',
            width: 80,
            render: (_, record) => (
              <NumberInput
                size="small"
                value={record.amps ?? 0}
                onChange={(value) => updateManhole(record.id, 'amps', value)}
                isInteger
                decimalPlaces={0}
                positiveOnly
                style={{ width: '100%', textAlign: 'right' }}
              />
            ),
          },
          {
            title: 'Actions',
            key: 'actions',
            width: 80,
            fixed: 'right' as const,
            align: 'center' as const,
            render: (_, record) => (
              <Button
                size="small"
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => requestDelete(() => deleteManhole(record.id))}
              />
            ),
          },
        ]}
        dataSource={availableManholes}
        rowKey="id"
      />

      {/* Add Manhole Button */}
      <div style={{ marginTop: '16px' }}>
        <Button
          type="text"
          icon={<PlusOutlined style={{ color: COLORS.primary }} />}
          onClick={addManhole}
          style={{
            color: COLORS.primary,
            fontWeight: 500,
            padding: '4px 8px'
          }}
        >
          Add New Row
        </Button>
      </div>
    </>
  );

  // ── Combined Storm Take-off table (Main Sewer Runs + House Laterals + Gully Laterals) ─
  // Each row pairs pipeRuns[i], lateralConnections[i], and gullyConnections[i] – positional pairing.

  interface StormTakeOffRow {
    id: string;
    index: number;
    pipeRun: StormSewerRun | null;
    lateral: LateralConnection | null;
    gully: GullyConnection | null;
  }

  const _stormPipeRuns = stormSewersData.pipeRuns.table || [];
  const _stormLaterals = stormSewersData.lateralConnections.table || [];
  const _stormGullies = stormSewersData.gullyConnections.table || [];
  const _stormNumRows = Math.max(_stormPipeRuns.length, _stormLaterals.length, _stormGullies.length);

  const combinedStormTakeOffData: StormTakeOffRow[] = Array.from({ length: _stormNumRows }, (_, i) => ({
    id: `st-row-${i}`,
    index: i,
    pipeRun: _stormPipeRuns[i] ?? null,
    lateral: _stormLaterals[i] ?? null,
    gully: _stormGullies[i] ?? null,
  }));

  const addCombinedStormRow = () => {
    const cfg = stormSewersData.pipeRuns.config.roadConstConfig;
    const roadConstDepth = getEffectiveRoadConstDepth(cfg);
    const now = Date.now();

    // Pad all three lists up to the longest one's length first, so the row
    // appended below always lands at the same index in every array and
    // renders with editable inputs (see padStormPipeRuns/padStormLaterals/padGullies above).
    const runsInSync = padStormPipeRuns(_stormPipeRuns, _stormNumRows, roadConstDepth, now);
    const lateralsInSync = padStormLaterals(_stormLaterals, _stormNumRows, now + _stormNumRows);
    const gulliesInSync = padGullies(_stormGullies, _stormNumRows, now + _stormNumRows * 2);

    const newRun: StormSewerRun = makeBlankStormPipeRun(roadConstDepth, now + runsInSync.length);
    const newLateral: LateralConnection = makeBlankStormLateral(now + lateralsInSync.length + 1);
    const newGully: GullyConnection = makeBlankGully(now + gulliesInSync.length + 2);

    updateField('stormSewers', {
      ...stormSewersData,
      pipeRuns: { ...stormSewersData.pipeRuns, table: [...runsInSync, newRun] },
      lateralConnections: { ...stormSewersData.lateralConnections, table: [...lateralsInSync, newLateral] },
      gullyConnections: { ...stormSewersData.gullyConnections, table: [...gulliesInSync, newGully] },
    });
  };

  const deleteCombinedStormRow = (index: number) => {
    updateField('stormSewers', {
      ...stormSewersData,
      pipeRuns: { ...stormSewersData.pipeRuns, table: _stormPipeRuns.filter((_, i) => i !== index) },
      lateralConnections: { ...stormSewersData.lateralConnections, table: _stormLaterals.filter((_, i) => i !== index) },
      gullyConnections: { ...stormSewersData.gullyConnections, table: _stormGullies.filter((_, i) => i !== index) },
    });
  };

  const deleteCombinedStormRows = (indices: number[]) => {
    const idxSet = new Set(indices);
    updateField('stormSewers', {
      ...stormSewersData,
      pipeRuns: { ...stormSewersData.pipeRuns, table: _stormPipeRuns.filter((_, i) => !idxSet.has(i)) },
      lateralConnections: { ...stormSewersData.lateralConnections, table: _stormLaterals.filter((_, i) => !idxSet.has(i)) },
      gullyConnections: { ...stormSewersData.gullyConnections, table: _stormGullies.filter((_, i) => !idxSet.has(i)) },
    });
  };

  // NOTE: `Manhole.type` is a free-text field (whatever the real Excel "Type"
  // column represents, e.g. construction type) — it is NOT a controlled
  // 'foul'/'storm'/'both' enum, so filtering by it here excluded every
  // imported manhole from the lateral/gully "Manhole Ref" dropdowns. Foul/Storm
  // manholes already live in separate arrays (foulSewersData.manholes vs
  // stormSewersData.manholes), so no further filtering by type is needed.
  const stormManholes = availableManholes;

  const combinedStormTakeOffColumns = [
    // ── Main Sewer Runs group ─────────────────────────────────────────────────
    {
      title: (
        <div style={{ textAlign: 'center', fontWeight: 700, color: '#1e40af', background: '#dbeafe', padding: '4px 8px', borderRadius: '4px' }}>
          Main Sewer Runs
        </div>
      ),
      key: 'stormMainSewerGroup',
      children: [
        {
          title: colHeader('Run Ref'),
          dataIndex: 'id',
          key: 'stRunRef',
          width: 90,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            const run = record.pipeRun;
            return (
              <Input size="small" value={run.ref || ''} onChange={(e) => handleCellEdit('pipeRuns', run.id, 'ref', e.target.value)} />
            );
          },
        },
        {
          title: colHeader('Manhole From'),
          dataIndex: 'id',
          key: 'stFromMH',
          width: 120,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            const run = record.pipeRun;
            return (
              <Select size="small" value={run.fromMH} onChange={(v) => updatePipeRun(run.id, 'fromMH', v)} placeholder="—" style={{ width: '100%' }} allowClear>
                {availableManholes.filter(mh => mh.ref).map(mh => <Select.Option key={mh.id} value={mh.ref}>{mh.ref}</Select.Option>)}
              </Select>
            );
          },
        },
        {
          title: colHeader('Manhole To'),
          dataIndex: 'id',
          key: 'stToMH',
          width: 110,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            const run = record.pipeRun;
            return (
              <Select size="small" value={run.toMH} onChange={(v) => updatePipeRun(run.id, 'toMH', v)} placeholder="—" style={{ width: '100%' }} allowClear>
                {availableManholes.filter(mh => mh.ref).map(mh => <Select.Option key={mh.id} value={mh.ref}>{mh.ref}</Select.Option>)}
              </Select>
            );
          },
        },
        {
          title: colHeaderCalc('Depth From', 'm', {
            formula: 'Lookup: From Manhole Ref → Manhole Schedule: Depth (cover level − invert level)',
            rows: _stormPipeRuns.map((r) => ({ ref: r.fromMH || r.id, value: r.depthFrom ?? 0, unit: 'm' })),
          }),
          dataIndex: 'id',
          key: 'stDepthFrom',
          width: 120,
          align: 'center' as const,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{(record.pipeRun.depthFrom ?? 0).toFixed(3)}</div>;
          },
        },
        {
          title: colHeaderCalc('Depth To', 'm', {
            formula: 'Lookup: To Manhole Ref → Manhole Schedule: Depth (cover level − invert level)',
            rows: _stormPipeRuns.map((r) => ({ ref: r.toMH || r.id, value: r.depthTo ?? 0, unit: 'm' })),
          }),
          dataIndex: 'id',
          key: 'stDepthTo',
          width: 105,
          align: 'center' as const,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{(record.pipeRun.depthTo ?? 0).toFixed(3)}</div>;
          },
        },
        {
          title: colHeader('Road Const Depth', 'm'),
          dataIndex: 'id',
          key: 'stRoadConstDepth',
          width: 150,
          align: 'center' as const,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{(record.pipeRun.roadConstDepth ?? 0).toFixed(3)}</div>;
          },
        },
        {
          title: colHeaderCalc('Avg Depth', 'm', {
            formula: '(Depth From + Depth To) / 2 − Road Construction Depth',
            rows: _stormPipeRuns.map((r) => ({ ref: r.fromMH && r.toMH ? `${r.fromMH}–${r.toMH}` : r.id, value: r.averageDepth ?? 0, unit: 'm' })),
          }),
          dataIndex: 'id',
          key: 'stAvgDepth',
          width: 110,
          align: 'center' as const,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{(record.pipeRun.averageDepth ?? 0).toFixed(3)}</div>;
          },
        },
        {
          title: colHeaderCalc('Avg Run Depth Category', 'm', {
            formula: 'ROUND(Avg Depth / 0.25) × 0.25  →  nearest 0.25m band',
            rows: _stormPipeRuns.map((r) => ({ ref: r.fromMH && r.toMH ? `${r.fromMH}–${r.toMH}` : r.id, value: r.runDepthCategory ?? 0, unit: 'm' })),
          }),
          dataIndex: 'id',
          key: 'stRunDepthCat',
          width: 185,
          align: 'center' as const,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{(record.pipeRun.runDepthCategory ?? 0).toFixed(3)}</div>;
          },
        },
        {
          title: colHeader('Clay/Conc/Ultra/Rigi'),
          dataIndex: 'id',
          key: 'stRunPipeType',
          width: 185,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            const run = record.pipeRun;
            return (
              <Select size="small" value={run.pipeType || undefined} onChange={(v) => updatePipeRun(run.id, 'pipeType', v)} placeholder="—" style={{ width: '100%' }}>
                <Select.Option value="clay">Clay</Select.Option>
                <Select.Option value="concrete">Concrete</Select.Option>
                <Select.Option value="ultra">Ultra</Select.Option>
                <Select.Option value="rigi">Rigi</Select.Option>
              </Select>
            );
          },
        },
        {
          title: colHeader('Pipe Ø', 'mm'),
          dataIndex: 'id',
          key: 'stRunDiameter',
          width: 100,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            const run = record.pipeRun;
            return (
              <NumberInput
                size="small"
                value={parseInt((run.diameter ?? '').replace(/mm$/i, ''), 10) || 0}
                onChange={(v) => updatePipeRun(run.id, 'diameter', v > 0 ? `${v}mm` : '')}
                isInteger
                decimalPlaces={0}
                positiveOnly
                style={{ width: '100%', textAlign: 'right' }}
              />
            );
          },
        },
        {
          title: colHeader('Run Length', 'm'),
          dataIndex: 'id',
          key: 'stRunLength',
          width: 105,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            const run = record.pipeRun;
            return <NumberInput size="small" value={run.length} onChange={(v) => updatePipeRun(run.id, 'length', v)} isInteger decimalPlaces={0} positiveOnly style={{ width: '100%', textAlign: 'right' }} />;
          },
        },
        {
          title: colHeader('Backfill','m'),
          dataIndex: 'id',
          key: 'stBackfill',
          width: 100,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            const run = record.pipeRun;
            return (
              <Select size="small" value={migrateBackfillType(run.backfillType) || undefined} onChange={(v) => updatePipeRun(run.id, 'backfillType', v)} placeholder="—" style={{ width: '100%' }}>
                {BACKFILL_TYPE_OPTIONS.map((opt) => (
                  <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                ))}
              </Select>
            );
          },
        },
        {
          title: colHeaderCalc('Concrete Protection', 'm', {
            formula: 'Run Length (m) if Depth Category − (Pipe Ø / 1000) ≤ 1.25m, else 0',
            rows: _stormPipeRuns.map((r) => ({ ref: r.fromMH && r.toMH ? `${r.fromMH}–${r.toMH}` : r.id, value: r.concreteProtection ?? 0, unit: 'm' })),
          }),
          dataIndex: 'id',
          key: 'stConcreteProt',
          width: 150,
          align: 'center' as const,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{(record.pipeRun.concreteProtection ?? 0).toFixed(3)}</div>;
          },
        },
        {
          title: colHeaderCalc('Rockers', 'Nr', {
            formula: '2 per pipe run (one at each end)',
            rows: _stormPipeRuns.map((r) => ({ ref: r.fromMH && r.toMH ? `${r.fromMH}–${r.toMH}` : r.id, value: r.rockers ?? 0, unit: 'Nr' })),
          }),
          dataIndex: 'id',
          key: 'stRockers',
          width: 95,
          align: 'center' as const,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{record.pipeRun.rockers ?? 0}</div>;
          },
        },
        {
          title: colHeaderCalc('Junction Size', undefined, {
            formula: 'Rigi ≥ 450mm → {Ø}mm Saddle  |  Concrete ≥ 525mm → Core Drill  |  else → Main Ø × Gully Ø mm Junction',
            rows: [],
          }),
          dataIndex: 'id',
          key: 'stJunctionSize',
          width: 210,
          align: 'left' as const,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            const text = record.pipeRun.junctionSize;
            return (
              <div style={{ ...calcCellStyle, textAlign: 'left', whiteSpace: 'nowrap' }}>
                {text || <span style={{ color: '#d1d5db' }}>—</span>}
              </div>
            );
          },
        },
        {
          title: colHeader('Junctions', 'Nr'),
          dataIndex: 'id',
          key: 'stJunctions',
          width: 90,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.pipeRun) return <span style={{ color: '#d1d5db' }}>—</span>;
            const run = record.pipeRun;
            return <NumberInput size="small" value={run.junctions ?? 0} onChange={(v) => updatePipeRun(run.id, 'junctions', v)} isInteger decimalPlaces={0} positiveOnly style={{ width: '100%', textAlign: 'right' }} />;
          },
        },
      ],
    },
    // ── House Laterals group ──────────────────────────────────────────────────
    {
      title: (
        <div style={{ textAlign: 'center', fontWeight: 700, color: '#065f46', background: '#d1fae5', padding: '4px 8px', borderRadius: '4px' }}>
          House Laterals
        </div>
      ),
      key: 'stormHouseLateralsGroup',
      children: [
        {
          title: colHeader('Manhole Ref'),
          dataIndex: 'id',
          key: 'stLatManholeRef',
          width: 110,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            const lat = record.lateral;
            return (
              <Select size="small" value={lat.toManhole} onChange={(v) => updateLateral(lat.id, 'toManhole', v)} placeholder="—" style={{ width: '100%' }} allowClear>
                {stormManholes.filter(mh => mh.ref).map(mh => <Select.Option key={mh.id} value={mh.ref}>{mh.ref}</Select.Option>)}
              </Select>
            );
          },
        },
        {
          title: colHeader('Junction Off Run', 'Nr'),
          dataIndex: 'id',
          key: 'stJunctionOffRun',
          width: 150,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            const lat = record.lateral;
            return <NumberInput size="small" value={lat.junctionOffRun ?? 0} onChange={(v) => updateLateral(lat.id, 'junctionOffRun', v)} isInteger decimalPlaces={0} positiveOnly style={{ width: '100%', textAlign: 'right' }} />;
          },
        },
        {
          title: colHeader('Clay/Conc/Ultra/Rigi'),
          dataIndex: 'id',
          key: 'stLatPipeType',
          width: 185,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            const lat = record.lateral;
            return (
              <Select size="small" value={lat.pipeType || undefined} onChange={(v) => updateLateral(lat.id, 'pipeType', v)} placeholder="—" style={{ width: '100%' }}>
                <Select.Option value="clay">Clay</Select.Option>
                <Select.Option value="concrete">Concrete</Select.Option>
                <Select.Option value="ultra">Ultra</Select.Option>
                <Select.Option value="rigi">Rigi</Select.Option>
              </Select>
            );
          },
        },
        {
          title: colHeader('Pipe Ø', 'mm'),
          dataIndex: 'id',
          key: 'stLatPipeDiameter',
          width: 100,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            const lat = record.lateral;
            return (
              <NumberInput
                size="small"
                value={parseInt((lat.pipeDiameter ?? '').replace(/mm$/i, ''), 10) || 0}
                onChange={(v) => updateLateral(lat.id, 'pipeDiameter', v > 0 ? `${v}mm` : '')}
                isInteger
                decimalPlaces={0}
                positiveOnly
                style={{ width: '100%', textAlign: 'right' }}
              />
            );
          },
        },
        {
          title: colHeader('Length', 'm'),
          dataIndex: 'id',
          key: 'stLatLength',
          width: 85,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            const lat = record.lateral;
            return <NumberInput size="small" value={lat.length ?? 0} onChange={(v) => updateLateral(lat.id, 'length', v)} isInteger decimalPlaces={0} positiveOnly style={{ width: '100%', textAlign: 'right' }} />;
          },
        },
        {
          title: colHeader('Demarc Cover', 'm'),
          dataIndex: 'id',
          key: 'stDemarcCover',
          width: 120,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            const lat = record.lateral;
            return <NumberInput size="small" value={lat.demarcCoverLevel ?? 0} onChange={(v) => updateLateral(lat.id, 'demarcCoverLevel', v)} decimalPlaces={3} positiveOnly style={{ width: '100%', textAlign: 'right' }} />;
          },
        },
        {
          title: colHeader('Demarc Invert', 'm'),
          dataIndex: 'id',
          key: 'stDemarcInvert',
          width: 130,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            const lat = record.lateral;
            return <NumberInput size="small" value={lat.demarcInvertLevel ?? 0} onChange={(v) => updateLateral(lat.id, 'demarcInvertLevel', v)} decimalPlaces={3} positiveOnly style={{ width: '100%', textAlign: 'right' }} />;
          },
        },
        {
          title: colHeaderCalc('Demarc Depth', 'm', {
            formula: 'Demarc Cover Level − Demarc Invert Level',
            rows: _stormLaterals.map((l) => ({ ref: l.toManhole || l.id, value: l.demarcDepth ?? 0, unit: 'm' })),
          }),
          dataIndex: 'id',
          key: 'stDemarcDepth',
          width: 135,
          align: 'center' as const,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{(record.lateral.demarcDepth ?? 0).toFixed(3)}</div>;
          },
        },
        {
          title: colHeaderCalc('Depth Category', 'm', {
            formula: 'ROUND(Demarc Depth / 0.25) × 0.25  →  nearest 0.25m band',
            rows: _stormLaterals.map((l) => ({ ref: l.toManhole || l.id, value: l.demarcDepthCategory ?? 0, unit: 'm' })),
          }),
          dataIndex: 'id',
          key: 'stDemarcDepthCat',
          width: 150,
          align: 'center' as const,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{(record.lateral.demarcDepthCategory ?? 0).toFixed(3)}</div>;
          },
        },
        {
          title: colHeaderCalc('Ave Run Depth', 'm', {
            formula: '(Run Depth Category + Demarc Depth Category) / 2',
            rows: _stormLaterals.map((l) => ({ ref: l.toManhole || l.id, value: l.averageDepth ?? 0, unit: 'm' })),
          }),
          dataIndex: 'id',
          key: 'stAveRunDepth',
          width: 145,
          align: 'center' as const,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{(record.lateral.averageDepth ?? 0).toFixed(3)}</div>;
          },
        },
        {
          title: colHeaderCalc('Avg Run Depth Category', 'm', {
            formula: 'ROUND(Ave Run Depth / 0.25) × 0.25  →  nearest 0.25m band',
            rows: _stormLaterals.map((l) => ({ ref: l.toManhole || l.id, value: l.avgDepthCategory ?? 0, unit: 'm' })),
          }),
          dataIndex: 'id',
          key: 'stAvgRunCat',
          width: 170,
          align: 'center' as const,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{(record.lateral.avgDepthCategory ?? 0).toFixed(3)}</div>;
          },
        },
        {
          title: colHeader('NME', 'Nr'),
          dataIndex: 'id',
          key: 'stNme',
          width: 75,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            const lat = record.lateral;
            return <NumberInput size="small" value={lat.nme ?? 0} onChange={(v) => updateLateral(lat.id, 'nme', v)} isInteger decimalPlaces={0} positiveOnly style={{ width: '100%', textAlign: 'right' }} />;
          },
        },
        {
          title: colHeader('1200mmØ chamber', 'Nr'),
          dataIndex: 'id',
          key: 'stCount1200',
          width: 150,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.lateral) return <span style={{ color: '#d1d5db' }}>—</span>;
            const lat = record.lateral;
            return <NumberInput size="small" value={lat.count1200 ?? 0} onChange={(v) => updateLateral(lat.id, 'count1200', v)} decimalPlaces={2} positiveOnly style={{ width: '100%', textAlign: 'right' }} />;
          },
        },
      ],
    },
    // ── Gully Laterals group ──────────────────────────────────────────────────
    {
      title: (
        <div style={{ textAlign: 'center', fontWeight: 700, color: '#92400e', background: '#fef3c7', padding: '4px 8px', borderRadius: '4px' }}>
          Gully Laterals
        </div>
      ),
      key: 'stormGullyLateralsGroup',
      children: [
        {
          title: colHeader('Run Ref'),
          dataIndex: 'id',
          key: 'stGullyRunRef',
          width: 90,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.gully) return <span style={{ color: '#d1d5db' }}>—</span>;
            const run = record.pipeRun;
            const runRef = run?.fromMH && run?.toMH ? `${run.fromMH}-${run.toMH}` : (run?.fromMH || run?.toMH || '');
            return <div style={calcCellStyle}>{runRef || '—'}</div>;
          },
        },
        {
          title: colHeader('Clay/Conc/Ultra/Rigi'),
          dataIndex: 'id',
          key: 'stGullyPipeType',
          width: 185,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.gully) return <span style={{ color: '#d1d5db' }}>—</span>;
            const g = record.gully;
            return (
              <Select size="small" value={g.pipeType || undefined} onChange={(v) => updateGullyConnection(g.id, 'pipeType', v)} placeholder="—" style={{ width: '100%' }}>
                <Select.Option value="clay">Clay</Select.Option>
                <Select.Option value="concrete">Concrete</Select.Option>
                <Select.Option value="ultra">Ultra</Select.Option>
                <Select.Option value="rigi">Rigi</Select.Option>
              </Select>
            );
          },
        },
        {
          title: colHeader('Pipe Ø', 'mm'),
          dataIndex: 'id',
          key: 'stGullyPipeDiameter',
          width: 100,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.gully) return <span style={{ color: '#d1d5db' }}>—</span>;
            const g = record.gully;
            return (
              <NumberInput
                size="small"
                value={parseInt((g.pipeDiameter ?? '').replace(/mm$/i, ''), 10) || 0}
                onChange={(v) => updateGullyConnection(g.id, 'pipeDiameter', v > 0 ? `${v}mm` : '')}
                isInteger
                decimalPlaces={0}
                positiveOnly
                style={{ width: '100%', textAlign: 'right' }}
              />
            );
          },
        },
        {
          title: colHeader('Length', 'm'),
          dataIndex: 'id',
          key: 'stGullyLength',
          width: 90,
          align: 'center' as const,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.gully) return <span style={{ color: '#d1d5db' }}>—</span>;
            const g = record.gully;
            return <NumberInput size="small" value={g.length} onChange={(v) => updateGullyConnection(g.id, 'length', v)} isInteger decimalPlaces={0} positiveOnly style={{ width: '100%', textAlign: 'right' }} />;
          },
        },
        {
          title: colHeaderCalc('Avg Run Depth', '', {
            formula: 'Depth of gully connection off the main run (from pipe run depth category)',
            rows: _stormGullies.map((g) => ({ ref: g.id, value: g.depthOffMainRun ?? 0, unit: 'm' })),
          }),
          dataIndex: 'id',
          key: 'stGullyAvgRunDepth',
          width: 145,
          align: 'center' as const,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.gully) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{(record.gully.depthOffMainRun ?? 0).toFixed(3)}</div>;
          },
        },
        {
          title: colHeader('Gully Depth', 'm'),
          dataIndex: 'id',
          key: 'stGullyDepth',
          width: 100,
          align: 'center' as const,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.gully) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{GULLY_DEPTH_M.toFixed(3)}</div>;
          },
        },
        {
          title: colHeaderCalc('Ave Run Depth', 'm', {
            formula: '(Avg Run Depth + Gully Depth) / 2',
            rows: _stormGullies.map((g) => ({ ref: g.id, value: g.averageDepth ?? 0, unit: 'm' })),
          }),
          dataIndex: 'id',
          key: 'stGullyAveRunDepth',
          width: 145,
          align: 'center' as const,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.gully) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{(record.gully.averageDepth ?? 0).toFixed(3)}</div>;
          },
        },
        {
          title: colHeaderCalc('Avg Run Depth Category', 'm', {
            formula: 'ROUND(Ave Run Depth / 0.25) × 0.25  →  nearest 0.25m band',
            rows: _stormGullies.map((g) => ({ ref: g.id, value: g.avgDepthCategory ?? 0, unit: 'm' })),
          }),
          dataIndex: 'id',
          key: 'stGullyAvgDepthCat',
          width: 215,
          align: 'center' as const,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.gully) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{(record.gully.avgDepthCategory ?? 0).toFixed(3)}</div>;
          },
        },
        {
          title: colHeader('Saddle Junction', 'Nr'),
          dataIndex: 'id',
          key: 'stGullySaddleJunction',
          width: 145,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.gully) return <span style={{ color: '#d1d5db' }}>—</span>;
            const g = record.gully;
            return <NumberInput size="small" value={g.junction150x150} onChange={(v) => updateGullyConnection(g.id, 'junction150x150', v)} isInteger decimalPlaces={0} positiveOnly style={{ width: '100%', textAlign: 'right' }} />;
          },
        },
        {
          title: colHeader('150mmØ Rocker (conn into MH)', 'Nr'),
          dataIndex: 'id',
          key: 'stGullyRocker',
          width: 230,
          align: 'center' as const,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.gully) return <span style={{ color: '#d1d5db' }}>—</span>;
            const g = record.gully;
            return <NumberInput size="small" value={g.gulleyIntoManhole} onChange={(v) => updateGullyConnection(g.id, 'gulleyIntoManhole', v)} isInteger decimalPlaces={0} positiveOnly style={{ width: '100%', textAlign: 'right' }} />;
          },
        },
        {
          title: colHeaderCalc('Conc B&S', 'm', {
            formula: 'Gully lateral length where Ave Run Depth Category − (Pipe Ø / 1000) ≤ 1.25m',
            rows: _stormGullies.map((g) => ({ ref: g.id, value: g.concBS ?? 0, unit: 'm' })),
          }),
          dataIndex: 'id',
          key: 'stGullyConcBS',
          width: 110,
          align: 'center' as const,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.gully) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{(record.gully.concBS ?? 0).toFixed(3)}</div>;
          },
        },
        {
          title: colHeaderCalc('Gullies', 'Nr', {
            formula: 'Number of gullies per connection (derived from lateral count)',
            rows: _stormGullies.map((g) => ({ ref: g.id, value: g.gulleys ?? 0, unit: 'Nr' })),
          }),
          dataIndex: 'id',
          key: 'stGullies',
          width: 95,
          align: 'center' as const,
          render: (_: unknown, record: StormTakeOffRow) => {
            if (!record.gully) return <span style={{ color: '#d1d5db' }}>—</span>;
            return <div style={calcCellStyle}>{record.gully.gulleys ?? 0}</div>;
          },
        },
      ],
    },
    // ── Delete action column ──────────────────────────────────────────────────
    {
      title: 'Actions',
      key: 'stCombinedActions',
      width: 80,
      fixed: 'right' as const,
      align: 'center' as const,
      render: (_: unknown, record: StormTakeOffRow) => (
        <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => requestDelete(() => deleteCombinedStormRow(record.index))} />
      ),
    },
  ];
  // ── Storm Summary helpers ──────────────────────────────────────────────────

  interface StormFormulaInfoRow { ref: string; value: number; unit: string }
  interface StormFormulaInfo {
    label: string;
    formula: string;
    rows: StormFormulaInfoRow[];
    total: number;
  }
  type StormSummaryRowType = 'section' | 'pipe-type' | 'item';
  interface StormSummaryRow {
    key: string;
    rowType: StormSummaryRowType;
    reference?: string;
    description?: string;
    quantity?: string;
    unit?: string;
    evaluateCode?: string;
    emptyText?: string;
    formulaInfo?: StormFormulaInfo;
    children?: StormSummaryRow[];
  }

  const pipeTypeEvalCode = (pt: string): string => {
    const m: Record<string, string> = { clay: 'PS', ultra: 'PU', concrete: 'PM', rigi: 'PR' };
    return m[pt?.toLowerCase()] ?? '';
  };

  const pipeRuns = stormSewersData.pipeRuns.table || [];
  const stormAllGullies = stormSewersData.gullyConnections.table || [];
  const manholes = availableManholes;

  // ── Drainage Runs section ──────────────────────────────────────────────────
  const stormDrainageRunItems: StormSummaryRow[] = pipeRuns.map((run, i) => {
    const diamNum = (run.diameter || '').replace(/mm$/i, '');
    const bf = backfillLabel(run.backfillType ?? '');
    const dti = (run.runDepthCategory || 0).toFixed(2);
    const ptCode = pipeTypeEvalCode(run.pipeType ?? '');
    const evalCode = diamNum && run.backfillType
      ? `${ptCode}${diamNum}\\${backfillEvalCode(run.backfillType)}\\${dti}`
      : '';
    const runLength = run.length || 0;
    // Only pair fromMH/toMH into a "A-B" reference when both manholes are actually
    // selected — blank take-off rows (padded rows, or rows a user hasn't filled in
    // yet) have no manhole chosen and previously rendered as "undefined-undefined".
    const refLabel = run.fromMH && run.toMH ? `${run.fromMH}-${run.toMH}` : (run.fromMH || run.toMH || '');
    return {
      key: run.id || `run-${i}`,
      rowType: 'item',
      reference: run.ref || refLabel,
      description: `${refLabel ? `${refLabel} - ` : ''}${diamNum}mmØ, GBS, backfill with ${bf}, cart surplus on site - dti ${dti}m.`,
      quantity: runLength.toFixed(3),
      unit: 'm',
      evaluateCode: evalCode,
      formulaInfo: {
        label: refLabel ? `Run ${refLabel}` : 'Run',
        formula: `Pipe run length from take-off data · ${diamNum}mmØ ${run.pipeType ?? ''} · backfill: ${bf} · dti: ${dti}m`,
        rows: [{ ref: refLabel || run.id, value: runLength, unit: 'm' }],
        total: runLength,
      },
    };
  });

  // ── Rocker / Stub Pipes section ────────────────────────────────────────────
  const stormStubPipesSection: StormSummaryRow = {
    key: 'stub-pipes',
    rowType: 'pipe-type',
    reference: 'Rocker / Stub Pipes',
    children: STORM_STUB_PIPE_GROUPS.map((grp) => ({
      key: grp.key,
      rowType: 'pipe-type' as StormSummaryRowType,
      reference: grp.label,
      children: grp.items.map((item) => {
        const qty = calcStormRockerQty(pipeRuns, grp.pipeType, item.dia);
        const contributing = pipeRuns.filter(
          (r) => (r.pipeType ?? '').toLowerCase() === grp.pipeType.toLowerCase() && parseInt(r.diameter ?? '', 10) === item.dia,
        );
        return {
          key: `sstub-${item.code}`,
          rowType: 'item' as StormSummaryRowType,
          evaluateCode: item.code || undefined,
          description: item.desc,
          quantity: qty > 0 ? qty.toString() : '',
          unit: 'Nr',
          formulaInfo: {
            label: item.desc,
            formula: `SUMIFS(rockers, pipeType="${grp.pipeType}", diameter=${item.dia}mm)`,
            rows: contributing.map((r) => ({ ref: `${r.fromMH}–${r.toMH}`, value: r.rockers ?? 0, unit: 'Nr' })),
            total: qty,
          } as StormFormulaInfo,
        };
      }),
    })),
  };

  // ── Junctions section ──────────────────────────────────────────────────────
  // Standard junction: match by positional gully pipeDiameter (not junctionSize string)
  const getStormJunctionQty = (pType: string, mainDia: string, branchDia: string): number => {
    if (!branchDia) return 0;
    return pipeRuns
      .filter((r, i) =>
        (r.pipeType ?? '').toLowerCase() === pType.toLowerCase() &&
        r.diameter === mainDia &&
        stormAllGullies[i]?.pipeDiameter === branchDia,
      )
      .reduce((sum, r) => sum + (r.junctions ?? 0), 0);
  };

  // Saddle: rigi pipe runs at exactly the given main diameter
  const getStormSaddleQty = (mainDia: string): number =>
    pipeRuns
      .filter(
        (r) =>
          (r.pipeType ?? '').toLowerCase() === 'rigi' &&
          r.diameter === mainDia,
      )
      .reduce((sum, r) => sum + (r.junctions ?? 0), 0);

  // Core drill: all concrete pipe runs with diameter >= 525mm
  const getStormCoreDrillQty = (): number =>
    pipeRuns
      .filter(
        (r) =>
          (r.pipeType ?? '').toLowerCase() === 'concrete' &&
          (parseInt((r.diameter ?? '').replace(/mm$/i, ''), 10) || 0) >= 525,
      )
      .reduce((sum, r) => sum + (r.junctions ?? 0), 0);

  const stormJunctionChildren: StormSummaryRow[] = STORM_JUNCTION_GROUPS.map((grp) => ({
    key: grp.key,
    rowType: 'pipe-type' as StormSummaryRowType,
    reference: grp.label,
    children: grp.items.map((item) => {
      let qty = 0;
      let formula = '';
      let contributing: typeof pipeRuns = [];

      if (item.branchDia) {
        // Standard junction — match pipeType + mainDia + gully pipeDiameter
        qty = getStormJunctionQty(grp.pipeType, item.mainDia, item.branchDia);
        contributing = pipeRuns.filter(
          (r, i) =>
            (r.pipeType ?? '').toLowerCase() === grp.pipeType.toLowerCase() &&
            r.diameter === item.mainDia &&
            stormAllGullies[i]?.pipeDiameter === item.branchDia,
        );
        formula = `SUMIFS(junctions, pipeType="${grp.pipeType}", mainDia=${item.mainDia}, gullyDia=${item.branchDia})`;
      } else if (grp.pipeType === 'rigi') {
        // Saddle — rigi pipes at exactly this mainDia
        qty = getStormSaddleQty(item.mainDia);
        contributing = pipeRuns.filter(
          (r) =>
            (r.pipeType ?? '').toLowerCase() === 'rigi' &&
            r.diameter === item.mainDia,
        );
        formula = `Sum junctions from Ridgisewer pipe runs where diameter = ${item.mainDia} (saddle required)`;
      } else if (grp.pipeType === 'concrete') {
        // Core drill — concrete pipes >= 525mm
        qty = getStormCoreDrillQty();
        contributing = pipeRuns.filter(
          (r) =>
            (r.pipeType ?? '').toLowerCase() === 'concrete' &&
            (parseInt((r.diameter ?? '').replace(/mm$/i, ''), 10) || 0) >= 525,
        );
        formula = `Sum junctions from Concrete pipe runs where diameter >= 525mm (core drill required)`;
      }

      return {
        key: `sjunc-${item.code || item.desc}`,
        rowType: 'item' as StormSummaryRowType,
        evaluateCode: item.code || undefined,
        description: item.desc,
        quantity: qty > 0 ? qty.toString() : '',
        unit: 'Nr',
        formulaInfo: {
          label: item.desc,
          formula,
          rows: contributing.map((r) => ({ ref: `${r.fromMH}–${r.toMH}`, value: r.junctions ?? 0, unit: 'Nr' })),
          total: qty,
        } as StormFormulaInfo,
      };
    }),
  }));

  // Moved under Junctions (below) rather than EO Items — both are concrete
  // quantities tied to junction diameters, not general EO items.
  const stormConcBedGroup: StormSummaryRow = {
    key: 'seo-conc-bed', rowType: 'pipe-type', reference: 'EO Concrete Bed & Surround',
    children: STORM_EO_CONC_BED_ITEMS.map((item) => ({
      key: `seo-cb-${item.code}`,
      rowType: 'item' as StormSummaryRowType,
      evaluateCode: item.code || undefined,
      description: item.desc,
      quantity: '0',
      unit: 'm',
      formulaInfo: {
        label: item.desc,
        formula: 'Hardcoded 0 — enter manually if applicable',
        rows: [],
        total: 0,
      } as StormFormulaInfo,
    })),
  };
  const stormConcProtectGroup: StormSummaryRow = {
    key: 'seo-conc-protect', rowType: 'pipe-type', reference: 'Concrete Protection Slab',
    children: STORM_CONC_PROTECTION_ITEMS.map((item) => {
      const qty = calcStormConcreteProtection(pipeRuns, item.dia);
      const contributing = pipeRuns.filter((r) => parseInt(r.diameter ?? '', 10) === item.dia);
      return {
        key: `seo-cp-${item.code}`,
        rowType: 'item' as StormSummaryRowType,
        evaluateCode: item.code || undefined,
        description: item.desc,
        quantity: qty.toFixed(3),
        unit: 'm',
        formulaInfo: {
          label: item.desc,
          formula: `SUMIF(diameter=${item.dia}mm, concreteProtection)`,
          rows: contributing.map((r) => ({ ref: `${r.fromMH}–${r.toMH}`, value: r.concreteProtection ?? 0, unit: 'm' })),
          total: qty,
        } as StormFormulaInfo,
      };
    }),
  };

  const stormJunctionsSection: StormSummaryRow = {
    key: 'junctions',
    rowType: 'pipe-type',
    reference: 'Junctions',
    children: [...stormJunctionChildren, stormConcBedGroup, stormConcProtectGroup],
  };

  // ── EO Items section ───────────────────────────────────────────────────────
  const stormEoItemsSection: StormSummaryRow = {
    key: 'eo-items',
    rowType: 'pipe-type',
    reference: 'EO Items',
    children: [
      {
        key: 'seo-misc', rowType: 'pipe-type', reference: 'Miscellaneous EO Items',
        children: STORM_EO_ITEMS.map((item, idx) => ({
          key: `seo-misc-${idx}`,
          rowType: 'item' as StormSummaryRowType,
          description: item.desc,
          quantity: '0',
          unit: item.unit,
          formulaInfo: {
            label: item.desc,
            formula: 'Hardcoded 0 — enter manually if applicable',
            rows: [],
            total: 0,
          } as StormFormulaInfo,
        })),
      },
    ],
  };

  const stormRedundantSewerSection: StormSummaryRow = {
    key: 'seo-redundant', rowType: 'pipe-type', reference: 'Removal of Redundant Sewer',
    children: STORM_REDUNDANT_SEWER_ITEMS.map((item, idx) => ({
      key: `seo-redundant-${idx}`,
      rowType: 'item' as StormSummaryRowType,
      description: item.desc,
      quantity: '0',
      unit: item.unit,
      formulaInfo: {
        label: item.desc,
        formula: 'Hardcoded 0 — enter manually if applicable',
        rows: [],
        total: 0,
      } as StormFormulaInfo,
    })),
  };

  // ── Manholes section ───────────────────────────────────────────────────────
  const stormManholeBaseRows: StormSummaryRow[] = manholes.map((mh, i) => {
    const diamNum = (mh.diameter || '').replace(/mm$/i, '');
    const chamberType = mh.easiTrad === 'easi' ? ' Wide wall chamber' : mh.easiTrad === 'trad' ? 'Traditional chamber' : '';
    const dti = (mh.depthCategory || 0).toFixed(2);
    const chamberCode = mh.easiTrad === 'easi' ? 'Easi' : mh.easiTrad === 'trad' ? 'Trad' : '';
    const evalCode = chamberCode && diamNum ? `${chamberCode}\\${diamNum}\\${dti}` : '';
    return {
      key: mh.id || `smh-${i}`,
      rowType: 'item',
      reference: mh.ref,
      description: `${mh.ref} - ${diamNum}mmØ,${chamberType} dti - ${dti}m`,
      quantity: '1',
      unit: 'Nr',
      evaluateCode: evalCode,
      formulaInfo: {
        label: mh.ref,
        formula: `Manhole from take-off data · ${diamNum}mmØ · ${mh.easiTrad ?? ''} chamber · depth to invert: ${dti}m`,
        rows: [{ ref: mh.ref, value: 1, unit: 'Nr' }],
        total: 1,
      },
    };
  });

  const stormMhAccessoryItems: StormSummaryRow[] = (() => {
    const ladderCfgLabel = stormSewersData.manholes.config.manholeScheduleConfig?.ladders ?? 'Galvanised';
    const safetyCfgLabel = stormSewersData.manholes.config.manholeScheduleConfig?.safetyChains ?? 'Galvanised';
    const totalLadders   = calcStormLadderMetres(manholes);
    const totalChains    = calcStormChainMetres(manholes);
    const totalAMPS      = manholes.reduce((s, mh) => s + (mh.amps ?? 0), 0);
    const totalBackdrops = manholes.reduce((s, mh) => s + (mh.backdrop ?? 0), 0);
    const totalMuckStop  = manholes.reduce((s, mh) => s + (mh.muckStopper ?? 0), 0);

    const ampsMhs    = manholes.filter((mh) => (mh.amps ?? 0) > 0);
    const backdropMhs = manholes.filter((mh) => (mh.backdrop ?? 0) > 0);
    const muckMhs    = manholes.filter((mh) => (mh.muckStopper ?? 0) > 0);

    return [
      {
        key: 'smh-ladders', rowType: 'item',
        description: `Manhole ladders (if not incl in manhole price)${ladderCfgLabel !== 'N/A' ? ` - ${ladderCfgLabel}` : ''}`,
        quantity: totalLadders.toFixed(3), unit: 'm',
        formulaInfo: {
          label: 'Manhole Ladders',
          formula: 'Per manhole: N/A in Configure → 0  |  Depth Category ≤ 2.25 → 0  |  else: Invert Level − Sump − 0.15 — summed across all manholes',
          rows: manholes.filter((mh) => (mh.ladders ?? 0) > 0).map((mh) => ({ ref: mh.ref, value: mh.ladders ?? 0, unit: 'm' })),
          total: totalLadders,
        },
      },
      {
        key: 'smh-chains', rowType: 'item',
        description: `Safety Chains${safetyCfgLabel !== 'N/A' ? ` - ${safetyCfgLabel}` : ''}`,
        quantity: totalChains.toFixed(3), unit: 'Nr',
        formulaInfo: {
          label: 'Safety Chains',
          formula: 'Per manhole: N/A in Configure → 0  |  Depth Category ≤ 2.25 → 0  |  else: Invert Level − Sump − 0.15 — summed across all manholes',
          rows: manholes.filter((mh) => (mh.safetyChains ?? 0) > 0).map((mh) => ({ ref: mh.ref, value: mh.safetyChains ?? 0, unit: 'Nr' })),
          total: totalChains,
        },
      },
      {
        key: 'smh-amps', rowType: 'item',
        description: 'AMPS',
        quantity: totalAMPS.toString(), unit: 'Nr',
        formulaInfo: {
          label: 'AMPS',
          formula: 'SUM(amps) across all manholes',
          rows: ampsMhs.map((mh) => ({ ref: mh.ref, value: mh.amps ?? 0, unit: 'Nr' })),
          total: totalAMPS,
        },
      },
      {
        key: 'smh-backdrops', rowType: 'item',
        description: 'Backdrops',
        quantity: totalBackdrops.toString(), unit: 'Nr',
        formulaInfo: {
          label: 'Backdrops',
          formula: 'SUM(backdrop) across all manholes',
          rows: backdropMhs.map((mh) => ({ ref: mh.ref, value: mh.backdrop ?? 0, unit: 'Nr' })),
          total: totalBackdrops,
        },
      },
      {
        key: 'smh-muckstoppers', rowType: 'item',
        description: 'Muckstoppers',
        quantity: totalMuckStop.toString(), unit: 'Nr',
        formulaInfo: {
          label: 'Muckstoppers',
          formula: 'SUM(muckStopper) across all manholes',
          rows: muckMhs.map((mh) => ({ ref: mh.ref, value: mh.muckStopper ?? 0, unit: 'Nr' })),
          total: totalMuckStop,
        },
      },
      {
        key: 'smh-clegg', rowType: 'item',
        description: "Clegg testing to sewer b'fill (500mm layers at 30m intervals)",
        quantity: '0', unit: 'Nr',
        formulaInfo: { label: "Clegg testing to sewer b'fill (500mm layers at 30m intervals)", formula: 'Hardcoded 0 — enter manually if applicable', rows: [], total: 0 },
      },
      ...STORM_HYDROBRAKE_PENSTOCK_WEIR_ITEMS.map((item, idx) => ({
        key: `smh-hw-${idx}`,
        rowType: 'item' as StormSummaryRowType,
        description: item.desc,
        quantity: '0', unit: item.unit,
        evaluateCode: item.code,
        formulaInfo: { label: item.desc, formula: 'Hardcoded 0 — enter manually if applicable', rows: [], total: 0 },
      })),
    ];
  })();

  const stormMhAdjustmentRow: StormSummaryRow = {
    key: 'smh-adjustment',
    rowType: 'pipe-type',
    reference: 'ADJUSTMENT FOR ROAD CONSTRUCTION GREATER THAN 450mm',
    children: [{
      key: 'smh-credits',
      rowType: 'pipe-type',
      reference: 'Credits',
      children: [
        {
          key: 'smh-adj-trench', rowType: 'item',
          description: 'Trench Excavation',
          quantity: stormEoConcBedQtys['smh-adj-trench'] ? String(stormEoConcBedQtys['smh-adj-trench']) : '', unit: 'm3',
        },
        {
          key: 'smh-adj-ews', rowType: 'item',
          description: 'EWS',
          quantity: stormEoConcBedQtys['smh-adj-ews'] ? String(stormEoConcBedQtys['smh-adj-ews']) : '', unit: 'm2',
        },
        {
          key: 'smh-adj-disposal', rowType: 'item',
          description: 'Disposal',
          quantity: stormEoConcBedQtys['smh-adj-disposal'] ? String(stormEoConcBedQtys['smh-adj-disposal']) : '', unit: 'm3',
        },
        {
          key: 'smh-adj-backfill', rowType: 'item',
          description: 'Backfill',
          quantity: stormEoConcBedQtys['smh-adj-backfill'] ? String(stormEoConcBedQtys['smh-adj-backfill']) : '', unit: 'm3',
        },
      ],
    }],
  };

  // ── Filter Drain — nested sub-group within Manholes, sitting above the
  // "ADJUSTMENT FOR ROAD CONSTRUCTION..." credits group in the source sheet.
  // Manual inputs, no formula backing (no evaluate codes on the source sheet
  // for these two rows).
  const stormFilterDrainGroup: StormSummaryRow = {
    key: 'filter-drain',
    rowType: 'pipe-type',
    reference: 'Filter Drain',
    children: STORM_FILTER_DRAIN_ITEMS.map((item, idx) => ({
      key: `sfilterdrain-${idx}`,
      rowType: 'item' as StormSummaryRowType,
      description: item.desc,
      quantity: '0', unit: item.unit,
      formulaInfo: { label: item.desc, formula: 'Hardcoded 0 — enter manually if applicable', rows: [], total: 0 },
    })),
  };

  const stormCustomManholeSection: StormSummaryRow = {
    key: 'manholes',
    rowType: 'section',
    description: 'Manholes',
    children: [
      ...(stormManholeBaseRows.length > 0
        ? stormManholeBaseRows
        : [{ key: 'smh-empty', rowType: 'item' as StormSummaryRowType, description: 'No manholes entered yet' }]),
      ...stormMhAccessoryItems,
      stormFilterDrainGroup,
      stormMhAdjustmentRow,
    ],
  };

  // ── House Laterals section ─────────────────────────────────────────────────
  const stormLateralItems: StormSummaryRow[] = (stormSewersData.lateralConnections.table || []).map((lat, i) => {
    const diamNum = (lat.pipeDiameter || '').replace(/mm$/i, '');
    // House Laterals have no backfill field of their own — the source sheet's
    // description formula reads the paired Main Sewer Run's Backfill column,
    // matched positionally by row (same convention as Gully Laterals).
    const runBackfillType = pipeRuns[i]?.backfillType;
    const bf = backfillLabel(runBackfillType ?? '');
    const dti = (lat.avgDepthCategory || 0).toFixed(2);
    const ptCode = pipeTypeEvalCode(lat.pipeType ?? '');
    const evalCode = diamNum && runBackfillType
      ? `${ptCode}${diamNum}\\${backfillEvalCode(runBackfillType)}\\${dti}`
      : '';
    const latLength = lat.length || 0;
    const mhRef = lat.toManhole || '';
    return {
      key: lat.id || `slat-${i}`,
      rowType: 'item',
      reference: mhRef,
      description: `${diamNum}mmØ, GBS, backfill with ${bf}, cart surplus on site - dti ${dti}m.`,
      quantity: latLength.toFixed(3),
      unit: 'm',
      evaluateCode: evalCode,
      formulaInfo: {
        label: mhRef,
        formula: `Lateral connection length from take-off data · ${diamNum}mmØ ${lat.pipeType ?? ''} · backfill: ${bf} · dti: ${dti}m`,
        rows: [{ ref: mhRef, value: latLength, unit: 'm' }],
        total: latLength,
      },
    };
  });

  const stormNmeQty = (stormSewersData.lateralConnections.table || []).reduce((sum, lat) => sum + (lat.nme ?? 0), 0);
  // The "1200" column holds each chamber's own depth (e.g. 3.25), not a Nr
  // count — so the chamber total is how many laterals have a depth entered,
  // and the depth breakdown below counts how many share each depth.
  const stormLaterals1200 = (stormSewersData.lateralConnections.table || []).filter((lat) => (lat.count1200 ?? 0) > 0);

  // 1200mmØ chamber depth breakdown — av 3.00m deep .. av 6.00m deep, in 0.25m steps.
  const STORM_DEPTH_BUCKETS_1200 = Array.from({ length: 13 }, (_, i) => (3 + i * 0.25).toFixed(2));
  const stormCount1200DepthRows: StormSummaryRow[] = STORM_DEPTH_BUCKETS_1200.map((d) => {
    const matching = stormLaterals1200.filter((lat) => (lat.count1200 ?? 0).toFixed(2) === d);
    const qty = matching.length;
    return {
      key: `slat-1200-chamber-${d}`,
      rowType: 'item' as const,
      description: `av ${d}m deep`,
      quantity: qty > 0 ? String(qty) : '',
      unit: 'Nr',
      formulaInfo: {
        label: `1200mmØ chamber av ${d}m deep`,
        formula: `COUNT(house laterals) where 1200 column = ${d}m`,
        rows: matching.map((lat) => ({ ref: lat.toManhole || '', value: 1, unit: 'Nr' })),
        total: qty,
      } as StormFormulaInfo,
    };
  });

  const stormLateralsSection: StormSummaryRow = {
    key: 'storm-laterals',
    rowType: 'pipe-type',
    reference: 'Laterals Connections',
    children: [
      ...(stormLateralItems.length > 0
        ? stormLateralItems
        : [{ key: 'slat-empty', rowType: 'item' as const, description: 'No house laterals entered yet' }]),
      {
        key: 'slat-nme-chamber',
        rowType: 'item' as const,
        description: 'NME Chamber',
        quantity: stormNmeQty > 0 ? String(stormNmeQty) : '',
        unit: 'Nr',
        formulaInfo: {
          label: 'NME Chamber',
          formula: 'SUM(nme) across all house laterals',
          rows: (stormSewersData.lateralConnections.table || [])
            .filter((lat) => (lat.nme ?? 0) > 0)
            .map((lat) => ({ ref: lat.toManhole || '', value: lat.nme ?? 0, unit: 'Nr' })),
          total: stormNmeQty,
        } as StormFormulaInfo,
      },
      {
        key: 'slat-1200-chamber',
        rowType: 'item' as const,
        description: '1200mmØ chamber',
        quantity: '',
        unit: 'Nr',
      },
      ...stormCount1200DepthRows,
      {
        key: 'slat-b125-cover',
        rowType: 'item' as const,
        description: 'EO B125 Cover & frame to drives',
        quantity: '0', unit: 'Nr',
        formulaInfo: { label: 'EO B125 Cover & frame to drives', formula: 'Hardcoded 0 — enter manually if applicable', rows: [], total: 0 } as StormFormulaInfo,
      },
    ],
  };

  // ── Gully Laterals section ─────────────────────────────────────────────────
  const stormGullyItems: StormSummaryRow[] = (stormSewersData.gullyConnections.table || []).map((gully, i) => {
    const diamNum = (gully.pipeDiameter || '').replace(/mm$/i, '');
    const ptCode = pipeTypeEvalCode(gully.pipeType ?? '');
    const dti = (gully.avgDepthCategory || 0).toFixed(2);
    // Gully connections have no backfill field of their own — the source sheet's
    // description formula reads the paired Main Sewer Run's Backfill column,
    // matched positionally by row like pipeRuns[i]/laterals[i]/gullies[i].
    const bf = backfillLabel((stormSewersData.pipeRuns.table || [])[i]?.backfillType ?? '');
    const evalCode = ptCode && diamNum ? `${ptCode}${diamNum}\\${dti}` : '';
    const gullyLength = gully.length || 0;
    const label = gully.gullyRef || gully.runRef || `G${i + 1}`;
    return {
      key: gully.id || `sgully-${i}`,
      rowType: 'item',
      reference: label,
      description: `${diamNum}mmØ, GBS, backfill with ${bf}, cart surplus on site - dti ${dti}m.`,
      quantity: gullyLength.toFixed(3),
      unit: 'm',
      evaluateCode: evalCode,
      formulaInfo: {
        label,
        formula: `Gully lateral length from take-off data · ${diamNum}mmØ ${gully.pipeType ?? ''} · backfill: ${bf} · dti: ${dti}m`,
        rows: [{ ref: label, value: gullyLength, unit: 'm' }],
        total: gullyLength,
      },
    };
  });

  const gullies = stormSewersData.gullyConnections.table || [];

  const totalConcBS      = gullies.reduce((s, g) => s + (g.concBS ?? 0), 0);
  const totalGullyNr     = gullies.reduce((s, g) => s + (g.gulleys ?? 0), 0);
  const totalRocker150   = gullies.reduce((s, g) => s + (g.gulleyIntoManhole ?? 0), 0);

  const stormGullySection: StormSummaryRow = {
    key: 'gully-laterals',
    rowType: 'pipe-type',
    reference: 'Gully Laterals',
    children: [
      ...(stormGullyItems.length > 0
        ? stormGullyItems
        : [{ key: 'sgully-empty', rowType: 'item' as const, description: 'No gully laterals entered yet' }]),
      {
        key: 'sgully-conc-bs',
        rowType: 'item' as const,
        description: 'Concrete bed & Surround',
        quantity: totalConcBS > 0 ? totalConcBS.toFixed(3) : '',
        unit: 'm',
        formulaInfo: {
          label: 'Concrete bed & Surround',
          formula: 'SUM(IF(avgDepthCategory<=1.25, length, 0)) across all gully laterals (Excel: SUM(AQ9:AQ208))',
          rows: gullies.filter((g) => (g.concBS ?? 0) > 0).map((g) => ({ ref: g.gullyRef || g.id, value: g.concBS ?? 0, unit: 'm' })),
          total: totalConcBS,
        } as StormFormulaInfo,
      },
      {
        key: 'sgully-gully-complete',
        rowType: 'item' as const,
        description: 'Precast concrete road gully 450x900 inc. grate & frame',
        quantity: totalGullyNr > 0 ? String(totalGullyNr) : '',
        unit: 'Nr',
        formulaInfo: {
          label: 'Precast concrete road gully 450x900 inc. grate & frame',
          formula: 'SUM(junctions + 150mmRocker - junctionOffRun) across all gully laterals (Excel: SUM(AR9:AR208))',
          rows: gullies.filter((g) => (g.gulleys ?? 0) > 0).map((g) => ({ ref: g.gullyRef || g.id, value: g.gulleys ?? 0, unit: 'Nr' })),
          total: totalGullyNr,
        } as StormFormulaInfo,
      },
      {
        key: 'sgully-rocker-150',
        rowType: 'item' as const,
        description: '150mmØ Rocker',
        quantity: totalRocker150 > 0 ? String(totalRocker150) : '',
        unit: 'Nr',
        formulaInfo: {
          label: '150mmØ Rocker',
          formula: 'SUM(gulleyIntoManhole) across all gully laterals (Excel: SUM(AP9:AP208))',
          rows: gullies.filter((g) => (g.gulleyIntoManhole ?? 0) > 0).map((g) => ({ ref: g.gullyRef || g.id, value: g.gulleyIntoManhole ?? 0, unit: 'Nr' })),
          total: totalRocker150,
        } as StormFormulaInfo,
      },
      {
        key: 'sgully-gulliblok',
        rowType: 'item' as const,
        description: 'GulliBlok',
        quantity: '',
        unit: 'Nr',
      },
      {
        key: 'sgully-emptying',
        rowType: 'item' as const,
        description: 'Emptying of gulliblok - periodically',
        quantity: '',
        unit: 'Nr',
      },
      {
        key: 'sgully-bags',
        rowType: 'item' as const,
        description: 'Gully Bags',
        quantity: '',
        unit: 'Nr',
      },
    ],
  };

  // Only two top-level parent sections remain — Drainage Runs and Manholes.
  // Every other former top-level section (Rocker/Stub Pipes, Junctions, EO
  // Items, Removal of Redundant Sewer, House Laterals, Gully Laterals) now
  // nests as a pipe-type group under Drainage Runs.
  const stormDrainageRunsSection: StormSummaryRow = {
    key: 'drainage-runs',
    rowType: 'section',
    description: 'Drainage Runs',
    children: [
      ...(stormDrainageRunItems.length > 0
        ? stormDrainageRunItems
        : [{ key: 'drainage-runs-empty', rowType: 'item' as StormSummaryRowType, description: 'No pipe runs entered yet' }]),
      stormStubPipesSection,
      stormJunctionsSection,
      stormEoItemsSection,
      stormRedundantSewerSection,
      stormLateralsSection,
      stormGullySection,
    ],
  };

  // ── Final table data ───────────────────────────────────────────────────────
  const stormSummaryTableData: StormSummaryRow[] = [
    stormDrainageRunsSection,
    stormCustomManholeSection,
  ];

  // ── Surplus / Backfill tables — mirrors 'Storm Summary' sheet's AO:AS
  // (Surplus Runs/Lateral/Manholes/Gullies/Total) and AU:AY (Backfill Runs/
  // Laterals/Manholes/Gullies/Total) columns. See buildStormSurplusBackfillData
  // (stormSummaryUtils.ts) for the formula detail.
  const stormSurplusBackfillData = buildStormSurplusBackfillData(
    pipeRuns,
    manholes,
    stormSewersData.lateralConnections.table || [],
    stormSewersData.gullyConnections.table || [],
  );

  const collectStormKeys = (rows: StormSummaryRow[]): React.Key[] =>
    rows.flatMap((r) => [r.key, ...(r.children ? collectStormKeys(r.children) : [])]);

  const stormSummaryAllKeys = collectStormKeys(stormSummaryTableData);

  const STORM_SUMMARY_COL_COUNT = showDevMode ? 5 : 4;

  const stormSummaryTableCols = [
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      width: 180,
      render: (v: string, record: StormSummaryRow) => {
        // Total-in-accordion display for Drainage Runs commented out in the UI —
        // now that it nests Stub Pipes/Junctions/EO Items/Laterals/Gully sub-groups,
        // it collapses like any other section instead of reserving 2 columns for a total.
        // if (record.key === 'drainage-runs' && record.rowType === 'section')
        //   return { children: <Typography.Text strong className="text-indigo-600">{record.description}</Typography.Text>, props: { colSpan: 2 } };
        if (record.rowType === 'section')
          return { children: <Typography.Text strong className="text-indigo-600">{record.description}</Typography.Text>, props: { colSpan: STORM_SUMMARY_COL_COUNT } };
        if (record.rowType === 'pipe-type')
          return { children: <Typography.Text strong style={{ color: '#374151' }}>{v || record.description}</Typography.Text>, props: { colSpan: STORM_SUMMARY_COL_COUNT } };
        return v ? <Typography.Text strong>{v}</Typography.Text> : null;
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 400,
      render: (v: string, record: StormSummaryRow) => {
        if (record.rowType === 'section' || record.rowType === 'pipe-type') return { children: null, props: { colSpan: 0 } };
        return (
          <span>
            {v}
            {showDevMode && record.formulaInfo && (
              <InfoCircleOutlined
                style={{ color: '#1677ff', marginLeft: 6, cursor: 'pointer' }}
                onClick={(e) => { e.stopPropagation(); setStormFormulaInfoKey(record.key); }}
              />
            )}
          </span>
        );
      },
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'center' as const,
      render: (v: string, record: StormSummaryRow) => {
        // Total-in-accordion display for Drainage Runs commented out in the UI —
        // it summed only pipe-run metres (nested Nr/m³ sub-groups contributed 0),
        // but now collapses like any other section instead of showing that total.
        // if (record.key === 'drainage-runs' && record.rowType === 'section') {
        //   const total = (record.children ?? []).reduce((sum, c) => sum + (parseFloat(c.quantity || '0') || 0), 0);
        //   return <Typography.Text strong>{total.toFixed(2)}</Typography.Text>;
        // }
        if (record.rowType === 'section' || record.rowType === 'pipe-type') return { children: null, props: { colSpan: 0 } };
        if (record.key.startsWith('smh-adj-')) {
          return (
            <NumberInput
              size="small"
              value={stormEoConcBedQtys[record.key] ?? 0}
              onChange={(val) => setStormEoConcBedQty(record.key, val)}
              isInteger
              decimalPlaces={0}
              style={{ width: '80px', textAlign: 'right' }}
            />
          );
        }
        if (
          record.key.startsWith('seo-cb-') ||
          record.key.startsWith('seo-misc-') ||
          record.key.startsWith('seo-redundant-') ||
          record.key === 'smh-clegg' ||
          record.key.startsWith('smh-hw-') ||
          record.key.startsWith('sfilterdrain-') ||
          record.key === 'slat-b125-cover' ||
          record.key === 'sgully-gulliblok' ||
          record.key === 'sgully-emptying' ||
          record.key === 'sgully-bags'
        ) {
          return (
            <NumberInput
              size="small"
              value={stormEoConcBedQtys[record.key] ?? 0}
              onChange={(val) => setStormEoConcBedQty(record.key, val)}
              isInteger
              positiveOnly
              decimalPlaces={0}
              style={{ width: '80px', textAlign: 'right' }}
            />
          );
        }
        if (v == null) return '—';
        if (record.key === 'slat-1200-chamber' && v === '') return '';
        const n = parseFloat(v);
        return isNaN(n) ? '0' : Math.round(n).toLocaleString('en-GB');
      },
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 60,
      align: 'center' as const,
      render: (v: string, record: StormSummaryRow) => {
        // if (record.key === 'drainage-runs' && record.rowType === 'section') {
        //   return <Typography.Text strong>{formatUnit('m')}</Typography.Text>;
        // }
        if (record.rowType === 'section' || record.rowType === 'pipe-type') return { children: null, props: { colSpan: 0 } };
        return v ? <Typography.Text type="secondary">{formatUnit(v)}</Typography.Text> : null;
      },
    },
    ...(showDevMode ? [{
      title: 'Evaluate Code',
      dataIndex: 'evaluateCode',
      key: 'evaluateCode',
      width: 160,
      render: (v: string, record: StormSummaryRow) => {
        // if (record.key === 'drainage-runs' && record.rowType === 'section') return null;
        if (record.rowType === 'section' || record.rowType === 'pipe-type') return { children: null, props: { colSpan: 0 } };
        if (!v) return <Typography.Text type="secondary">—</Typography.Text>;
        const libRate = getLibRate(v);
        return (
          <span>
            <Tag color="blue">{v}</Tag>
            {libRate !== undefined && (
              <div style={{ color: '#52c41a', fontSize: 12, marginTop: 2 }}>
                {formatCurrency(libRate)}
              </div>
            )}
          </span>
        );
      },
    }] : []),
  ];

  // Numeric scroll.x (sum of column widths) forces table-layout: fixed, which
  // the sticky-header prop below needs to keep the header/body columns in sync.
  const STORM_SUMMARY_TABLE_SCROLL_X = 180 + 400 + 100 + 60 + (showDevMode ? 160 : 0);

  // Shared column shape for the Surplus and Backfill tables below — same
  // layout, different column labels. Clicking the Total cell's info icon
  // opens the stormSurplusBackfillFormula modal (mirrors the main summary
  // table's stormFormulaInfoKey modal above).
  const buildStormSurplusBackfillCols = (labels: { runs: string; laterals: string; manholes: string; gullies: string; total: string }) => [
    { title: 'Reference', dataIndex: 'reference', key: 'reference', width: 120 },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    {
      title: `${labels.runs} (m³)`, dataIndex: 'runs', key: 'runs', width: 130, align: 'right' as const,
      render: (v: number, record: StormSurplusBackfillRow) => (
        <span>
          {v.toFixed(3)}
          {showDevMode && (
            <InfoCircleOutlined
              style={{ color: '#1677ff', marginLeft: 6, cursor: 'pointer' }}
              onClick={() => setStormSurplusBackfillFormula(record.runsFormula)}
            />
          )}
        </span>
      ),
    },
    {
      title: `${labels.laterals} (m³)`, dataIndex: 'laterals', key: 'laterals', width: 130, align: 'right' as const,
      render: (v: number, record: StormSurplusBackfillRow) => (
        <span>
          {v.toFixed(3)}
          {showDevMode && (
            <InfoCircleOutlined
              style={{ color: '#1677ff', marginLeft: 6, cursor: 'pointer' }}
              onClick={() => setStormSurplusBackfillFormula(record.lateralsFormula)}
            />
          )}
        </span>
      ),
    },
    {
      title: `${labels.manholes} (m³)`, dataIndex: 'manholes', key: 'manholes', width: 140, align: 'right' as const,
      render: (v: number, record: StormSurplusBackfillRow) => (
        <span>
          {v.toFixed(3)}
          {showDevMode && (
            <InfoCircleOutlined
              style={{ color: '#1677ff', marginLeft: 6, cursor: 'pointer' }}
              onClick={() => setStormSurplusBackfillFormula(record.manholesFormula)}
            />
          )}
        </span>
      ),
    },
    {
      title: `${labels.gullies} (m³)`, dataIndex: 'gullies', key: 'gullies', width: 130, align: 'right' as const,
      render: (v: number, record: StormSurplusBackfillRow) => (
        <span>
          {v.toFixed(3)}
          {showDevMode && (
            <InfoCircleOutlined
              style={{ color: '#1677ff', marginLeft: 6, cursor: 'pointer' }}
              onClick={() => setStormSurplusBackfillFormula(record.gulliesFormula)}
            />
          )}
        </span>
      ),
    },
    {
      title: `${labels.total} (m³)`, key: 'total', width: 140, align: 'right' as const,
      render: (_: unknown, record: StormSurplusBackfillRow) => (
        <Typography.Text strong>{record.total.toFixed(3)}</Typography.Text>
      ),
    },
  ];

  const summaryContent = (
    <div>
      {stormSummaryView === 'summary' && (
        <>
          <Table<StormSummaryRow>
            className="storm-summary-table"
            dataSource={stormSummaryTableData}
            columns={stormSummaryTableCols}
            pagination={false}
            size="middle"
            bordered
            scroll={{ x: STORM_SUMMARY_TABLE_SCROLL_X }}
            sticky={{ offsetHeader: stickyOffset, offsetScroll: 9999 }}
            expandable={{
              childrenColumnName: 'children',
              expandedRowKeys: summaryExpandedKeys,
              onExpand: (expanded, record) => {
                if (expanded) {
                  setSummaryExpandedKeys([...summaryExpandedKeys, record.key]);
                } else {
                  setSummaryExpandedKeys(summaryExpandedKeys.filter((k) => k !== record.key));
                }
              },
              expandRowByClick: true,
            }}
            rowClassName={(record) =>
              record.rowType === 'section' ? 'bg-indigo-50 font-semibold' : ''
            }
          />
          {(() => {
            if (!stormFormulaInfoKey) return null;
            const findRow = (rows: StormSummaryRow[], key: string): StormSummaryRow | null => {
              for (const r of rows) {
                if (r.key === key) return r;
                if (r.children) { const f = findRow(r.children, key); if (f) return f; }
              }
              return null;
            };
            const row = findRow(stormSummaryTableData, stormFormulaInfoKey);
            if (!row?.formulaInfo) return null;
            const { label, formula, total } = row.formulaInfo;
            const unit = row.unit ?? '';
            const fmt = (n: number) => Number.isInteger(n) ? String(n) : n.toFixed(3);
            return (
              <Modal
                title={`${label} — Quantity Formula`}
                open={true}
                onCancel={() => setStormFormulaInfoKey(null)}
                footer={[<Button key="close" onClick={() => setStormFormulaInfoKey(null)}>Close</Button>]}
                width={560}
              >
                <div style={{ fontSize: 13, color: '#374151' }}>
                  <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f0f4ff', borderRadius: 4, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                    {formula}
                  </div>
                  <div style={{ padding: '16px', textAlign: 'center', color: '#6b7280', background: '#f9fafb', borderRadius: 4 }}>
                    Total: <strong>{fmt(total)} {formatUnit(unit)}</strong>
                  </div>
                </div>
              </Modal>
            );
          })()}
        </>
      )}

      {/* ── Surplus table ────────────────────────────────────────────────── */}
      {stormSummaryView === 'surplus' && (
        <Table<StormSurplusBackfillRow>
          className="storm-summary-table"
          dataSource={stormSurplusBackfillData.surplus}
          columns={buildStormSurplusBackfillCols({
            runs: 'Surplus Runs', laterals: 'Surplus Lateral', manholes: 'Surplus Manholes', gullies: 'Surplus Gullies', total: 'Total Surplus',
          })}
          rowKey="key"
          bordered
          size="middle"
          pagination={false}
          sticky={{ offsetHeader: stickyOffset, offsetScroll: 9999 }}
          summary={() => (
            <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 700 }}>
              <Table.Summary.Cell index={0} colSpan={6}><Typography.Text strong>TOTAL SURPLUS</Typography.Text></Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <Typography.Text strong>{stormSurplusBackfillData.surplusTotal.toFixed(3)}</Typography.Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      )}

      {/* ── Backfill table ───────────────────────────────────────────────── */}
      {stormSummaryView === 'backfill' && (
        <Table<StormSurplusBackfillRow>
          className="storm-summary-table"
          dataSource={stormSurplusBackfillData.backfill}
          columns={buildStormSurplusBackfillCols({
            runs: 'Backfill Runs', laterals: 'Backfill Laterals', manholes: 'Backfill Manholes', gullies: 'Backfill Gullies', total: 'Total Backfill',
          })}
          rowKey="key"
          bordered
          size="middle"
          pagination={false}
          sticky={{ offsetHeader: stickyOffset, offsetScroll: 9999 }}
          summary={() => (
            <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 700 }}>
              <Table.Summary.Cell index={0} colSpan={6}><Typography.Text strong>TOTAL BACKFILL</Typography.Text></Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <Typography.Text strong>{stormSurplusBackfillData.backfillTotal.toFixed(3)}</Typography.Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      )}

      {/* ── Surplus/Backfill formula modal ─────────────────────────────────── */}
      {stormSurplusBackfillFormula && (
        <Modal
          title={`${stormSurplusBackfillFormula.label} — Quantity Formula`}
          open={true}
          onCancel={() => setStormSurplusBackfillFormula(null)}
          footer={[<Button key="close" onClick={() => setStormSurplusBackfillFormula(null)}>Close</Button>]}
          width={560}
        >
          <div style={{ fontSize: 13, color: '#374151' }}>
            <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f0f4ff', borderRadius: 4, fontFamily: 'ui-monospace, monospace', fontSize: 12, whiteSpace: 'pre-line' }}>
              {stormSurplusBackfillFormula.formula}
            </div>
            <div style={{ padding: '16px', textAlign: 'center', color: '#6b7280', background: '#f9fafb', borderRadius: 4 }}>
              Total: <strong>{stormSurplusBackfillFormula.total.toFixed(3)} {formatUnit('m3')}</strong>
            </div>
          </div>
        </Modal>
      )}

    </div>
  );

  return (
    <MainLayout canProceed={true} showFooter={false}>
      <Card
        className="storm-sewers-card shadow-sm"
        bodyStyle={{ padding: 0 }}
        style={{ borderRadius: '8px', overflow: 'hidden' }}
      >
        {/* Sticky header + tabs */}
        <div
          className="storm-sewers-sticky-header"
          style={{
            position: 'sticky',
            top: '144px',
            backgroundColor: '#fff',
            zIndex: 98,
            borderRadius: '8px 8px 0 0',
            isolation: 'isolate',
          }}
        >
          {/* Header */}
          <div className="px-6 pt-6 pb-4" style={{ borderBottom: '1px solid #e5e7eb' }}>
            <div className="flex justify-between items-center">
              <div>
                <Typography.Title level={3} style={{ fontWeight: 600, fontSize: '20px', color: '#111827', marginBottom: '8px' }}>
                  Storm Sewers
                </Typography.Title>
                <Typography.Text type="secondary" style={{ fontSize: '14px', color: '#6B7280' }}>
                  Enter manhole and pipe run details for storm sewer systems
                </Typography.Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {activeTab === 'weholiteAttenuation' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Typography.Text style={{ fontSize: 13, color: '#6B7280', whiteSpace: 'nowrap' }}>Supplier Text Type:</Typography.Text>
                    <Input
                      size="small"
                      value={weholiteSupplierName}
                      onChange={(e) => updateField('weholiteAttenuation', {
                        ...state.weholiteAttenuation,
                        config: { ...state.weholiteAttenuation.config, supplierName: e.target.value },
                      })}
                      style={{ width: 140 }}
                    />
                  </div>
                )}
                {activeTab === 'cellularAttenuation' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Typography.Text style={{ fontSize: 13, color: '#6B7280', whiteSpace: 'nowrap' }}>Supplier Text Type:</Typography.Text>
                    <Input
                      size="small"
                      value={cellularSupplierName}
                      onChange={(e) => updateField('cellularAttenuation', {
                        ...state.cellularAttenuation,
                        config: { ...state.cellularAttenuation.config, supplierName: e.target.value },
                      })}
                      style={{ width: 140 }}
                    />
                  </div>
                )}
                {activeTab === 'concAttenuation' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Typography.Text style={{ fontSize: 13, color: '#6B7280', whiteSpace: 'nowrap' }}>Supplier Text Type:</Typography.Text>
                    <Input
                      size="small"
                      value={concSupplierName}
                      onChange={(e) => updateField('concAttenuation', {
                        ...state.concAttenuation,
                        config: { ...state.concAttenuation.config, supplierName: e.target.value },
                      })}
                      style={{ width: 140 }}
                    />
                  </div>
                )}
                {activeTab === 'manholes' && selectedManholeKeys.length > 0 && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => requestDelete(() => {
                      deleteManholes(selectedManholeKeys);
                      setSelectedManholeKeys([]);
                    }, selectedManholeKeys.length)}
                    style={{ marginTop: '16px', height: '32px', borderRadius: '6px', fontWeight: 500 }}
                  >
                    Delete Selected ({selectedManholeKeys.length})
                  </Button>
                )}
                {activeTab === 'manholes' && (
                  <Button
                    size="small"
                    icon={<SettingOutlined />}
                    onClick={openMhConfigModal}
                    type="default"
                    className="configure-headers-btn"
                    style={{
                      marginTop:"16px",
                      height: '32px', borderRadius: '6px', fontWeight: 500,
                      borderColor: '#1890ff', color: '#1890ff',
                      display: 'flex', alignItems: 'center', gap: '6px',
                    }}
                  >
                    Configure
                  </Button>
                )}
                {activeTab === 'stormTakeOff' && selectedCombinedStormKeys.length > 0 && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => requestDelete(() => {
                      const indices = combinedStormTakeOffData
                        .filter((row) => selectedCombinedStormKeys.includes(row.id))
                        .map((row) => row.index);
                      deleteCombinedStormRows(indices);
                      setSelectedCombinedStormKeys([]);
                    }, selectedCombinedStormKeys.length)}
                    style={{ marginTop: '16px', height: '32px', borderRadius: '6px', fontWeight: 500 }}
                  >
                    Delete Selected ({selectedCombinedStormKeys.length})
                  </Button>
                )}
                {activeTab === 'stormTakeOff' && (
                  <Button
                    size="small"
                    icon={<SettingOutlined />}
                    onClick={openRoadConstModal}
                    type="default"
                    className="configure-headers-btn"
                    style={{
                      marginTop: '16px',
                      height: '32px', borderRadius: '6px', fontWeight: 500,
                      borderColor: '#1890ff', color: '#1890ff',
                      display: 'flex', alignItems: 'center', gap: '6px',
                    }}
                  >
                    Configure
                  </Button>
                )}
                {showDevMode && (
                  <Button
                    type="text"
                    icon={<DownloadOutlined />}
                    onClick={() => setImportModalVisible(true)}
                    style={{ color: '#1890ff', fontWeight: 500 }}
                  >
                    Import Take-off
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Tab Buttons */}
          <div className="storm-sewers-sticky-tabs px-6" style={{ paddingTop: '16px', paddingBottom: '0px', display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'flex-end', backgroundColor: '#fff' }}>
            {[
              { key: 'manholes', label: 'Manhole Schedule' },
              { key: 'stormTakeOff', label: 'Storm Take off' },
              { key: 'summary', label: 'Storm Summary' },
              { key: 'pondsSwales', label: 'Ponds & Swales' },
              { key: 'cellularAttenuation', label: `${cellularSupplierName.trim() || 'Cellular'} Attenuation` },
              { key: 'weholiteAttenuation', label: `${weholiteSupplierName} Attenuation` },
              { key: 'concAttenuation', label: `${concSupplierName.trim() || 'Conc'} Attenuation` },
              { key: 'boxCulverts', label: 'Box Culverts' },
            ].map((tab) => (
              <Button
                key={tab.key}
                type={activeTab === tab.key ? 'primary' : 'default'}
                className={activeTab === tab.key ? 'active-tab-button' : 'inactive-tab-button'}
                onClick={() => handleTabChange(tab.key)}
                style={{
                  backgroundColor: activeTab === tab.key ? COLORS.primary : '#E1E6EF',
                  borderColor: activeTab === tab.key ? COLORS.primary : '#E1E6EF',
                  color: activeTab === tab.key ? '#fff' : '#6B7280',
                  fontWeight: 500,
                  borderRadius: '6px 6px 0 0',
                  height: '38px',
                  paddingLeft: '20px',
                  paddingRight: '20px',
                  borderBottom: activeTab === tab.key ? 'none' : '1px solid #E1E6EF',
                  position: 'relative',
                  zIndex: activeTab === tab.key ? 2 : 1,
                }}
              >
                {tab.label}
              </Button>
            ))}
            {activeTab === 'summary' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', marginBottom: 4 }}>
                <Segmented
                  size="small"
                  value={stormSummaryView}
                  onChange={(value) => setStormSummaryView(value as typeof stormSummaryView)}
                  options={[
                    { label: 'Summary', value: 'summary' },
                    { label: 'Surplus', value: 'surplus' },
                    { label: 'Backfill', value: 'backfill' },
                  ]}
                />
                {stormSummaryView === 'summary' && (
                  <ExpandCollapseAllButton
                    expanded={summaryExpandedKeys.length > 0}
                    onToggle={() => setSummaryExpandedKeys(summaryExpandedKeys.length > 0 ? [] : stormSummaryAllKeys)}
                  />
                )}
              </div>
            )}
            {activeTab === 'pondsSwales' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', marginBottom: 4 }}>
                <Segmented
                  size="small"
                  value={pondsSwalesView}
                  onChange={(value) => {
                    setPondsSwalesView(value as typeof pondsSwalesView);
                    setPondsSwalesActiveKeys([]);
                  }}
                  options={[
                    { label: 'Ponds', value: 'pond' },
                    { label: 'Swales', value: 'swale' },
                    { label: 'Headwalls', value: 'headwall' },
                  ]}
                />
                <ExpandCollapseAllButton
                  expanded={pondsSwalesAllExpanded}
                  onToggle={() => setPondsSwalesActiveKeys(pondsSwalesAllExpanded ? [] : pondsSwalesSectionKeys)}
                />
              </div>
            )}
            {activeTab === 'cellularAttenuation' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', marginBottom: 4 }}>
                <ExpandCollapseAllButton
                  expanded={cellularAllExpanded}
                  onToggle={() => setCellularActiveKeys(cellularAllExpanded ? [] : cellularSectionKeys)}
                />
              </div>
            )}
            {activeTab === 'weholiteAttenuation' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', marginBottom: 4 }}>
                <ExpandCollapseAllButton
                  expanded={weholiteAllExpanded}
                  onToggle={() => setWeholiteActiveKeys(weholiteAllExpanded ? [] : weholiteSectionKeys)}
                />
              </div>
            )}
            {activeTab === 'concAttenuation' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', marginBottom: 4 }}>
                <ExpandCollapseAllButton
                  expanded={concAllExpanded}
                  onToggle={() => setConcActiveKeys(concAllExpanded ? [] : concSectionKeys)}
                />
              </div>
            )}
            {activeTab === 'boxCulverts' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', marginBottom: 4 }}>
                <ExpandCollapseAllButton
                  expanded={boxCulvertAllExpanded}
                  onToggle={() => setBoxCulvertActiveKeys(boxCulvertAllExpanded ? [] : boxCulvertSectionKeys)}
                />
              </div>
            )}
          </div>
        </div>

        {/* Content section */}
        <div
          className="px-6 pb-6 storm-sewers-content-section"
          style={{ paddingTop: '0px', position: 'relative', zIndex: 1, marginTop: '0px' }}
        >
          {/* Tabbed Content */}
          <div style={{ borderRadius: '0 0 6px 6px' }}>
            {activeTab === 'manholes' && (isTableLoading ? (
              <TableLoadingPlaceholder />
            ) : (
              <div style={{ backgroundColor: '#fff' }}>
                {manholeScheduleContent}
              </div>
            ))}
            {activeTab === 'stormTakeOff' && (isTableLoading ? (
              <TableLoadingPlaceholder />
            ) : (
              <div>
                <Table
                  className="storm-sewers-table"
                  bordered
                  size="small"
                  scroll={{ x: 5430 }}
                  sticky={{ offsetHeader: stickyOffset, offsetScroll: 9999 }}
                  pagination={false}
                  rowSelection={
                    state.isViewMode
                      ? undefined
                      : {
                          selectedRowKeys: selectedCombinedStormKeys,
                          onChange: setSelectedCombinedStormKeys,
                          fixed: true,
                        }
                  }
                  columns={combinedStormTakeOffColumns}
                  dataSource={combinedStormTakeOffData}
                  rowKey="id"
                />
                <div style={{ marginTop: 16 }}>
                  <Button
                    type="text"
                    icon={<PlusOutlined style={{ color: COLORS.primary }} />}
                    onClick={addCombinedStormRow}
                    style={{ color: COLORS.primary, fontWeight: 500, padding: '4px 8px' }}
                  >
                    Add New Row
                  </Button>
                </div>
              </div>
            ))}
            {activeTab === 'summary' && summaryContent}
            {activeTab === 'pondsSwales' && (
              <div>
                {pondsSwalesView === 'pond' && (
                  <PondsTable
                    getRow={getStormBuildUpRow}
                    updateRow={updateStormBuildUpRow}
                    stickyOffset={stickyOffset}
                    activeKeys={pondsSwalesActiveKeys}
                    onActiveKeysChange={setPondsSwalesActiveKeys}
                    sectionCount={pondSectionCount}
                    onAddPond={handleAddPond}
                    onDeleteSection={handleDeletePondSection}
                    onConfigureSection={handleConfigurePondSection}
                    exTopsoilMm={exTopsoilMm}
                    pondTopsoilThicknessMm={pondTopsoilThicknessMm}
                    sectionTitles={pondsSwalesSectionTitles}
                    onEditSectionTitle={handleEditPondsSwalesTitle}
                  />
                )}
                {pondsSwalesView === 'swale' && (
                  <SwalesTable
                    getRow={getStormBuildUpRow}
                    updateRow={updateStormBuildUpRow}
                    stickyOffset={stickyOffset}
                    activeKeys={pondsSwalesActiveKeys}
                    onActiveKeysChange={setPondsSwalesActiveKeys}
                    sectionCount={swaleSectionCount}
                    onAddSwale={handleAddSwale}
                    onDeleteSection={handleDeleteSwaleSection}
                    onConfigureSection={handleConfigureSwaleSection}
                    exTopsoilMm={exTopsoilMm}
                    swaleTopsoilThicknessMm={swaleTopsoilThicknessMm}
                    sectionTitles={pondsSwalesSectionTitles}
                    onEditSectionTitle={handleEditPondsSwalesTitle}
                  />
                )}
                {pondsSwalesView === 'headwall' && (
                  <HeadwallsTable getRow={getStormBuildUpRow} updateRow={updateStormBuildUpRow} stickyOffset={stickyOffset} activeKeys={pondsSwalesActiveKeys} onActiveKeysChange={setPondsSwalesActiveKeys} />
                )}
              </div>
            )}
            {activeTab === 'cellularAttenuation' && (
              <CellularAttenuationTable
                getRow={getStormBuildUpRow}
                updateRow={updateStormBuildUpRow}
                stickyOffset={stickyOffset}
                activeKeys={cellularActiveKeys}
                onActiveKeysChange={setCellularActiveKeys}
                sectionCount={cellularSectionCount}
                supplierName={cellularSupplierName}
                onAddTank={handleAddCellularTank}
                onDeleteSection={handleDeleteCellularSection}
                onConfigureSection={handleConfigureCellularSection}
                crateDims={cellularCrateDims}
                sectionTitles={cellularSectionTitles}
                onEditSectionTitle={handleEditCellularTitle}
              />
            )}
            {activeTab === 'weholiteAttenuation' && (
              <WeholiteAttenuationTable
                getRow={getStormBuildUpRow}
                updateRow={updateStormBuildUpRow}
                stickyOffset={stickyOffset}
                activeKeys={weholiteActiveKeys}
                onActiveKeysChange={setWeholiteActiveKeys}
                sectionCount={weholiteSectionCount}
                supplierName={weholiteSupplierName}
                onAddTank={handleAddWeholiteTank}
                onDeleteSection={handleDeleteWeholiteSection}
                onConfigureSection={handleConfigureWeholiteSection}
                weholiteConfigPct={weholiteConfigPct}
                sectionTitles={weholiteSectionTitles}
                onEditSectionTitle={handleEditWeholiteTitle}
              />
            )}
            {activeTab === 'concAttenuation' && (
              <ConcAttenuationTable
                getRow={getStormBuildUpRow}
                updateRow={updateStormBuildUpRow}
                stickyOffset={stickyOffset}
                activeKeys={concActiveKeys}
                onActiveKeysChange={setConcActiveKeys}
                sectionCount={concSectionCount}
                onAddTank={handleAddConcTank}
                onDeleteSection={handleDeleteConcSection}
                onConfigureSection={handleConfigureConcSection}
                concConfigPct={concConfigPct}
                supplierName={concSupplierName}
                sectionTitles={concSectionTitles}
                drawingRefs={concDrawingRefs}
                onEditSectionTitle={handleEditConcTitle}
              />
            )}
            {activeTab === 'boxCulverts' && (
              <BoxCulvertsTable
                getRow={getStormBuildUpRow}
                updateRow={updateStormBuildUpRow}
                stickyOffset={stickyOffset}
                activeKeys={boxCulvertActiveKeys}
                onActiveKeysChange={setBoxCulvertActiveKeys}
                sectionCount={boxCulvertSectionCount}
                onAddCulvert={handleAddBoxCulvert}
                onDeleteSection={handleDeleteBoxCulvertSection}
                onConfigureSection={handleConfigureBoxCulvertSection}
                culvertConfigMm={culvertConfigMm}
                culvertBackfillType={culvertBackfillType}
              />
            )}
          </div>

        </div>

        {/* Footer with Save & Continue button */}
        <div style={{ borderTop: "1px solid #e5e7eb", marginTop: "16px" }}>
          <div className="px-6 py-4 flex justify-end gap-3">
            <Button
              type="primary"
              onClick={handleSaveAndContinue}
              style={{
                backgroundColor: COLORS.primary,
                borderColor: COLORS.primary,
              }}
            >
              Save & Continue <ArrowRightOutlined />
            </Button>
          </div>
        </div>
      </Card>

      {/* Rates Modal */}
      <Modal
        title="Manhole, Pipework, Lateral & Gullies Rates"
        open={ratesModalVisible}
        onCancel={() => setRatesModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setRatesModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={1200}
      >
        <RatesTable
          manholes={availableManholes}
          pipeRuns={stormSewersData.pipeRuns.table}
          laterals={stormSewersData.lateralConnections.table || []}
          gullies={stormSewersData.gullyConnections.table || []}
          projectName={state.projectName}
        />
      </Modal>

      {/* Excel Import Modal */}
      <ExcelImportModal
        onImport={handleUnifiedImport}
        onDownloadTemplate={handleDownloadTemplate}
        importLoading={importLoading}
        importModalVisible={importModalVisible}
        setImportModalVisible={setImportModalVisible}
      />

      {/* Manhole Schedule Configure Modal */}
      <ColumnConfigModal
        title="Configure Manhole Schedule"
        open={mhConfigModalVisible}
        onClose={() => setMhConfigModalVisible(false)}
        onApply={applyMhConfig}
        width={380}
        isViewMode={state.isViewMode}
      >
        <ConfigSection title="Set values for all manholes" showDivider={false} columns={1}>
          <ConfigField label="Ladders">
            <Select
              value={mhConfigDraft.ladders}
              onChange={(v: StormMhLadderOption) => setMhConfigDraft((prev) => ({ ...prev, ladders: v }))}
              style={{ width: '100%' }}
            >
              <Select.Option value="N/A">N/A</Select.Option>
              <Select.Option value="Stainless">Stainless</Select.Option>
              <Select.Option value="Galvanised">Galvanised</Select.Option>
            </Select>
          </ConfigField>
          <ConfigField label="Safety Chains">
            <Select
              value={mhConfigDraft.safetyChains}
              onChange={(v: StormMhLadderOption) => setMhConfigDraft((prev) => ({ ...prev, safetyChains: v }))}
              style={{ width: '100%' }}
            >
              <Select.Option value="N/A">N/A</Select.Option>
              <Select.Option value="Stainless">Stainless</Select.Option>
              <Select.Option value="Galvanised">Galvanised</Select.Option>
            </Select>
          </ConfigField>
        </ConfigSection>
      </ColumnConfigModal>

      {/* Configure Road Construction Depth Modal */}
      <ColumnConfigModal
        title="Configure Road Construction Depth"
        open={roadConstModalVisible}
        onClose={() => setRoadConstModalVisible(false)}
        onApply={applyRoadConstConfig}
        width={440}
        isViewMode={state.isViewMode}
      >
        <ConfigSection title="Inputs" showDivider={false} columns={1}>
          <ConfigField label="Road Level (m)">
            <NumberInput
              value={roadConstDraft.roadLevel}
              onChange={(v) => updateRoadConstLevel('roadLevel', v ?? 0)}
              decimalPlaces={3}
              disabled={roadConstDepthOverridden}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Formation Level (m)">
            <NumberInput
              value={roadConstDraft.formationLevel}
              onChange={(v) => updateRoadConstLevel('formationLevel', v ?? 0)}
              decimalPlaces={3}
              disabled={roadConstDepthOverridden}
              style={{ width: '100%' }}
            />
          </ConfigField>
        </ConfigSection>
        <div style={{ borderTop: '1px solid #e8e8e8', margin: '12px 0 8px' }} />
        <ConfigSection title="Depth" showDivider={false} columns={1}>
          <ConfigField label="Road Construction Depth (m)">
            <Space.Compact style={{ width: '100%' }}>
              <NumberInput
                value={roadConstDraft.depth}
                onChange={(v) => setRoadConstDraft((prev) => ({ ...prev, depth: v ?? 0 }))}
                decimalPlaces={3}
                style={{ width: '100%' }}
              />
              <Tooltip title={roadConstDepthOverridden
                ? 'Manual override — click to reset to Road Level − Formation Level (re-enables those fields)'
                : 'Auto = Road Level − Formation Level'}
              >
                <Button
                  size="small"
                  type={roadConstDepthOverridden ? 'primary' : 'default'}
                  onClick={roadConstDepthOverridden ? clearRoadConstDepthOverride : undefined}
                  disabled={!roadConstDepthOverridden}
                  style={{ borderLeft: 0, fontSize: 11, fontWeight: 500, padding: '0 8px', height: 32 }}
                >
                  {roadConstDepthOverridden ? 'Manual' : 'Auto'}
                </Button>
              </Tooltip>
            </Space.Compact>
          </ConfigField>
        </ConfigSection>
      </ColumnConfigModal>

      {/* Configure Pond Section Modal */}
      <ColumnConfigModal
        title={pondConfigSectionNo !== null ? `Configure POND ${pondConfigSectionNo}` : 'Configure'}
        open={pondConfigModalVisible}
        onClose={() => setPondConfigModalVisible(false)}
        onApply={applyPondSectionConfig}
        width={380}
        isViewMode={state.isViewMode}
      >
        <ConfigSection title="Main" showDivider={false} columns={1}>
          <ConfigField label="Top soil (site won), xxxmm thick">
            <NumberInput
              value={pondConfigDraftSiteWonMm / 1000}
              onChange={(v) => {
                const metresValue = v ? parseFloat(v.toString()) : 0;
                setPondConfigDraftSiteWonMm(metresValue > 0 ? Math.round(metresValue * 1000) : 0);
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Top soil (imported), xxxmm thick">
            <NumberInput
              value={pondConfigDraftImportedMm / 1000}
              onChange={(v) => {
                const metresValue = v ? parseFloat(v.toString()) : 0;
                setPondConfigDraftImportedMm(metresValue > 0 ? Math.round(metresValue * 1000) : 0);
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
        </ConfigSection>
      </ColumnConfigModal>

      {/* Configure Culvert Section Modal */}
      <ColumnConfigModal
        title={boxCulvertConfigSectionNo !== null ? `Configure Culvert ${boxCulvertConfigSectionNo}` : 'Configure'}
        open={boxCulvertConfigModalVisible}
        onClose={() => setBoxCulvertConfigModalVisible(false)}
        onApply={applyBoxCulvertSectionConfig}
        width={480}
        isViewMode={state.isViewMode}
      >
        <ConfigSection title="" showDivider={false} columns={2}>
          <ConfigField label="Box Culvert Size (mm)">
            <NumberInput
              value={boxCulvertConfigDraft.specSize}
              onChange={(v) => setBoxCulvertConfigDraft((prev) => ({ ...prev, specSize: v ?? 0 }))}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Box Culvert Depth (m)">
            <NumberInput
              value={boxCulvertConfigDraft.specDepth / 1000}
              onChange={(v) => {
                const metresValue = v ? parseFloat(v.toString()) : 0;
                setBoxCulvertConfigDraft((prev) => ({ ...prev, specDepth: metresValue > 0 ? Math.round(metresValue * 1000) : 0 }));
              }}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Height (m)">
            <NumberInput
              value={boxCulvertConfigDraft.height / 1000}
              onChange={(v) => {
                const metresValue = v ? parseFloat(v.toString()) : 0;
                setBoxCulvertConfigDraft((prev) => ({ ...prev, height: metresValue > 0 ? Math.round(metresValue * 1000) : 0 }));
              }}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Concrete Blinding (mm)">
            <NumberInput
              value={boxCulvertConfigDraft.concBlinding}
              onChange={(v) => setBoxCulvertConfigDraft((prev) => ({ ...prev, concBlinding: v ?? 0 }))}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Pipe Bedding Top & Bottom (mm)">
            <NumberInput
              value={boxCulvertConfigDraft.bedTop}
              onChange={(v) => setBoxCulvertConfigDraft((prev) => ({ ...prev, bedTop: v ?? 0 }))}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Pipe Bedding Sides (mm)">
            <NumberInput
              value={boxCulvertConfigDraft.bedSide}
              onChange={(v) => setBoxCulvertConfigDraft((prev) => ({ ...prev, bedSide: v ?? 0 }))}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Backfill Type">
            <Select
              value={boxCulvertBackfillDraft}
              onChange={(v: string) => setBoxCulvertBackfillDraft(v)}
              style={{ width: '100%' }}
            >
              <Select.Option value="">Select</Select.Option>
              {BOX_CULVERT_BACKFILL_TYPE_OPTIONS.map((opt) => (
                <Select.Option key={opt} value={opt}>{opt}</Select.Option>
              ))}
            </Select>
          </ConfigField>
          <ConfigField label="FP (%)">
            <NumberInput
              value={boxCulvertConfigDraft.purchase}
              onChange={(v) => setBoxCulvertConfigDraft((prev) => ({ ...prev, purchase: v ?? 0 }))}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          {/* Stored but not yet wired to any row description — added ahead of
              a future OH&P line item, same as FP (%) above. */}
          <ConfigField label="OH&P (%)">
            <NumberInput
              value={boxCulvertConfigDraft.ohp}
              onChange={(v) => setBoxCulvertConfigDraft((prev) => ({ ...prev, ohp: v ?? 0 }))}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
        </ConfigSection>
      </ColumnConfigModal>

      {/* Configure Tank Section Modal (Cellular Attenuation) */}
      <ColumnConfigModal
        title={cellularConfigSectionNo !== null ? `Configure Cellular Tank ${cellularConfigSectionNo}` : 'Configure'}
        open={cellularConfigModalVisible}
        onClose={() => setCellularConfigModalVisible(false)}
        onApply={applyCellularSectionConfig}
        width={560}
        isViewMode={state.isViewMode}
      >
        <ConfigSection title="Supply & Fit Crates" showDivider={true} columns={3}>
          {CELLULAR_CRATE_FIELDS.map((f) => (
            <ConfigField key={f.suffix} label={f.label.replace('Sub Contract ', '').replace('Crate ', '')}>
              <NumberInput
                value={cellularConfigDraft[f.suffix] ?? f.default}
                onChange={(v) => setCellularConfigDraft((prev) => ({ ...prev, [f.suffix]: v ?? 0 }))}
                positiveOnly
                decimalPlaces={3}
                style={{ width: '100%' }}
              />
            </ConfigField>
          ))}
        </ConfigSection>
        <ConfigSection title="Supply & Fit Crates - Sub Contract" showDivider={false} columns={3}>
          {CELLULAR_SUB_CRATE_FIELDS.map((f) => (
            <ConfigField key={f.suffix} label={f.label.replace('Sub Contract ', '').replace('Crate ', '')}>
              <NumberInput
                value={cellularConfigDraft[f.suffix] ?? f.default}
                onChange={(v) => setCellularConfigDraft((prev) => ({ ...prev, [f.suffix]: v ?? 0 }))}
                positiveOnly
                decimalPlaces={3}
                style={{ width: '100%' }}
              />
            </ConfigField>
          ))}
        </ConfigSection>
      </ColumnConfigModal>

      {/* Configure Tank Section Modal (Weholite Attenuation) */}
      <ColumnConfigModal
        title={weholiteConfigSectionNo !== null ? `Configure ${weholiteSupplierName} Tank ${weholiteConfigSectionNo}` : 'Configure'}
        open={weholiteConfigModalVisible}
        onClose={() => setWeholiteConfigModalVisible(false)}
        onApply={applyWeholiteSectionConfig}
        width={420}
        isViewMode={state.isViewMode}
      >
        <ConfigSection title="" showDivider={false} columns={2}>
          <ConfigField label="FP (%)">
            <NumberInput
              value={weholiteConfigDraft.fixedPrice}
              onChange={(v) => setWeholiteConfigDraft((prev) => ({ ...prev, fixedPrice: v ?? 0 }))}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="OHP (%)">
            <NumberInput
              value={weholiteConfigDraft.ohp}
              onChange={(v) => setWeholiteConfigDraft((prev) => ({ ...prev, ohp: v ?? 0 }))}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
        </ConfigSection>
      </ColumnConfigModal>

      {/* Configure Tank Section Modal (Conc Attenuation) */}
      <ColumnConfigModal
        title={concConfigSectionNo !== null ? `Configure ${concSupplierName} Tank ${concConfigSectionNo}` : 'Configure'}
        open={concConfigModalVisible}
        onClose={() => setConcConfigModalVisible(false)}
        onApply={applyConcSectionConfig}
        width={560}
        isViewMode={state.isViewMode}
      >
        <ConfigSection title="" showDivider={false} columns={2}>
          <ConfigField label="Tank Width (m)">
            <NumberInput
              value={concConfigDraft.tankWidth}
              onChange={(v) => setConcConfigDraft((prev) => ({ ...prev, tankWidth: v ?? 0 }))}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Tank Length (m)">
            <NumberInput
              value={concConfigDraft.tankLength}
              onChange={(v) => setConcConfigDraft((prev) => ({ ...prev, tankLength: v ?? 0 }))}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Tank Depth (m)">
            <NumberInput
              value={concConfigDraft.tankDepth}
              onChange={(v) => setConcConfigDraft((prev) => ({ ...prev, tankDepth: v ?? 0 }))}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Drawing Reference">
            <Input
              value={concDrawingRefDraft}
              onChange={(e) => setConcDrawingRefDraft(e.target.value)}
              placeholder={CONC_DEFAULT_DRAWING_REF}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="MOT Type 1 (mm)">
            <NumberInput
              value={concConfigDraft.mot150}
              onChange={(v) => setConcConfigDraft((prev) => ({ ...prev, mot150: v ?? 0 }))}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Supply of Carlow materials">
            <Select
              value={concConfigDraft.supplyCar}
              onChange={(v: number) => setConcConfigDraft((prev) => ({ ...prev, supplyCar: v }))}
              style={{ width: '100%' }}
            >
              <Select.Option value={0}>Select</Select.Option>
              {CONC_SUPPLY_OPTIONS.map((o, i) => (
                <Select.Option key={o.c} value={i + 1}>{o.n}</Select.Option>
              ))}
            </Select>
          </ConfigField>
          <ConfigField label="FP (%)">
            <NumberInput
              value={concConfigDraft.fp5}
              onChange={(v) => setConcConfigDraft((prev) => ({ ...prev, fp5: v ?? 0 }))}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="OHP (%)">
            <NumberInput
              value={concConfigDraft.ohp15}
              onChange={(v) => setConcConfigDraft((prev) => ({ ...prev, ohp15: v ?? 0 }))}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Haul Road Width (m)">
            <NumberInput
              value={concConfigDraft.haulRoadWidth}
              onChange={(v) => setConcConfigDraft((prev) => ({ ...prev, haulRoadWidth: v ?? 0 }))}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Haul Road Thickness (mm)">
            <NumberInput
              value={concConfigDraft.haulRoadThick}
              onChange={(v) => setConcConfigDraft((prev) => ({ ...prev, haulRoadThick: v ?? 0 }))}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Crane Visits (days)">
            <NumberInput
              value={concConfigDraft.craneVisit}
              onChange={(v) => setConcConfigDraft((prev) => ({ ...prev, craneVisit: v ?? 0 }))}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Crane Programme (days)">
            <NumberInput
              value={concConfigDraft.craneProgramme}
              onChange={(v) => setConcConfigDraft((prev) => ({ ...prev, craneProgramme: v ?? 0 }))}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Concrete Pump Visits (days)">
            <NumberInput
              value={concConfigDraft.concPump}
              onChange={(v) => setConcConfigDraft((prev) => ({ ...prev, concPump: v ?? 0 }))}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Concrete Requirement (m³)">
            <NumberInput
              value={concConfigDraft.conc58}
              onChange={(v) => setConcConfigDraft((prev) => ({ ...prev, conc58: v ?? 0 }))}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="RTU Mortar Requirement (m³)">
            <NumberInput
              value={concConfigDraft.mortar1}
              onChange={(v) => setConcConfigDraft((prev) => ({ ...prev, mortar1: v ?? 0 }))}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Steel Reinforcement (t)">
            <NumberInput
              value={concConfigDraft.steel}
              onChange={(v) => setConcConfigDraft((prev) => ({ ...prev, steel: v ?? 0 }))}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Skips (nr)">
            <NumberInput
              value={concConfigDraft.skip}
              onChange={(v) => setConcConfigDraft((prev) => ({ ...prev, skip: v ?? 0 }))}
              positiveOnly
              decimalPlaces={3}
              style={{ width: '100%' }}
            />
          </ConfigField>
        </ConfigSection>
      </ColumnConfigModal>

      {/* Configure Swale Section Modal */}
      <ColumnConfigModal
        title={swaleConfigSectionNo !== null ? `Configure SWALE ${swaleConfigSectionNo}` : 'Configure'}
        open={swaleConfigModalVisible}
        onClose={() => setSwaleConfigModalVisible(false)}
        onApply={applySwaleSectionConfig}
        width={380}
        isViewMode={state.isViewMode}
      >
        <ConfigSection title="Main" showDivider={false} columns={1}>
          <ConfigField label="Top soil (site won), xxxmm thick">
            <NumberInput
              value={swaleConfigDraftSiteWonMm / 1000}
              onChange={(v) => {
                const metresValue = v ? parseFloat(v.toString()) : 0;
                setSwaleConfigDraftSiteWonMm(metresValue > 0 ? Math.round(metresValue * 1000) : 0);
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Top soil (imported), xxxmm thick">
            <NumberInput
              value={swaleConfigDraftImportedMm / 1000}
              onChange={(v) => {
                const metresValue = v ? parseFloat(v.toString()) : 0;
                setSwaleConfigDraftImportedMm(metresValue > 0 ? Math.round(metresValue * 1000) : 0);
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
        </ConfigSection>
      </ColumnConfigModal>

      <style>{`
        .storm-sewers-card.ant-card {
          border-radius: 8px !important;
          overflow: visible !important;
        }

        .storm-sewers-card.ant-card .ant-card-body {
          border-radius: 8px !important;
          overflow: visible !important;
          position: relative !important;
        }

        .storm-sewers-card {
          position: relative;
          height: auto;
          overflow: visible !important;
        }

        .storm-sewers-card .ant-card-body {
          overflow: visible !important;
        }

        .storm-sewers-sticky-header {
          box-shadow: none;
          will-change: transform;
          transform: translateZ(0);
          z-index: 98 !important;
        }

        .storm-sewers-sticky-tabs {
          border-bottom: none;
          min-height: 40px;
        }

        .storm-sewers-content-section {
          clip-path: inset(0px -100vw -100vh -100vw);
        }

        .active-tab-button {
          border-bottom: none !important;
        }

        .inactive-tab-button {
          border-bottom: none !important;
        }

        /* Table layout */
        .storm-sewers-table {
          font-size: 14px;
          margin-top: 0 !important;
        }

        .storm-sewers-table .ant-table {
          border: none !important;
          margin-top: 0 !important;
        }

        .storm-sewers-table .ant-table-container {
          border: 1px solid #e5e7eb !important;
          border-top: none !important;
          border-radius: 0 !important;
        }

        .storm-sewers-table .ant-table-wrapper {
          margin-top: -1px !important;
        }

        .storm-sewers-table .ant-table-body {
          margin-top: 0 !important;
        }

        /* Table headers */
        .storm-sewers-table .ant-table-thead > tr > th {
          background-color: ${COLORS.tableHeaderBg} !important;
          font-weight: 600 !important;
          color: #000 !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-left: none !important;
          border-top: none !important;
          padding: 24px 8px 10px 8px !important;
          text-align: center !important;
          font-size: 13px !important;
        }

        /* Only promote sticky header cells into a stacking context.
           Applying will-change/transform to ALL <th> causes non-sticky scrolling
           headers to overlap fixed columns when scrolling horizontally. */
        .storm-sewers-table .ant-table-thead > tr > th.ant-table-cell-fix-left,
        .storm-sewers-table .ant-table-thead > tr > th.ant-table-cell-fix-right,
        .storm-sewers-table .ant-table-thead > tr > th.ant-table-cell-fix-sticky {
          will-change: transform !important;
          transform: translateZ(0) !important;
          z-index: 97 !important;
        }

        .storm-sewers-table .ant-table-thead > tr > th:first-child {
          border-left: none !important;
        }

        /* Body cells */
        .storm-sewers-table .ant-table-tbody > tr > td {
          padding: 6px 8px !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-left: none !important;
          border-top: none !important;
          background-color: #fff;
        }

        .storm-sewers-table .ant-table-tbody > tr > td:first-child {
          border-left: none !important;
        }

        .storm-sewers-table .ant-table-tbody > tr > td.bg-gray-50 {
          background-color: #f9fafb !important;
        }

        .storm-sewers-table .ant-table-tbody > tr:hover > td {
          background-color: #fafafa !important;
        }

        .storm-sewers-table .ant-input[type="text"] {
          text-align: right !important;
        }

        /* Storm summary table */
        .storm-summary-table .ant-table-thead > tr > th {
          background-color: ${COLORS.tableHeaderBg} !important;
        }
        .storm-summary-table .ant-table-tbody > tr.bg-indigo-50 > td,
        .storm-summary-table .ant-table-tbody > tr.bg-indigo-50:hover > td {
          background-color: #eef2ff !important;
        }
        .storm-summary-table .ant-table-tbody > tr.bg-gray-50 > td,
        .storm-summary-table .ant-table-tbody > tr.bg-gray-50:hover > td {
          background-color: #f9fafb !important;
        }

        /* Build Up table styles */
        .build-up-table .ant-table-thead > tr > th {
          background-color: ${COLORS.tableHeaderBg} !important;
          /* Reset stacking-context promoters from the shared foul-sewers-table rule
             so these non-sticky headers don't paint over the sticky accordion header */
          z-index: 2 !important;
          will-change: auto !important;
          transform: none !important;
        }
        .build-up-collapse .ant-collapse-content-box {
          padding: 0 !important;
        }
        .build-up-table .build-up-header-row > td {
          background-color: #eff6ff !important;
          font-weight: 600 !important;
        }
        /* Sticky accordion panel headers — must sit above table cell stacking contexts */
        .build-up-collapse .ant-collapse-header {
          position: sticky !important;
          top: ${stickyOffset}px !important;
          z-index: 99 !important;
          background-color: #fafafa !important;
          border-bottom: 1px solid #d9d9d9;
        }
      `}</style>

      {/* Column calculation info modal — outside tabs so it renders on any active tab */}
      {colCalcInfo && (
        <Modal
          title={`${colCalcInfo.title} — Calculation`}
          open={true}
          onCancel={() => setColCalcInfo(null)}
          footer={[<Button key="close" onClick={() => setColCalcInfo(null)}>Close</Button>]}
          width={500}
        >
          <div style={{ fontSize: 13, color: '#374151' }}>
            <div style={{ padding: '8px 12px', background: '#f0f4ff', borderRadius: 4, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
              {colCalcInfo.formula}
            </div>
          </div>
        </Modal>
      )}

      <DeleteConfirmationModal
        visible={deleteModalVisible}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        title={pendingDeleteCount > 1 ? `Are you sure you want to delete ${pendingDeleteCount} records?` : undefined}
      />
    </MainLayout>
  );
};

export default StormSewersTakeoff;
